import React, { useState, useEffect, useCallback } from 'react';
import { supabase, logActivity } from '../lib/supabase';
import { BASE_RATES, RATE_LABELS, RATE_UNITS, getRating, isStale, getOrAnalyzeMarket, tierLabel, tierColor, staticTierToMILabel } from '../lib/marketRates';
import { useConfig } from '../contexts/ConfigContext';
import { useAuth } from '../contexts/AuthContext';
import { calcQuote } from '../lib/pricing';
import { loadPackageIncludes } from '../lib/packageIncludes';

const TABS = ['IT & Security Products', 'Managed IT Packages', 'Market Tiers', 'Pricing Settings', 'Notifications', 'Addendums', 'OOS Rates', 'Voice Hardware', 'Voice Fax Packages', 'Users', 'Documents', 'Schedule of Services', 'Integrations'];

const QTY_DRIVERS = ['user','mailbox','workstation','location','server','flat','mixed','mobile_device','domain','manual'];
// Product categories are stored in pricing_settings key 'product_categories'
// Fallback list used if not yet configured
const DEFAULT_CATEGORIES = [
  'Cloud & Email Security','Endpoint Security','Backup & Recovery',
  'Security Awareness','SIEM & SOC','Network & Connectivity','Strategic Advisory'
];

export default function AdminPage() {
  const [tab, setTab] = useState('IT & Security Products');
  const [reloading, setReloading] = useState(false);
  const { reload } = useConfig();
  // v3.5.79: pending-review badge for the Schedule of Services tab.
  // Counts active rows still in review_status='needs_review', so the
  // operator can tell at a glance whether internal review work is in
  // flight before the next quarterly attorney send.
  const [sosPendingCount, setSosPendingCount] = useState(0);

  async function handleReload() {
    setReloading(true);
    await reload();
    setTimeout(() => setReloading(false), 800);
  }

  useEffect(() => {
    // Load on mount AND whenever the tab changes back to Schedule of Services
    // — saves a stale count after editing.
    supabase.from('schedule_of_services')
      .select('id', { count: 'exact', head: true })
      .eq('status', 'active')
      .eq('review_status', 'needs_review')
      .then(({ count }) => setSosPendingCount(count || 0));
  }, [tab]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '0 16px', display: 'flex', gap: 2, flexShrink: 0 }}>
        {TABS.map(t => {
          // v3.5.79: small badge count next to the Schedule of Services tab
          // showing rows pending internal review.
          const badge = (t === 'Schedule of Services' && sosPendingCount > 0) ? sosPendingCount : null;
          return (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 14px', background: 'none', border: 'none', fontSize: 12, fontWeight: tab === t ? 700 : 500,
              color: tab === t ? '#0f1e3c' : '#6b7280', borderBottom: `2px solid ${tab === t ? '#2563eb' : 'transparent'}`,
              cursor: 'pointer', transition: 'all 0.12s', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {t}
              {badge !== null && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 18, height: 18, padding: '0 6px',
                  background: '#fef3c7', color: '#78350f',
                  fontSize: 10, fontWeight: 700, borderRadius: 9,
                }}>{badge}</span>
              )}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button onClick={handleReload} disabled={reloading} style={{ margin: '6px 0', padding: '4px 10px', background: reloading ? '#dcfce7' : '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11, color: reloading ? '#166534' : '#6b7280', cursor: 'pointer' }}>
          {reloading ? '✓ Reloaded' : '↺ Reload config'}
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {tab === 'IT & Security Products' && <ProductsAdmin />}
        {tab === 'Managed IT Packages'    && <PackagesAdmin />}
        {tab === 'Market Tiers'     && <MarketTiersAdmin />}
        {tab === 'Voice Hardware'    && <VoiceHardwareAdmin />}
        {tab === 'Voice Fax Packages'&& <VoiceFaxPackagesAdmin />}
        {tab === 'Pricing Settings' && <SettingsAdmin />}
        {tab === 'Notifications'    && <NotificationsAdmin />}
        {tab === 'Addendums'        && <AddendumsAdmin />}
        {tab === 'OOS Rates'        && <OosRatesAdmin />}
        {tab === 'Users'            && <UsersAdmin />}
        {tab === 'Documents'        && <DocumentsAdmin />}
        {tab === 'Schedule of Services' && <ScheduleOfServicesAdmin />}
        {tab === 'Integrations'     && <IntegrationsAdmin />}
      </div>
    </div>
  );
}

// ─── Shared Components ────────────────────────────────────────────────────────
function AdminTable({ cols, rows, onEdit, onToggle, loading, extraActions }) {
  if (loading) return <div style={{ padding: 20, color: '#6b7280', fontSize: 12 }}>Loading...</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr style={{ background: '#f9fafb' }}>
          {cols.map(c => <th key={c} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{c}</th>)}
          <th style={{ padding: '8px 10px', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={row.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
            {cols.map(c => <td key={c} style={{ padding: '8px 10px', color: '#374151', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[c]}</td>)}
            <td style={{ padding: '8px 10px', display: 'flex', gap: 4 }}>
              <button onClick={() => onEdit(row)} style={btnStyle('#eff6ff','#1e40af')}>Edit</button>
              {onToggle && (
                <button onClick={() => onToggle(row)} style={btnStyle(row.active ? '#fef2f2' : '#f0fdf4', row.active ? '#dc2626' : '#166534')}>
                  {row.active ? 'Deactivate' : 'Activate'}
                </button>
              )}
              {/* extraActions: optional render prop for tab-specific row actions
                  (e.g., "Calculate Rates" on packages — added v3.5.35) */}
              {extraActions && extraActions(row)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Modal({ title, onClose, onSave, saving, children, width = 520 }) {
  // v3.5.94.4: width is now an opt-in prop (default 520). Four of the five
  // Modal call sites in AdminPage are form-only and fit fine at 520
  // (Edit Product, Edit Market Tier, Edit Settings, Edit User). The Edit
  // Package modal contains 8-column tables (MSP Tool Stack) and 6-column
  // tables (Revenue Tool Stack) that don't fit at 520px — pre-deploy the
  // SORT input and delete button on MSP Tool Stack rows were clipped
  // entirely off-screen. Edit Package now passes width={900}. Future
  // wide-modal use cases can pass their own width; defensive overflow-x:
  // auto on the inner tables (see MSP Tool Stack render below) handles
  // any future column additions that might exceed even the wider modal.
  // Width is capped at 95vw so very small viewports degrade gracefully.
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width, maxWidth: '95vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f1e3c' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#6b7280' }}>×</button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={btnStyle('#f3f4f6','#374151')}>Cancel</button>
          <button onClick={onSave} disabled={saving} style={{ padding: '6px 16px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = 'text', ...props }) {
  return <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} {...props}
    style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, outline: 'none' }} />;
}

function Select({ value, onChange, opts }) {
  return (
    <select value={value ?? ''} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, background: 'white', outline: 'none' }}>
      <option value="">— select —</option>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

const btnStyle = (bg, color) => ({
  padding: '3px 8px', background: bg, color, border: `1px solid ${color}30`,
  borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: 'pointer'
});

// ─── PRODUCTS ADMIN ───────────────────────────────────────────────────────────
function ProductsAdmin() {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [newCat,     setNewCat]     = useState('');
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  // v3.5.99.7.7: SoS linkage state. sosByProductId maps product_id → SoS row
  // so the products table can show "✓ SoS" or "⚠ No SoS" status inline. The
  // `generatingSosForId` flag disables the Generate button while a request
  // is in flight (prevents double-creates).
  const [sosByProductId, setSosByProductId] = useState({});
  // v3.5.99.7.9: candidate matches for UNLINKED SoS rows. The v7.7 backfill
  // matched display_name = product.name exactly, which missed cases like
  // "Managed Firewall (Per Location)" vs product "Managed Firewall". We now
  // fuzzy-match at load time and surface candidates so the rep can Link
  // instead of blindly Generating a duplicate.
  const [sosCandidatesByProductId, setSosCandidatesByProductId] = useState({});
  const [generatingSosForId, setGeneratingSosForId] = useState(null);
  const { profile } = useAuth();
  const { reload } = useConfig();

  // v3.5.99.7.9: Find the best unlinked SoS row that looks like a match for
  // a product. Returns { sos, score } where score ∈ [0,1] is confidence:
  //   1.00 exact case-insensitive display_name == product.name
  //   0.95 same after stripping trailing parenthetical (e.g. "(Per Location)")
  //   0.80 SoS display_name starts with product.name + ' '
  //   0.65 v3.5.99.7.10: Jaccard token-set similarity ≥ 0.66 (strong overlap)
  //   0.55 v3.5.99.7.10: Jaccard token-set similarity ≥ 0.40 (moderate overlap)
  //   0.55 product.name is a substring of SoS display_name (≥5 chars)
  // Threshold 0.55 — below that we say "No SoS" and offer Generate.
  //
  // v3.5.99.7.10: Jaccard tier added to catch word-order swaps that the
  // exact/prefix/substring tiers can't see. Example: product "Backup & DR —
  // Workstations" vs SoS "Workstation Backup & Disaster Recovery". Same
  // concept, different word order; substring of either fails, but token-
  // set Jaccard (after crude pluralization stemming) scores 0.40, which
  // we map to the 0.55 confidence band so the UI surfaces the candidate
  // as a Link option.
  function findSosCandidate(product, allSosRows) {
    if (!product?.name) return null;
    const productName = product.name.toLowerCase().trim();
    if (!productName) return null;

    // Tokenize for Jaccard: lowercase, split on non-word chars, drop empties,
    // crude-stem trailing 's' on words longer than 3 chars (covers most
    // English plurals: workstations → workstation, servers → server).
    const tokenize = (s) => {
      const stem = (w) => (w.length > 3 && w.endsWith('s')) ? w.slice(0, -1) : w;
      return new Set(
        String(s || '')
          .toLowerCase()
          .split(/\W+/)
          .filter(Boolean)
          .map(stem)
      );
    };
    const productTokens = tokenize(productName);

    let best = null;
    for (const sos of allSosRows) {
      if (sos.product_id) continue;
      if (!sos.display_name) continue;
      const sosName = sos.display_name.toLowerCase().trim();
      let score = 0;
      if (sosName === productName) {
        score = 1.0;
      } else {
        const stripped = sosName.replace(/\s*\([^)]*\)\s*$/, '').trim();
        if (stripped === productName) {
          score = 0.95;
        } else if (sosName.startsWith(productName + ' ')) {
          score = 0.80;
        } else if (productName.length >= 5 && sosName.includes(productName)) {
          score = 0.55;
        } else {
          // v3.5.99.7.10: Jaccard token-set similarity as last-chance tier.
          // Computed against both raw sosName AND parens-stripped version, take
          // the higher Jaccard (handles "Foo (Bar)" where parens contain noise
          // that would dilute the score).
          const sosTokens         = tokenize(sosName);
          const sosStrippedTokens = tokenize(stripped);
          const jaccard = (a, b) => {
            if (a.size === 0 || b.size === 0) return 0;
            let intersect = 0;
            for (const t of a) if (b.has(t)) intersect++;
            return intersect / (a.size + b.size - intersect);
          };
          const j = Math.max(jaccard(productTokens, sosTokens), jaccard(productTokens, sosStrippedTokens));
          if (j >= 0.66)      score = 0.65;
          else if (j >= 0.40) score = 0.55;
        }
      }
      if (score > 0 && (!best || score > best.score)) {
        best = { sos, score };
        if (score >= 1.0) break;
      }
    }
    return best;
  }

  async function load() {
    setLoading(true);
    // v3.5.99.7.9: fetch ALL SoS rows (was: linked-only). We need the
    // unlinked rows so findSosCandidate can suggest manual links for
    // products where the v7.7 exact-match backfill missed.
    const [prodRes, catRes, sosRes] = await Promise.all([
      supabase.from('products').select('*').order('category').order('sort_order'),
      supabase.from('pricing_settings').select('value').eq('key','product_categories').maybeSingle(),
      supabase.from('schedule_of_services')
        .select('id, product_id, part_number, display_name, status, review_status'),
    ]);
    if (prodRes.error) console.error('Load products error:', prodRes.error);
    if (sosRes.error)  console.error('Load SoS data error:', sosRes.error);
    const productsList = prodRes.data || [];
    setProducts(productsList);

    // Linked map (product_id → SoS row)
    const linkedMap = {};
    for (const s of (sosRes.data || [])) {
      if (s.product_id) linkedMap[s.product_id] = s;
    }
    setSosByProductId(linkedMap);

    // v3.5.99.7.9: Candidate map (product_id → { sos, score }). Skip
    // products that already have a linked SoS.
    const candidatesMap = {};
    const allSos = sosRes.data || [];
    for (const p of productsList) {
      if (linkedMap[p.id]) continue;
      const candidate = findSosCandidate(p, allSos);
      if (candidate) candidatesMap[p.id] = candidate;
    }
    setSosCandidatesByProductId(candidatesMap);

    if (catRes.data?.value) {
      try { setCategories(JSON.parse(catRes.data.value)); } catch {}
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function saveCategories(updated) {
    setCategories(updated);
    await supabase.from('pricing_settings').upsert({
      key: 'product_categories',
      value: JSON.stringify(updated),
      description: 'Product category list for Admin → Products',
    }, { onConflict: 'key' });
  }

  async function addCategory() {
    const name = newCat.trim();
    if (!name || categories.includes(name)) return;
    await saveCategories([...categories, name].sort());
    setNewCat('');
  }

  async function removeCategory(cat) {
    if (!window.confirm(`Remove category "${cat}"? Products using it won't be affected.`)) return;
    await saveCategories(categories.filter(c => c !== cat));
  }

  function startNew() {
    setSaveError('');
    setEditing({ name:'', category:'', sub_category:'', description:'', sell_price:'', cost_price:'', base_fee:0, base_cost:0, qty_driver:'user', exclusive_group:'', sort_order:0, active:true, notes:'', no_discount:false, no_commission:false, internal_only:false, compliance_tags:[], recommendation_reason:'' });
  }

  async function save() {
    setSaving(true); setSaveError('');
    const isNew = !editing.id;
    const payload = {
      name:            editing.name,
      category:        editing.category,
      sub_category:    editing.sub_category || null,
      description:     editing.description || null,
      sell_price:      parseFloat(editing.sell_price),
      cost_price:      parseFloat(editing.cost_price),
      // v3.5.60: base_fee / base_cost — flat MRR & COGS added on top of qty × rate
      // (e.g., Compliance Program products with a fixed monthly program-management
      // component). Defaults to 0 for legacy products. Coerced via parseFloat so
      // empty inputs become 0 rather than NaN.
      base_fee:        parseFloat(editing.base_fee)  || 0,
      base_cost:       parseFloat(editing.base_cost) || 0,
      qty_driver:      editing.qty_driver,
      exclusive_group: editing.exclusive_group || null,
      sort_order:      parseInt(editing.sort_order) || 0,
      active:          editing.active !== false,
      notes:           editing.notes || null,
      cost_qty_driver: editing.cost_qty_driver || null,
      no_discount:          editing.no_discount  || false,
      no_commission:        editing.no_commission || false,
      compliance_tags:      editing.compliance_tags || [],
      recommendation_reason: editing.recommendation_reason || null,
      internal_only:        editing.internal_only || false,
      updated_by:           profile?.id
    };

    // Validate
    if (!payload.name) { setSaveError('Name is required.'); setSaving(false); return; }
    if (!payload.category) { setSaveError('Category is required.'); setSaving(false); return; }
    if (isNaN(payload.sell_price)) { setSaveError('Sell price must be a number.'); setSaving(false); return; }
    if (isNaN(payload.cost_price)) { setSaveError('Cost price must be a number.'); setSaving(false); return; }

    const old = products.find(p => p.id === editing.id);
    let error;
    if (isNew) {
      const res = await supabase.from('products').insert(payload);
      error = res.error;
    } else {
      const res = await supabase.from('products').update(payload).eq('id', editing.id);
      error = res.error;
    }

    if (error) {
      console.error('Save product error:', error);
      setSaveError(error.message || 'Save failed — check console for details.');
      setSaving(false);
      return;
    }

    await logActivity({
      action: isNew ? 'CREATE' : 'UPDATE',
      entityType: 'product',
      entityId: editing.id,
      entityName: editing.name,
      changes: isNew ? payload : diffObjects(old, payload)
    });
    setEditing(null);
    await load();
    reload(); // also refresh the global config context
    setSaving(false);
  }

  async function toggle(row) {
    const { error } = await supabase.from('products').update({ active: !row.active, updated_by: profile?.id }).eq('id', row.id);
    if (!error) {
      await logActivity({ action: row.active ? 'DEACTIVATE' : 'ACTIVATE', entityType: 'product', entityId: row.id, entityName: row.name });
      load();
    }
  }

  // v3.5.60: margin and price columns now factor in base_fee/base_cost.
  // For products with no base components (the common case), behavior is unchanged.
  // For products with a Base Fee (Compliance Programs), we use a qty=10
  // reference so the table shows representative effective margin instead of
  // unit-only margin (which understates GM for high-base/low-rate products).
  const REF_QTY = 10;
  const margin = p => {
    const baseFee  = Number(p.base_fee)  || 0;
    const baseCost = Number(p.base_cost) || 0;
    if (baseFee > 0 || baseCost > 0) {
      const rev  = (Number(p.sell_price) || 0) * REF_QTY + baseFee;
      const cost = (Number(p.cost_price) || 0) * REF_QTY + baseCost;
      return rev > 0 ? ((1 - cost / rev) * 100).toFixed(0) + '%*' : '—';
    }
    return p.sell_price > 0 ? ((1 - p.cost_price / p.sell_price) * 100).toFixed(0) + '%' : '—';
  };
  const fmtSell = p => {
    const baseFee = Number(p.base_fee) || 0;
    return baseFee > 0 ? `$${p.sell_price} + $${baseFee}` : `$${p.sell_price}`;
  };
  const fmtCost = p => {
    const baseCost = Number(p.base_cost) || 0;
    return baseCost > 0 ? `$${p.cost_price} + $${baseCost}` : `$${p.cost_price}`;
  };
  const filteredProducts = products.filter(p => {
    if (catFilter && p.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (p.name || '').toLowerCase().includes(q)
          || (p.sub_category || '').toLowerCase().includes(q)
          || (p.category || '').toLowerCase().includes(q)
          || (p.description || '').toLowerCase().includes(q);
    }
    return true;
  });
  const rows = filteredProducts.map(p => {
    // v3.5.99.7.7 + v3.5.99.7.9: SoS linkage badge — 3 states.
    //   Linked       → green ✓ SoS         (per-row sos exists with product_id)
    //   Match found  → blue ~ Match?       (unlinked sos with similar display_name;
    //                                       Link button in actions to confirm)
    //   No match     → amber ⚠ No SoS     (no candidate; Generate creates new)
    // The match-candidate state replaces v7.7's blanket "No SoS" badge for
    // unlinked products, which previously misled reps into Generating
    // duplicates of existing SoS entries.
    const sos = sosByProductId[p.id];
    const candidate = !sos ? sosCandidatesByProductId[p.id] : null;
    const sosCell = sos
      ? <span title={`Linked → ${sos.part_number || '(no part #)'} · ${sos.status || ''} · ${sos.review_status || ''}`}
              style={{ display:'inline-block', padding:'2px 7px', background:'#dcfce7', color:'#166534',
                       border:'1px solid #86efac', borderRadius:3, fontSize:10, fontWeight:700 }}>
          ✓ SoS
        </span>
      : candidate
      ? <span title={`Possible match (${Math.round(candidate.score * 100)}% confidence) → #${candidate.sos.part_number} · "${candidate.sos.display_name}". Use the Link button to confirm.`}
              style={{ display:'inline-block', padding:'2px 7px', background:'#dbeafe', color:'#1e40af',
                       border:'1px solid #93c5fd', borderRadius:3, fontSize:10, fontWeight:700 }}>
          ~ Match?
        </span>
      : <span style={{ display:'inline-block', padding:'2px 7px', background:'#fef3c7', color:'#92400e',
                       border:'1px solid #fde68a', borderRadius:3, fontSize:10, fontWeight:700 }}>
          ⚠ No SoS
        </span>;
    return { ...p,
      '$sell': fmtSell(p), '$cost': fmtCost(p), 'gm': margin(p),
      'flags': [p.no_discount ? '🔒 No Discount' : '', p.no_commission ? '💼 No Comm' : ''].filter(Boolean).join(' · ') || '—',
      '$sos': sosCell,
    };
  });

  // v3.5.99.7.9: Manually link an existing (unlinked) SoS row to this product.
  // Used when fuzzy-match found a candidate that the rep wants to confirm.
  // PATCHes schedule_of_services.product_id; does NOT touch display_name (the
  // SoS keeps its customer-facing name like "Managed Firewall (Per Location)"
  // while the product stays "Managed Firewall" — by design).
  async function linkSosToProduct(product, sosRow) {
    if (!product?.id || !sosRow?.id) return;
    if (!window.confirm(`Link product "${product.name}" to SoS #${sosRow.part_number} ("${sosRow.display_name}")?\n\nThis will not modify either record's name — only sets the foreign key so the linkage is reflected throughout the system.`)) return;

    setGeneratingSosForId(product.id);
    try {
      const { error } = await supabase
        .from('schedule_of_services')
        .update({ product_id: product.id, updated_by: profile?.id || null })
        .eq('id', sosRow.id);
      if (error) throw error;

      await logActivity({
        action: 'UPDATE',
        entityType: 'schedule_of_services',
        entityId: sosRow.id,
        entityName: sosRow.part_number,
        changes: {
          product_id: { from: null, to: product.id },
          linked_via: { from: null, to: 'fuzzy-match-button' },
          linked_product_name: { from: null, to: product.name },
        },
      });

      await load();
      window.alert(`✓ Linked "${product.name}" → #${sosRow.part_number}\n("${sosRow.display_name}")`);
    } catch (e) {
      console.error('Link SoS failed:', e);
      window.alert('Could not link SoS: ' + (e.message || 'unknown error'));
    } finally {
      setGeneratingSosForId(null);
    }
  }

  // v3.5.99.7.7: Generate a stub SoS row for a product that doesn't have one.
  // This is Phase A (structural) — content is intentionally minimal (status:
  // draft, review_status: needs_review, empty bullet arrays). The next deploy
  // (v3.5.99.7.8) wires AI generation to fill the sos_summary + bullets from
  // the product's name and description. Until then, the rep clicks Generate
  // to create the linked stub, then opens the SoS tab to fill in the content.
  //
  // part_number is auto-derived from the product name (slugified, uppercased,
  // hyphenated). If the derived part_number collides with an existing SoS
  // row's part_number, we append -2, -3, etc. The rep can edit the part_number
  // afterwards if they want something different.
  async function generateSosStub(product) {
    setGeneratingSosForId(product.id);
    try {
      // Slugify name → UPPERCASE-HYPHEN-FORMAT. Max ~40 chars. Strip
      // non-alphanumeric chars except hyphens. Collapse multiple hyphens.
      const slugify = (s) => String(s || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
      const base = slugify(product.name) || 'PRODUCT';

      // Collision check against ALL existing SoS rows (need fresh load, not
      // just the linked-only map). Fetch part_numbers cheaply.
      const { data: existing, error: exErr } = await supabase
        .from('schedule_of_services')
        .select('part_number');
      if (exErr) throw exErr;
      const taken = new Set((existing || []).map(r => (r.part_number || '').toUpperCase()));

      let partNumber = base;
      let suffix = 2;
      while (taken.has(partNumber.toUpperCase())) {
        partNumber = `${base}-${suffix}`;
        suffix += 1;
        if (suffix > 99) throw new Error('Could not find a unique part_number after 99 attempts');
      }

      // Sort order: place at the end of its category.
      const { data: sortRows } = await supabase
        .from('schedule_of_services')
        .select('sort_order')
        .eq('category', product.category)
        .order('sort_order', { ascending: false })
        .limit(1);
      const nextSort = sortRows?.[0]?.sort_order != null ? sortRows[0].sort_order + 10 : 1000;

      // v3.5.99.7.8: AI-generate the SoS content (summary + 3 bullet arrays)
      // before insert. Mirrors the assumptionsAI.js pattern — POSTs product
      // details to /.netlify/functions/sosAI which returns strict-JSON
      // content. On any failure (network, missing API key, parse error,
      // upstream error), we log a warning and fall back to empty content
      // arrays so the rep STILL gets a linked stub row they can fill
      // manually. Never blocks the row creation.
      let aiContent = { sos_summary: '', sos_included: [], sos_assumptions: [], sos_requirements: [] };
      let aiOk = false;
      let aiError = '';
      try {
        const aiRes = await fetch('/.netlify/functions/sosAI', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:         product.name,
            description:  product.description || '',
            category:     product.category || '',
            sub_category: product.sub_category || '',
          }),
        });
        if (aiRes.ok) {
          const j = await aiRes.json();
          if (j.result && typeof j.result.sos_summary === 'string'
              && Array.isArray(j.result.sos_included)
              && Array.isArray(j.result.sos_assumptions)
              && Array.isArray(j.result.sos_requirements)) {
            aiContent = j.result;
            aiOk = true;
          } else {
            aiError = 'AI response malformed';
          }
        } else {
          const errJson = await aiRes.json().catch(() => ({}));
          aiError = errJson.error || `AI returned ${aiRes.status}`;
          console.warn('SoS AI generation failed:', aiError);
        }
      } catch (e) {
        aiError = e.message || 'AI request error';
        console.warn('SoS AI generation error:', e);
      }

      const today = new Date().toISOString().slice(0, 10);
      const notes = aiOk
        ? `Auto-created with AI-generated content from Products admin (${today}). Review summary + bullets carefully before sending to customer — AI output is a starting draft, not final.`
        : `Auto-created STUB from Products admin (${today}). AI generation failed: ${aiError || 'unknown error'}. Fill in summary + bullets manually before sending to customer.`;

      const payload = {
        product_id:      product.id,
        part_number:     partNumber,
        category:        product.category || 'Managed IT Services',
        display_name:    product.name,
        sort_order:      nextSort,
        sos_summary:     aiContent.sos_summary,
        sos_included:    aiContent.sos_included,
        sos_assumptions: aiContent.sos_assumptions,
        sos_requirements:aiContent.sos_requirements,
        status:          'draft',
        review_status:   'needs_review',
        source_table:    'products',
        source_id:       product.id,
        notes:           notes,
        created_by:      profile?.id || null,
        updated_by:      profile?.id || null,
      };

      const { data: inserted, error: insErr } = await supabase
        .from('schedule_of_services')
        .insert(payload).select().single();
      if (insErr) throw insErr;

      await logActivity({
        action: 'CREATE',
        entityType: 'schedule_of_services',
        entityId: inserted.id,
        entityName: inserted.part_number,
        changes: {
          generated_from_product: { from: null, to: product.name },
          part_number:            { from: null, to: partNumber },
          ai_generated:           { from: null, to: aiOk },
        },
      });

      await load();

      // v3.5.99.7.8: surface what happened. The rep clicked Generate
      // expecting AI content, so they need to know if they got that or a
      // fallback stub (in which case they need to author content manually).
      if (aiOk) {
        window.alert(`✓ Generated Schedule of Services for "${product.name}"\nPart #: ${partNumber}\n\nAI-drafted content has been written to a new SoS entry. Open the Schedule of Services tab to review and refine before sending to customers.`);
      } else {
        window.alert(`⚠ Created empty stub for "${product.name}"\nPart #: ${partNumber}\n\nAI generation failed: ${aiError || 'unknown error'}\n\nThe SoS row is linked to the product but content is empty — open the Schedule of Services tab to fill in summary + bullets manually.`);
      }
    } catch (e) {
      console.error('Generate SoS stub failed:', e);
      window.alert('Could not generate SoS stub: ' + (e.message || 'unknown error'));
    } finally {
      setGeneratingSosForId(null);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>IT & Security Products</h2>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Add-on products for Managed IT and Bundle quotes — sell prices, costs, and per-product flags. Voice hardware and fax packages are managed separately under their own tabs.</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          <button onClick={() => setShowCatMgr(m => !m)}
            style={{ padding:'6px 12px', background:'white', border:'1px solid #d1d5db', borderRadius:5, fontSize:11, fontWeight:600, cursor:'pointer', color:'#374151' }}>
            ⚙ Categories ({categories.length})
          </button>
          <button onClick={startNew} style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor:'pointer' }}>+ Add Product</button>
        </div>
      </div>
      {/* Category manager */}
      {showCatMgr && (
        <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, padding:'12px 14px', marginBottom:12 }}>
          <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:8, letterSpacing:'.05em' }}>Manage Categories</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
            {categories.map(cat => (
              <span key={cat} style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 8px', background:'white', border:'1px solid #d1d5db', borderRadius:4, fontSize:11 }}>
                {cat}
                <button onClick={() => removeCategory(cat)}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:12, padding:0, lineHeight:1 }}>×</button>
              </span>
            ))}
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <input value={newCat} onChange={e => setNewCat(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              placeholder="New category name..."
              style={{ flex:1, padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>
            <button onClick={addCategory} disabled={!newCat.trim()}
              style={{ padding:'5px 12px', background:'#2563eb', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:700, cursor:'pointer', opacity:!newCat.trim()?.6:1 }}>
              Add
            </button>
          </div>
          <div style={{ fontSize:9, color:'#9ca3af', marginTop:5 }}>Categories are sorted alphabetically. Removing a category doesn't affect existing products — just removes it from the dropdown.</div>
        </div>
      )}

      {/* Search + category filter — consistent with Voice Hardware tab */}
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, category, or sub-category..."
          style={{ padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', width:280 }}/>
        <button onClick={()=>setCatFilter('')}
          style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${catFilter===''?'#2563eb':'#e5e7eb'}`, background:catFilter===''?'#eff6ff':'white', color:catFilter===''?'#1d4ed8':'#374151', fontSize:10, fontWeight:catFilter===''?700:400, cursor:'pointer' }}>
          All ({products.length})
        </button>
        {categories.map(cat => {
          const count = products.filter(p => p.category === cat).length;
          if (count === 0) return null;
          const active = catFilter === cat;
          return (
            <button key={cat} onClick={()=>setCatFilter(cat)}
              style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${active?'#2563eb':'#e5e7eb'}`, background:active?'#eff6ff':'white', color:active?'#1d4ed8':'#374151', fontSize:10, fontWeight:active?700:400, cursor:'pointer' }}>
              {cat} ({count})
            </button>
          );
        })}
        {(search || catFilter) && (
          <span style={{ fontSize:10, color:'#6b7280', marginLeft:'auto' }}>
            {filteredProducts.length} of {products.length} shown
          </span>
        )}
      </div>

      <AdminTable
        cols={['name','category','$sos','qty_driver','$sell','$cost','gm','flags']}
        rows={rows}
        onEdit={r => { setSaveError(''); setEditing(r); }}
        onToggle={toggle}
        loading={loading}
        extraActions={row => (
          <>
            {/* v3.5.99.7.7 + v3.5.99.7.8 + v3.5.99.7.9: SoS action button —
                conditional on linkage state:
                  Linked       → no button (badge alone communicates)
                  Match found  → Link SoS button (PATCHes product_id on the
                                 existing SoS row, no duplicate)
                  No match     → Generate SoS button (creates new row via AI) */}
            {!sosByProductId[row.id] && (
              sosCandidatesByProductId[row.id] ? (
                <button
                  onClick={() => linkSosToProduct(row, sosCandidatesByProductId[row.id].sos)}
                  disabled={generatingSosForId === row.id}
                  title={`Link to existing: #${sosCandidatesByProductId[row.id].sos.part_number} · "${sosCandidatesByProductId[row.id].sos.display_name}" (${Math.round(sosCandidatesByProductId[row.id].score * 100)}% match)`}
                  style={btnStyle('#dbeafe','#1e40af')}>
                  {generatingSosForId === row.id ? '...' : '↗ Link SoS'}
                </button>
              ) : (
                <button
                  onClick={() => generateSosStub(row)}
                  disabled={generatingSosForId === row.id}
                  style={btnStyle('#fef3c7','#92400e')}>
                  {generatingSosForId === row.id ? '...' : 'Generate SoS'}
                </button>
              )
            )}
            {/* v3.5.65: Clone Product — saves reps a lot of typing for variants
              of an existing product (different sub-category, different rate
              tier, etc.). Strips identity fields (id, created_at, updated_at,
              active state ignored) so the editor treats it as a brand-new
              product on save. The original is untouched. Name gets '(Copy)'
              appended so reps can rename before saving. */}
            <button
              onClick={() => {
                setSaveError('');
                const source = products.find(p => p.id === row.id) || row;
                const { id, created_at, updated_at, ...rest } = source;
                setEditing({
                  ...rest,
                  name: `${source.name || ''} (Copy)`,
                  active: true,
                  sort_order: (Number(source.sort_order) || 0) + 1,
                });
              }}
              style={btnStyle('#fef3c7','#92400e')}
            >
              Clone
            </button>
          </>
        )}
      />
      {editing && (
        <Modal title={editing.id ? 'Edit Product' : 'New Product'} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Product Name"><Input value={editing.name} onChange={v => setEditing(e => ({...e, name: v}))} /></Field>
            </div>
            <Field label="Category">
              <Select value={editing.category} onChange={v => setEditing(e => ({...e, category: v}))} opts={categories.map(c => [c, c])} />
            </Field>
            <Field label="Sub-category (optional)"><Input value={editing.sub_category || ''} onChange={v => setEditing(e => ({...e, sub_category: v}))} placeholder="e.g. INKY, vCIO" /></Field>
            <Field label="Sell Price ($)"><Input type="number" value={editing.sell_price} onChange={v => setEditing(e => ({...e, sell_price: v}))} /></Field>
            <Field label="Cost Price ($)"><Input type="number" value={editing.cost_price} onChange={v => setEditing(e => ({...e, cost_price: v}))} /></Field>
            {/* v3.5.60: Base Fee / Base Cost — flat MRR & COGS added on top of
                qty × rate. Used by Compliance Program products (HIPAA / SOC 2 /
                PCI / CMMC) where there's a fixed monthly program-management
                component (BAAs, audit support, evidence collection) plus a
                small per-user component for tooling/awareness. Default 0 means
                the product behaves exactly like legacy add-ons. */}
            <Field label="Base Fee ($/mo)">
              <div>
                <Input type="number" value={editing.base_fee || 0} onChange={v => setEditing(e => ({...e, base_fee: v}))} placeholder="0" />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>Flat monthly fee on top of qty × Sell Price. Use for compliance/oversight programs with a fixed cost component. Leave 0 for normal per-unit products.</div>
              </div>
            </Field>
            <Field label="Base Cost ($/mo)">
              <div>
                <Input type="number" value={editing.base_cost || 0} onChange={v => setEditing(e => ({...e, base_cost: v}))} placeholder="0" />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>Flat monthly internal cost on top of qty × Cost Price (admin/policy delivery time). Pairs with Base Fee for accurate GM.</div>
              </div>
            </Field>
            <Field label="Qty Driver (Sell)">
              <div>
                <Select value={editing.qty_driver} onChange={v => setEditing(e => ({...e, qty_driver: v}))} opts={QTY_DRIVERS.map(d => [d, d])} />
                {editing.qty_driver === 'manual' && (
                  <div style={{ fontSize: 10, color: '#6d28d9', marginTop: 3, fontWeight:600 }}>↳ Rep enters qty per quote — use for licenses (M365, Adobe, etc.) that don't track 1:1 with users or workstations</div>
                )}
              </div>
            </Field>
            <Field label="Qty Driver (Cost)" >
              <div>
                <Select value={editing.cost_qty_driver || ''} onChange={v => setEditing(e => ({...e, cost_qty_driver: v || null}))} opts={[['', '— same as sell —'], ...QTY_DRIVERS.map(d => [d, d])]} />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>Override cost basis only — e.g. sell per mailbox but cost per user</div>
              </div>
            </Field>
            <Field label="Exclusive Group (optional)">
              <Input value={editing.exclusive_group || ''} onChange={v => setEditing(e => ({...e, exclusive_group: v}))} placeholder="e.g. inky, endpoint_sec" />
            </Field>
            <div style={{ gridColumn:'1/-1', borderTop:'1px solid #f1f5f9', paddingTop:10, marginTop:2 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Pricing Protection Flags</div>
              <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input type="checkbox" checked={editing.no_discount || false}
                    onChange={e2 => setEditing(e => ({...e, no_discount: e2.target.checked}))}
                    style={{ width:14, height:14 }} />
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c' }}>No Discount</div>
                    <div style={{ fontSize:9, color:'#6b7280' }}>MSRP product — never discounted. Contract term discounts do not apply.</div>
                  </div>
                </label>
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input type="checkbox" checked={editing.no_commission || false}
                    onChange={e2 => setEditing(e => ({...e, no_commission: e2.target.checked}))}
                    style={{ width:14, height:14 }} />
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c' }}>No Commission</div>
                    <div style={{ fontSize:9, color:'#6b7280' }}>Vendor pass-through — excluded from commissionable revenue base.</div>
                  </div>
                </label>
                {/* v3.5.40: Internal-only flag for MSP infrastructure tools */}
                <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}>
                  <input type="checkbox" checked={editing.internal_only || false}
                    onChange={e2 => setEditing(e => ({...e, internal_only: e2.target.checked}))}
                    style={{ width:14, height:14, accentColor:'#7c3aed' }} />
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed' }}>Internal Only · MSP Tool</div>
                    <div style={{ fontSize:9, color:'#6b7280' }}>Hidden from rep add-on pickers. Use for NinjaOne, Datto, Hudu, Domotz, etc. — tools that contribute to cost basis but aren't sold to customers as add-ons. Can still be referenced as a Package Cost Component.</div>
                  </div>
                </label>
              </div>
            </div>
            <div style={{ gridColumn:'1/-1', marginTop:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Compliance Recommendations</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:6 }}>
                {[['hipaa','HIPAA','#0891b2'],['soc2','SOC 2','#7c3aed'],['pci','PCI DSS','#dc2626'],['cmmc','CMMC','#d97706']].map(([key, label, color]) => {
                  const tags = editing.compliance_tags || [];
                  const active = tags.includes(key);
                  return (
                    <button key={key} onClick={() => setEditing(e => ({...e, compliance_tags: active ? tags.filter(t=>t!==key) : [...tags, key]}))}
                      style={{ padding:'3px 10px', border:`2px solid ${active ? color : '#e5e7eb'}`, borderRadius:4, background: active ? color+'18' : 'white', color: active ? color : '#6b7280', fontSize:10, fontWeight:700, cursor:'pointer' }}>
                      {active ? '✓ ' : ''}{label}
                    </button>
                  );
                })}
              </div>
              <Field label="Recommendation reason (shown to rep when compliance matches)">
                <Input value={editing.recommendation_reason || ''} onChange={v => setEditing(e => ({...e, recommendation_reason: v}))} placeholder="e.g. Required for HIPAA — PHI email encryption..." />
              </Field>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Description"><Input value={editing.description || ''} onChange={v => setEditing(e => ({...e, description: v}))} /></Field>
            </div>
            <Field label="Sort Order"><Input type="number" value={editing.sort_order} onChange={v => setEditing(e => ({...e, sort_order: v}))} /></Field>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Notes (internal)"><textarea value={editing.notes || ''} onChange={e2 => setEditing(e => ({...e, notes: e2.target.value}))} rows={2}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, resize: 'vertical', outline: 'none' }} /></Field>
            </div>
          </div>
          {/* v3.5.60: GM preview now factors in base_fee/base_cost. For products
              with no base components (the common case), behavior is unchanged.
              For products with a Base Fee (e.g. Compliance Programs), the
              effective margin scales with quantity — we show the unit-level
              margin AND a reference @ qty=10 so admins can see how it scales. */}
          {(() => {
            const sell = parseFloat(editing.sell_price) || 0;
            const cost = parseFloat(editing.cost_price) || 0;
            const baseFee  = parseFloat(editing.base_fee)  || 0;
            const baseCost = parseFloat(editing.base_cost) || 0;
            const hasBase = baseFee > 0 || baseCost > 0;

            // Don't render anything if there's nothing useful to show
            if (sell <= 0 && baseFee <= 0) return null;
            if (cost <= 0 && baseCost <= 0) return null;

            // Reference quantity for the "scale" preview when base components exist
            const refQty = 10;
            const revAtRef  = sell * refQty + baseFee;
            const costAtRef = cost * refQty + baseCost;
            const gmAtRef   = revAtRef > 0 ? (1 - costAtRef / revAtRef) * 100 : 0;

            // Unit-only margin (sell vs cost, ignoring base) — useful for normal products
            const unitGM = sell > 0 ? (1 - cost / sell) * 100 : null;

            return (
              <div style={{ padding: '8px 10px', background: '#f0fdf4', borderRadius: 5, fontSize: 12, color: '#166534', marginTop: 4 }}>
                {hasBase ? (
                  <>
                    <div>
                      Effective margin @ qty {refQty}: <strong>{gmAtRef.toFixed(1)}%</strong>
                      {' '}<span style={{ color:'#15803d', fontFamily:'DM Mono, monospace', fontSize:11 }}>
                        (${revAtRef.toFixed(2)} rev / ${costAtRef.toFixed(2)} cost)
                      </span>
                    </div>
                    {unitGM != null && (
                      <div style={{ fontSize:10, color:'#15803d', marginTop:2 }}>
                        Per-unit margin: {unitGM.toFixed(1)}% — total margin scales with qty when Base Fee is non-zero
                      </div>
                    )}
                  </>
                ) : (
                  <>Gross margin: <strong>{unitGM.toFixed(1)}%</strong></>
                )}
              </div>
            );
          })()}
          {saveError && (
            <div style={{ padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, fontSize: 12, color: '#dc2626', marginTop: 8 }}>
              ✗ {saveError}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── PACKAGES ADMIN ───────────────────────────────────────────────────────────
function PackagesAdmin() {
  const [packages,  setPackages]  = useState([]);
  const [products,  setProducts]  = useState([]);   // for the "include products" picker
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');
  // Included-products state (v3.5.30): mirrors rows from package_includes.
  // Loaded when the modal opens, edited locally, persisted on save().
  // Each item shape: { id?, package_id, product_id, is_mandatory, notes, sort_order, _delete? }
  // Items with _delete:true are removed from DB on save.
  const [includes,         setIncludes]         = useState([]);
  const [includesLoading,  setIncludesLoading]  = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  // Cost components state (v3.5.40): mirrors rows from package_cost_components.
  // Same load-edit-persist pattern as includes. Each item shape:
  //   { id?, package_id, product_id, qty_driver, qty_per_unit, min_qty, notes, sort_order, _delete? }
  // The same product can appear multiple times with different qty_drivers, so the
  // picker doesn't filter by "alreadyIncluded" the way the includes picker does.
  const [components,         setComponents]         = useState([]);
  const [componentsLoading,  setComponentsLoading]  = useState(false);
  const [showComponentPicker, setShowComponentPicker] = useState(false);
  // Pricing Calculator state (v3.5.35) — which package's calculator is open
  const [calculatorPkg, setCalculatorPkg] = useState(null);
  const { profile } = useAuth();
  // Pull marketTiers + settings from useConfig for the pricing calculator engine call.
  // PackagesAdmin doesn't otherwise need these — they're calculator-only deps.
  const { marketTiers, settings } = useConfig();

  async function load() {
    setLoading(true);
    const [{ data: pkgs }, { data: prods }] = await Promise.all([
      supabase.from('packages').select('*').order('sort_order'),
      supabase.from('products').select('*').eq('active', true).order('category').order('name'),
    ]);
    setPackages(pkgs || []);
    setProducts(prods || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // When the editing modal opens for an existing package, load its includes
  useEffect(() => {
    if (!editing?.id) { setIncludes([]); return; }
    setIncludesLoading(true);
    supabase.from('package_includes')
      .select('*')
      .eq('package_id', editing.id)
      .order('sort_order')
      .then(({ data }) => {
        setIncludes((data || []).map(row => ({ ...row })));
        setIncludesLoading(false);
      });
  }, [editing?.id]);

  // v3.5.40: Load cost components when modal opens. Mirrors the includes effect.
  useEffect(() => {
    if (!editing?.id) { setComponents([]); return; }
    setComponentsLoading(true);
    supabase.from('package_cost_components')
      .select('*')
      .eq('package_id', editing.id)
      .eq('active', true)
      .order('sort_order')
      .then(({ data, error }) => {
        // Defensive: if the table doesn't exist yet (migration not run),
        // surface a clear error rather than silently rendering an empty list.
        if (error) {
          console.error('Failed to load package_cost_components:', error);
          setComponents([]);
        } else {
          setComponents((data || []).map(row => ({ ...row })));
        }
        setComponentsLoading(false);
      });
  }, [editing?.id]);

  async function save() {
    setSaving(true); setSaveError('');
    try {
      // Strip computed display-only fields before saving.
      // v3.5.57: 'pricing' was a display-only column added in v3.5.41 and
      // removed entirely in v3.5.57 along with the use_cost_components
      // toggle. No longer present in rows[]; no longer needed in the
      // strip list.
      const { '$ws': _ws, '$user': _user, '$server': _srv, '$location': _loc, ...editingClean } = editing;
      const payload = { ...editingClean,
        ws_rate:        parseFloat(editing.ws_rate)        || 0,
        user_rate:      parseFloat(editing.user_rate)      || 0,
        server_rate:    parseFloat(editing.server_rate)    || 0,
        location_rate:  parseFloat(editing.location_rate)  || 0,
        tenant_rate:    parseFloat(editing.tenant_rate)    || 0,
        vendor_rate:    parseFloat(editing.vendor_rate)    || 0,
        included_vendors: parseInt(editing.included_vendors) || 2,
        hrs_user:       parseFloat(editing.hrs_user)       || 0,
        hrs_ws:         parseFloat(editing.hrs_ws)         || 0,
        hrs_server:     parseFloat(editing.hrs_server)     || 0,
        hrs_location:   parseFloat(editing.hrs_location)   || 0,
        flex_minutes_per_ws: parseInt(editing.flex_minutes_per_ws) || 0,
        flex_label:       editing.flex_label || 'Flex Time (Onsite / Tier 2 Support)',
        flex_time_model:  editing.flex_time_model || 'none',
        // v3.5.57: use_cost_components dropped from packages table (every active
        // package was already on true; engine reads components unconditionally now).
        updated_by:     profile?.id
      };
      // Remove id from insert payload
      if (!editing.id) delete payload.id;
      const old = packages.find(p => p.id === editing.id);
      const isNew = !editing.id;
      let savedId = editing.id;
      if (isNew) {
        const { data, error } = await supabase.from('packages').insert(payload).select('id').single();
        if (error) throw new Error(error.message);
        savedId = data?.id;
      } else {
        const { error } = await supabase.from('packages').update(payload).eq('id', editing.id);
        if (error) throw new Error(error.message);
      }
      // Persist package_includes — sync local state to DB
      if (savedId) await syncIncludes(savedId);
      // v3.5.40: Persist package_cost_components — same pattern as includes
      if (savedId) await syncComponents(savedId);
      await logActivity({ action: isNew ? 'CREATE' : 'UPDATE', entityType: 'package',
        entityId: savedId, entityName: editing.name,
        changes: isNew ? payload : diffObjects(old, payload) });
      setEditing(null); load();
    } catch (e) {
      console.error('Package save error:', e);
      setSaveError(e.message || 'Save failed — check console for details.');
    }
    setSaving(false);
  }

  // Persist included-products edits made in the modal.
  // - Items marked _delete:true → DELETE from DB (if they have an id)
  // - New items (no id) → INSERT
  // - Existing items → UPDATE (always, even if unchanged — cheap and avoids needing dirty tracking)
  async function syncIncludes(packageId) {
    const toDelete = includes.filter(it => it._delete && it.id).map(it => it.id);
    const toInsert = includes.filter(it => !it._delete && !it.id).map(it => ({
      package_id:   packageId,
      product_id:   it.product_id,
      is_mandatory: !!it.is_mandatory,
      notes:        it.notes || null,
      sort_order:   parseInt(it.sort_order) || 100,
    }));
    const toUpdate = includes.filter(it => !it._delete && it.id);

    if (toDelete.length) {
      const { error } = await supabase.from('package_includes').delete().in('id', toDelete);
      if (error) throw new Error(`Delete includes: ${error.message}`);
    }
    if (toInsert.length) {
      const { error } = await supabase.from('package_includes').insert(toInsert);
      if (error) throw new Error(`Insert includes: ${error.message}`);
    }
    for (const it of toUpdate) {
      const { error } = await supabase.from('package_includes').update({
        is_mandatory: !!it.is_mandatory,
        notes:        it.notes || null,
        sort_order:   parseInt(it.sort_order) || 100,
        updated_at:   new Date().toISOString(),
      }).eq('id', it.id);
      if (error) throw new Error(`Update include: ${error.message}`);
    }
  }

  // v3.5.40 — Persist cost components edits to package_cost_components
  // Same delete/insert/update pattern as syncIncludes. Soft-deletes via active=false
  // would be possible too, but we use hard deletes for parity with includes.
  async function syncComponents(packageId) {
    const toDelete = components.filter(c => c._delete && c.id).map(c => c.id);
    const toInsert = components.filter(c => !c._delete && !c.id).map(c => ({
      package_id:    packageId,
      product_id:    c.product_id,
      qty_driver:    c.qty_driver || 'workstations',
      qty_per_unit:  parseFloat(c.qty_per_unit) || 1.0,
      min_qty:       parseInt(c.min_qty) || 0,
      notes:         c.notes || null,
      sort_order:    parseInt(c.sort_order) || 100,
      active:        true,
      updated_by:    profile?.id,
    }));
    const toUpdate = components.filter(c => !c._delete && c.id);

    if (toDelete.length) {
      const { error } = await supabase.from('package_cost_components').delete().in('id', toDelete);
      if (error) throw new Error(`Delete cost components: ${error.message}`);
    }
    if (toInsert.length) {
      const { error } = await supabase.from('package_cost_components').insert(toInsert);
      if (error) throw new Error(`Insert cost components: ${error.message}`);
    }
    for (const c of toUpdate) {
      const { error } = await supabase.from('package_cost_components').update({
        qty_driver:    c.qty_driver,
        qty_per_unit:  parseFloat(c.qty_per_unit) || 1.0,
        min_qty:       parseInt(c.min_qty) || 0,
        notes:         c.notes || null,
        sort_order:    parseInt(c.sort_order) || 100,
        updated_at:    new Date().toISOString(),
        updated_by:    profile?.id,
      }).eq('id', c.id);
      if (error) throw new Error(`Update cost component: ${error.message}`);
    }
  }

  async function toggle(row) {
    const { error } = await supabase.from('packages').update({ active: !row.active, updated_by: profile?.id }).eq('id', row.id);
    if (!error) {
      await logActivity({ action: row.active ? 'DEACTIVATE' : 'ACTIVATE', entityType: 'package', entityId: row.id, entityName: row.name });
      load();
    }
  }

  const rows = packages.map(p => ({
    ...p,
    '$ws': `$${p.ws_rate}/WS`,
    '$user': `$${p.user_rate}/US`,
    '$server': `$${p.server_rate}/server`,
    '$location': `$${p.location_rate}/LOC`,
    // v3.5.57: 'pricing' column removed — every package uses cost components now,
    // so the at-a-glance "✓ Components / — Legacy" indicator served no purpose.
  }));

  function startNew() {
    setEditing({
      name: '', coverage: 'business_hours', active: true, sort_order: packages.length + 1,
      ws_rate: '', user_rate: '', server_rate: '', location_rate: '', tenant_rate: 0,
      included_vendors: 2, vendor_rate: 25,
      hrs_user: 0.10, hrs_ws: 0.25, hrs_server: 0.60, hrs_location: 0.75,
      flex_minutes_per_ws: 0, flex_label: 'Flex Time (Onsite / Tier 2 Support)',
      ideal_desc: ''
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Managed IT Packages</h2>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Base rates, support hours, and package configuration</p>
        </div>
        <button onClick={startNew} style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ Add Package</button>
      </div>
      <AdminTable cols={['name','$ws','$user','$server','$location','coverage']} rows={rows} onEdit={setEditing} onToggle={toggle} loading={loading}
        extraActions={(row) => (
          <button onClick={(e) => { e.stopPropagation(); setCalculatorPkg(row); }}
            style={btnStyle('#faf5ff','#6d28d9')}>
            Calculate
          </button>
        )} />
      {calculatorPkg && (
        <PricingCalculatorModal
          pkg={calculatorPkg}
          packages={packages}
          marketTiers={marketTiers || []}
          products={products}
          settings={settings || {}}
          profile={profile}
          onClose={() => setCalculatorPkg(null)}
          onApplied={() => { setCalculatorPkg(null); load(); }}
        />
      )}
      {editing && (
        <Modal title={editing.id ? `Edit Package: ${editing.name}` : 'New Package'} onClose={() => { setEditing(null); setSaveError(''); }} onSave={save} saving={saving} width={900}>
          {saveError && <div style={{ padding:'7px 10px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, fontSize:11, color:'#dc2626', fontWeight:600, marginBottom:10 }}>✗ {saveError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
            <div style={{ gridColumn: '1/-1' }}><Field label="Package Name"><Input value={editing.name} onChange={v => setEditing(e => ({...e, name: v}))} /></Field></div>
            <Field label="Workstation Rate ($)"><Input type="number" value={editing.ws_rate} onChange={v => setEditing(e => ({...e, ws_rate: v}))} /></Field>
            <Field label="User Rate ($)"><Input type="number" value={editing.user_rate} onChange={v => setEditing(e => ({...e, user_rate: v}))} /></Field>
            <Field label="Server Rate ($)"><Input type="number" value={editing.server_rate} onChange={v => setEditing(e => ({...e, server_rate: v}))} /></Field>
            <Field label="Location Rate ($)"><Input type="number" value={editing.location_rate} onChange={v => setEditing(e => ({...e, location_rate: v}))} /></Field>
            <Field label="Cloud Tenant Rate ($)"><Input type="number" value={editing.tenant_rate} onChange={v => setEditing(e => ({...e, tenant_rate: v}))} /></Field>
            <Field label="Included Vendors"><Input type="number" value={editing.included_vendors} onChange={v => setEditing(e => ({...e, included_vendors: v}))} /></Field>
            <Field label="Extra Vendor Rate ($)"><Input type="number" value={editing.vendor_rate} onChange={v => setEditing(e => ({...e, vendor_rate: v}))} /></Field>
            <Field label="Coverage">
              <Select value={editing.coverage} onChange={v => setEditing(e => ({...e, coverage: v}))} opts={[['business_hours','Business Hours (8×5)'],['24x5','24×5'],['24x7','24×7']]} />
            </Field>
            <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Support Hours Per Unit</div>
            <Field label="Hours / User"><Input type="number" value={editing.hrs_user} onChange={v => setEditing(e => ({...e, hrs_user: v}))} /></Field>
            <Field label="Hours / Workstation"><Input type="number" value={editing.hrs_ws} onChange={v => setEditing(e => ({...e, hrs_ws: v}))} /></Field>
            <Field label="Hours / Server"><Input type="number" value={editing.hrs_server} onChange={v => setEditing(e => ({...e, hrs_server: v}))} /></Field>
            <Field label="Hours / Location"><Input type="number" value={editing.hrs_location} onChange={v => setEditing(e => ({...e, hrs_location: v}))} /></Field>
            <div style={{ gridColumn: '1/-1', fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flex / Onsite Time Included</div>
            <Field label="Flex Minutes Per Workstation / Month">
              <div>
                <Input type="number" value={editing.flex_minutes_per_ws ?? 0} onChange={v => setEditing(e => ({...e, flex_minutes_per_ws: parseInt(v)||0}))} />
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>0 = not included · positive = minutes per WS/month · -1 = unlimited</div>
              </div>
            </Field>
            <Field label="Flex Time Label">
              <Input value={editing.flex_label || ''} onChange={v => setEditing(e => ({...e, flex_label: v}))} placeholder="Flex Time (Onsite / Tier 2 Support)" />
            </Field>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Flex Time Model">
                <select value={editing.flex_time_model||'none'} onChange={e => setEditing(p => ({...p, flex_time_model: e.target.value}))}
                  style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white' }}>
                  <option value="none">None — Not available on this package</option>
                  <option value="included">Included — X min/WS per month (use field above)</option>
                  <option value="required">Required — Client must purchase a block</option>
                  <option value="all_inclusive">All-Inclusive — Unlimited Tier 1/2 remote support</option>
                </select>
                <div style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>Controls Flex Time block selector on quotes</div>
              </Field>
            </div>
            <div style={{ gridColumn: '1/-1' }}><Field label="Ideal For (description)"><Input value={editing.ideal_desc || ''} onChange={v => setEditing(e => ({...e, ideal_desc: v}))} /></Field></div>

            {/* ── Included Products (v3.5.30) ─────────────────────────────────── */}
            <div style={{ gridColumn: '1/-1', marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  📦 Included Products
                </div>
                <button type="button" onClick={() => setShowProductPicker(true)} disabled={!editing.id}
                  style={{ padding:'4px 10px', background: editing.id ? '#7c3aed' : '#d4d4d8', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:600, cursor: editing.id ? 'pointer' : 'not-allowed' }}>
                  + Add product
                </button>
              </div>
              {!editing.id && (
                <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                  Save the package first, then add included products.
                </div>
              )}
              {editing.id && includesLoading && (
                <div style={{ fontSize: 11, color: '#9ca3af', padding: '8px 0' }}>Loading includes…</div>
              )}
              {editing.id && !includesLoading && includes.filter(it => !it._delete).length === 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                  No products bundled with this package yet. Add products above.
                </div>
              )}
              {editing.id && !includesLoading && includes.filter(it => !it._delete).length > 0 && (
                <>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8, fontStyle: 'italic', lineHeight: 1.5 }}>
                    Adding products here doesn't change package rates. Adjust user_rate / ws_rate / etc. above to absorb the additional cost. Quote pages will display these in v3.5.31.
                  </div>
                  {/* v3.5.94.4: overflow-x:auto wrapper — defensive (same as
                      MSP Tool Stack below). 6 columns currently; fits at the
                      new 900px modal width. Future column additions get a
                      scrollable viewport rather than off-modal clipping. */}
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['Product', 'Sell × Driver', 'Cost × Driver', 'Locked?', 'Sort', ''].map(h => (
                          <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {includes.filter(it => !it._delete).sort((a,b) => (a.sort_order || 100) - (b.sort_order || 100)).map((it) => {
                        const prod = products.find(p => p.id === it.product_id);
                        if (!prod) return (
                          <tr key={it.id || `new-${it.product_id}`} style={{ background: '#fef2f2' }}>
                            <td colSpan={6} style={{ padding: '6px 8px', fontSize: 10, color: '#dc2626' }}>
                              ⚠ Product {it.product_id} no longer exists or is inactive. <button onClick={() => removeInclude(it)} style={{ color:'#dc2626', textDecoration:'underline', background:'none', border:'none', cursor:'pointer', fontSize:10 }}>Remove</button>
                            </td>
                          </tr>
                        );
                        return (
                          <tr key={it.id || `new-${it.product_id}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 8px' }}>
                              <div style={{ fontWeight: 600, color: '#374151' }}>{prod.name}</div>
                              <div style={{ fontSize: 10, color: '#9ca3af' }}>{prod.category}{prod.sub_category ? ` › ${prod.sub_category}` : ''}</div>
                            </td>
                            <td style={{ padding: '5px 8px', fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#0f766e' }}>
                              ${Number(prod.sell_price).toFixed(2)} / {prod.qty_driver}
                            </td>
                            <td style={{ padding: '5px 8px', fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#1e40af' }}>
                              ${Number(prod.cost_price).toFixed(2)} / {prod.cost_qty_driver || prod.qty_driver}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                              <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <input type="checkbox" checked={!!it.is_mandatory}
                                  onChange={e => updateInclude(it, { is_mandatory: e.target.checked })}
                                  style={{ accentColor: '#7c3aed' }} />
                                <span style={{ fontSize: 10, color: it.is_mandatory ? '#7c3aed' : '#9ca3af' }}>
                                  {it.is_mandatory ? 'Mandatory' : 'Swappable'}
                                </span>
                              </label>
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <input type="number" value={it.sort_order ?? 100}
                                onChange={e => updateInclude(it, { sort_order: e.target.value === '' ? null : parseInt(e.target.value) })}
                                style={{ width: 50, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, fontFamily: 'DM Mono, monospace' }} />
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                              <button type="button" onClick={() => removeInclude(it)}
                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  {/* Cost-impact summary by qty_driver */}
                  <IncludeCostSummary includes={includes.filter(it => !it._delete)} products={products} />
                </>
              )}
            </div>
            {/* ── End Included Products ───────────────────────────────────────── */}

            {/* ── Cost Components (v3.5.40) ──────────────────────────────────────
                Per-package list of internal MSP tools that contribute to COGS.
                Mirrors Included Products in shape but for internal-only items.
                v3.5.41 wired these into the pricing engine via the
                use_cost_components toggle. v3.5.57 dropped the toggle (every
                active package was migrated, gate verified via SQL) — the
                engine now reads components unconditionally.
            */}
            <div style={{ gridColumn: '1/-1', marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  🛠 Cost Components — MSP Tool Stack
                </div>
                <button type="button" onClick={() => setShowComponentPicker(true)} disabled={!editing.id}
                  style={{ padding:'4px 10px', background: editing.id ? '#0f766e' : '#d4d4d8', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:600, cursor: editing.id ? 'pointer' : 'not-allowed' }}>
                  + Add tool
                </button>
              </div>

              {!editing.id && (
                <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
                  Save the package first, then add cost components.
                </div>
              )}
              {editing.id && componentsLoading && (
                <div style={{ fontSize: 11, color: '#9ca3af', padding: '8px 0' }}>Loading components…</div>
              )}
              {editing.id && !componentsLoading && components.filter(c => !c._delete).length === 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0', lineHeight:1.5 }}>
                  No cost components configured for this package yet. Add tools above (NinjaOne, Datto, Hudu, Domotz, etc.) to track COGS per tool.
                </div>
              )}
              {editing.id && !componentsLoading && components.filter(c => !c._delete).length > 0 && (
                <>
                  <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8, fontStyle: 'italic', lineHeight: 1.5 }}>
                    Each row maps a product (the tool) to a quantity driver. Cost is product.cost_price × max(min_qty, inputs[qty_driver] × qty_per_unit). The toggle above controls whether the engine consumes these rows.
                  </div>
                  {/* v3.5.94.4: overflow-x:auto wrapper — defensive. The modal
                      is now 900px wide which fits the current 8 columns, but
                      future column additions (or larger MIN QTY values that
                      widen the number-input cells) could reintroduce horizontal
                      overflow. The wrapper gives the table its own scrollable
                      viewport rather than pushing content off the modal edge
                      with no scrollbar — the exact failure mode in the May 12
                      2026 screenshot. */}
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['Tool', 'Cost Price', 'Qty Driver', 'Per Unit', 'Min Qty', 'Notes', 'Sort', ''].map(h => (
                          <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {components.filter(c => !c._delete).sort((a,b) => (a.sort_order || 100) - (b.sort_order || 100)).map((c) => {
                        const prod = products.find(p => p.id === c.product_id);
                        if (!prod) return (
                          <tr key={c.id || `new-${c.product_id}-${c.qty_driver}`} style={{ background: '#fef2f2' }}>
                            <td colSpan={8} style={{ padding: '6px 8px', fontSize: 10, color: '#dc2626' }}>
                              ⚠ Product {c.product_id} no longer exists or is inactive.{' '}
                              <button onClick={() => removeComponent(c)} style={{ color:'#dc2626', textDecoration:'underline', background:'none', border:'none', cursor:'pointer', fontSize:10 }}>Remove</button>
                            </td>
                          </tr>
                        );
                        return (
                          <tr key={c.id || `new-${c.product_id}-${c.qty_driver}-${c.sort_order}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 8px' }}>
                              <div style={{ fontWeight: 600, color: '#374151' }}>
                                {prod.name}
                                {prod.internal_only && (
                                  <span style={{ marginLeft: 5, fontSize: 8, color: '#7c3aed', background: '#faf5ff', padding: '1px 4px', borderRadius: 2, fontWeight: 700 }}>INTERNAL</span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: '#9ca3af' }}>{prod.category}{prod.sub_category ? ` › ${prod.sub_category}` : ''}</div>
                            </td>
                            <td style={{ padding: '5px 8px', fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#1e40af' }}>
                              ${Number(prod.cost_price).toFixed(2)}
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <select value={c.qty_driver || 'workstations'}
                                onChange={e => updateComponent(c, { qty_driver: e.target.value })}
                                style={{ padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, background: 'white' }}>
                                {['workstations','users','servers','locations','cloud_tenants','endpoints','mobile_devices','domains','flat'].map(d =>
                                  <option key={d} value={d}>{d}</option>
                                )}
                              </select>
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <input type="number" step="0.01" value={c.qty_per_unit ?? 1.0}
                                onChange={e => updateComponent(c, { qty_per_unit: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                style={{ width: 60, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, fontFamily: 'DM Mono, monospace' }} />
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <input type="number" min="0" value={c.min_qty ?? 0}
                                onChange={e => updateComponent(c, { min_qty: e.target.value === '' ? '' : parseInt(e.target.value) })}
                                style={{ width: 50, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, fontFamily: 'DM Mono, monospace' }} />
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <input type="text" value={c.notes || ''}
                                onChange={e => updateComponent(c, { notes: e.target.value })}
                                placeholder="optional"
                                style={{ width: 130, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11 }} />
                            </td>
                            <td style={{ padding: '5px 8px' }}>
                              <input type="number" value={c.sort_order ?? 100}
                                onChange={e => updateComponent(c, { sort_order: e.target.value === '' ? null : parseInt(e.target.value) })}
                                style={{ width: 50, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 3, fontSize: 11, fontFamily: 'DM Mono, monospace' }} />
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                              <button type="button" onClick={() => removeComponent(c)}
                                style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 11 }}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  {/* Estimated cost preview at sample volumes (50ws / 30 users / 2 servers / 1 location) */}
                  <ComponentCostPreview components={components.filter(c => !c._delete)} products={products} />
                </>
              )}
            </div>
            {/* ── End Cost Components ─────────────────────────────────────────── */}
          </div>
          {/* Product picker sub-modal */}
          {showProductPicker && (
            <ProductPickerModal
              products={products}
              alreadyIncluded={includes.filter(it => !it._delete).map(it => it.product_id)}
              onClose={() => setShowProductPicker(false)}
              onPick={(productIds) => {
                setIncludes(prev => [
                  ...prev,
                  ...productIds.map((pid, i) => ({
                    package_id:   editing.id,
                    product_id:   pid,
                    is_mandatory: true,
                    notes:        null,
                    sort_order:   100 + (prev.filter(it => !it._delete).length + i) * 10,
                  })),
                ]);
                setShowProductPicker(false);
              }}
            />
          )}
          {/* v3.5.40: Cost component picker. Different from ProductPickerModal:
              - Doesn't filter alreadyIncluded (same product can appear with different qty_drivers)
              - Highlights internal_only products (preferred for cost components)
              - Shows cost_price prominently (sell_price is irrelevant here) */}
          {showComponentPicker && (
            <ComponentPickerModal
              products={products}
              onClose={() => setShowComponentPicker(false)}
              onPick={addComponents}
            />
          )}
        </Modal>
      )}
    </div>
  );

  function updateInclude(item, patch) {
    setIncludes(prev => prev.map(it => it === item ? { ...it, ...patch } : it));
  }
  function removeInclude(item) {
    if (item.id) {
      // Mark for DB deletion on save
      setIncludes(prev => prev.map(it => it === item ? { ...it, _delete: true } : it));
    } else {
      // Never persisted — just drop it from local state
      setIncludes(prev => prev.filter(it => it !== item));
    }
  }

  // v3.5.40 — same pattern as includes helpers, for cost components
  function addComponents(productIds) {
    setComponents(prev => [
      ...prev,
      ...productIds.map((pid, i) => ({
        product_id:    pid,
        qty_driver:    'workstations',  // sensible default; admin edits inline
        qty_per_unit:  1.0,
        min_qty:       0,
        notes:         '',
        sort_order:    (prev.length + i + 1) * 10,
      })),
    ]);
    setShowComponentPicker(false);
  }
  function updateComponent(item, patch) {
    setComponents(prev => prev.map(c => c === item ? { ...c, ...patch } : c));
  }
  function removeComponent(item) {
    if (item.id) {
      setComponents(prev => prev.map(c => c === item ? { ...c, _delete: true } : c));
    } else {
      setComponents(prev => prev.filter(c => c !== item));
    }
  }
}

// ─── Product picker for the package-includes section (v3.5.30) ────────────────
// Lets admin select one or more products to include in a package. Already-included
// products are filtered out so admin can't double-add. Search filters by name/category.
function ProductPickerModal({ products, alreadyIncluded, onClose, onPick }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');

  const available = products.filter(p => !alreadyIncluded.includes(p.id));
  const categories = [...new Set(available.map(p => p.category))].sort();

  const filtered = available.filter(p => {
    if (categoryFilter && p.category !== categoryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
    }
    return true;
  });

  function togglePick(id) {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:600 }}>
      <div style={{ background:'white', borderRadius:8, padding:20, width:680, maxHeight:'85vh', display:'flex', flexDirection:'column', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>Add Products to Package</h3>
          <div style={{ fontSize:11, color:'#6b7280' }}>{picked.size} selected · {available.length} available</div>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or description..."
            style={{ flex:1, padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }} />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            style={{ padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', minWidth:140 }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ flex:1, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:4 }}>
          {filtered.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:'#9ca3af', fontSize:11 }}>No products match.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} onClick={() => togglePick(p.id)}
                    style={{ borderBottom:'1px solid #f3f4f6', cursor:'pointer', background: picked.has(p.id) ? '#faf5ff' : 'white' }}>
                    <td style={{ padding:'7px 10px', width:30 }}>
                      <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)}
                        style={{ accentColor:'#7c3aed', cursor:'pointer' }} />
                    </td>
                    <td style={{ padding:'7px 10px' }}>
                      <div style={{ fontWeight:600, color:'#374151' }}>{p.name}</div>
                      <div style={{ fontSize:10, color:'#9ca3af' }}>
                        {p.category}{p.sub_category ? ` › ${p.sub_category}` : ''}{p.description ? ` — ${p.description.substring(0, 80)}${p.description.length > 80 ? '…' : ''}` : ''}
                      </div>
                    </td>
                    <td style={{ padding:'7px 10px', fontFamily:'DM Mono, monospace', fontSize:10, whiteSpace:'nowrap', textAlign:'right' }}>
                      <div style={{ color:'#0f766e' }}>${Number(p.sell_price).toFixed(2)}/{p.qty_driver}</div>
                      <div style={{ color:'#1e40af' }}>${Number(p.cost_price).toFixed(2)}/{p.cost_qty_driver || p.qty_driver}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
          <button onClick={onClose}
            style={{ padding:'6px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:4, fontSize:11, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={() => onPick([...picked])} disabled={picked.size === 0}
            style={{ padding:'6px 14px', background: picked.size === 0 ? '#d4d4d8' : '#7c3aed', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:600, cursor: picked.size === 0 ? 'not-allowed' : 'pointer' }}>
            Add {picked.size > 0 ? `${picked.size} product${picked.size === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cost summary aggregator (v3.5.30) ────────────────────────────────────────
// Shows admin the total monthly COGS from included products, broken down by
// qty_driver. Helps admin decide how much to bump user_rate / ws_rate / etc.
function IncludeCostSummary({ includes, products }) {
  // Build totals per driver
  const totals = {};
  for (const it of includes) {
    const prod = products.find(p => p.id === it.product_id);
    if (!prod) continue;
    const driver = prod.cost_qty_driver || prod.qty_driver;
    const cost = Number(prod.cost_price) || 0;
    totals[driver] = (totals[driver] || 0) + cost;
  }
  const entries = Object.entries(totals).filter(([, v]) => v > 0);
  if (entries.length === 0) return null;

  return (
    <div style={{ marginTop: 10, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 10 }}>
      <div style={{ fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        💰 Monthly cost contribution
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, color: '#166534', fontFamily: 'DM Mono, monospace' }}>
        {entries.map(([driver, total]) => (
          <span key={driver}>
            <strong>${total.toFixed(2)}</strong> per {driver}
          </span>
        ))}
      </div>
      <div style={{ fontSize: 9, color: '#15803d', marginTop: 4, fontStyle: 'italic' }}>
        Adjust the package rates above (user_rate, ws_rate, etc.) to cover these costs plus margin.
      </div>
    </div>
  );
}

// ─── Cost component picker (v3.5.40) ─────────────────────────────────────────
// Picks one or more products to add as cost components. Differs from
// ProductPickerModal:
//   - No alreadyIncluded filter (same product can appear with different qty_drivers)
//   - Internal-only products are highlighted (preferred for cost components)
//   - Cost price is the prominent column; sell price is irrelevant here
//   - Default sort: internal_only products first, then by category/name
function ComponentPickerModal({ products, onClose, onPick }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [internalOnlyFilter, setInternalOnlyFilter] = useState(false);

  const categories = [...new Set(products.map(p => p.category))].sort();

  const filtered = products
    .filter(p => p.active !== false)
    .filter(p => internalOnlyFilter ? p.internal_only : true)
    .filter(p => categoryFilter ? p.category === categoryFilter : true)
    .filter(p => {
      if (!search) return true;
      const q = search.toLowerCase();
      return p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
    })
    // Sort: internal-only first (preferred for cost components), then alphabetical
    .sort((a, b) => {
      if (!!a.internal_only !== !!b.internal_only) return a.internal_only ? -1 : 1;
      return (a.category || '').localeCompare(b.category || '') || (a.name || '').localeCompare(b.name || '');
    });

  function togglePick(id) {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:600 }}>
      <div style={{ background:'white', borderRadius:8, padding:20, width:720, maxHeight:'85vh', display:'flex', flexDirection:'column', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>Add Cost Components to Package</h3>
          <div style={{ fontSize:11, color:'#6b7280' }}>{picked.size} selected · {filtered.length} shown</div>
        </div>
        <div style={{ fontSize:10, color:'#6b7280', marginBottom:10, fontStyle:'italic' }}>
          Pick MSP infrastructure tools that contribute to this package's cost basis. Internal-only products are listed first. The same product can be added multiple times if it scales by different drivers (e.g. NinjaOne per workstation AND per server).
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or description..."
            style={{ flex:1, padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }} />
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            style={{ padding:'6px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', minWidth:140 }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'#7c3aed', cursor:'pointer' }}>
            <input type="checkbox" checked={internalOnlyFilter}
              onChange={e => setInternalOnlyFilter(e.target.checked)}
              style={{ accentColor:'#7c3aed' }} />
            Internal-only
          </label>
        </div>
        <div style={{ flex:1, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:4 }}>
          {filtered.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:'#9ca3af', fontSize:11 }}>No products match.</div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id} onClick={() => togglePick(p.id)}
                    style={{ borderBottom:'1px solid #f3f4f6', cursor:'pointer', background: picked.has(p.id) ? '#ecfdf5' : 'white' }}>
                    <td style={{ padding:'7px 10px', width:30 }}>
                      <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)}
                        style={{ accentColor:'#0f766e', cursor:'pointer' }} />
                    </td>
                    <td style={{ padding:'7px 10px' }}>
                      <div style={{ fontWeight:600, color:'#374151' }}>
                        {p.name}
                        {p.internal_only && (
                          <span style={{ marginLeft:6, fontSize:8, color:'#7c3aed', background:'#faf5ff', padding:'1px 5px', borderRadius:2, fontWeight:700, letterSpacing:'.02em' }}>INTERNAL</span>
                        )}
                      </div>
                      <div style={{ fontSize:10, color:'#9ca3af' }}>
                        {p.category}{p.sub_category ? ` › ${p.sub_category}` : ''}{p.description ? ` — ${p.description.substring(0, 80)}${p.description.length > 80 ? '…' : ''}` : ''}
                      </div>
                    </td>
                    <td style={{ padding:'7px 10px', fontFamily:'DM Mono, monospace', fontSize:10, whiteSpace:'nowrap', textAlign:'right' }}>
                      <div style={{ color:'#1e40af', fontWeight:700 }}>${Number(p.cost_price).toFixed(2)} cost</div>
                      <div style={{ color:'#9ca3af', fontSize:9 }}>${Number(p.sell_price).toFixed(2)} sell</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
          <button onClick={onClose}
            style={{ padding:'6px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:4, fontSize:11, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={() => onPick([...picked])} disabled={picked.size === 0}
            style={{ padding:'6px 14px', background: picked.size === 0 ? '#d4d4d8' : '#0f766e', color:'white', border:'none', borderRadius:4, fontSize:11, fontWeight:600, cursor: picked.size === 0 ? 'not-allowed' : 'pointer' }}>
            Add {picked.size > 0 ? `${picked.size} component${picked.size === 1 ? '' : 's'}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cost component preview (v3.5.40) ────────────────────────────────────────
// Shows admin the projected COGS at a sample volume (50ws / 30 users / 2 servers
// / 1 location / 1 cloud_tenant) so they can sanity-check the configuration
// before saving. Mirrors IncludeCostSummary in spirit but uses the qty_driver
// + qty_per_unit + min_qty math.
function ComponentCostPreview({ components, products }) {
  // Sample volume — typical mid-market deal. Admin should treat as reference only.
  const sample = {
    workstations:   50,
    users:          30,
    servers:        2,
    locations:      1,
    cloud_tenants:  1,
    endpoints:      55,
    mobile_devices: 20,
    flat:           1,  // flat treats qty_per_unit as constant per quote
  };
  let total = 0;
  const breakdown = [];
  for (const c of components) {
    const prod = products.find(p => p.id === c.product_id);
    if (!prod) continue;
    const driverQty = sample[c.qty_driver] ?? 0;
    const perUnit = parseFloat(c.qty_per_unit) || 1.0;
    const minQty = parseInt(c.min_qty) || 0;
    const qty = Math.max(minQty, c.qty_driver === 'flat' ? perUnit : driverQty * perUnit);
    const lineCost = qty * (parseFloat(prod.cost_price) || 0);
    total += lineCost;
    breakdown.push({ name: prod.name, driver: c.qty_driver, qty, lineCost });
  }
  if (breakdown.length === 0) return null;

  return (
    <div style={{ marginTop: 10, padding: '8px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 10 }}>
      <div style={{ fontWeight: 700, color: '#075985', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        🧮 Projected COGS at sample volume (50 ws / 30 users / 2 servers / 1 location)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '2px 12px', color: '#075985', fontFamily: 'DM Mono, monospace', fontSize: 10 }}>
        {breakdown.map((b, i) => (
          <React.Fragment key={i}>
            <span>{b.name}</span>
            <span style={{ textAlign: 'right', color:'#64748b' }}>{b.qty.toFixed(2)} × {b.driver}</span>
            <span style={{ textAlign: 'right', fontWeight: 700 }}>${b.lineCost.toFixed(2)}</span>
          </React.Fragment>
        ))}
        <span style={{ fontWeight: 700, borderTop: '1px solid #bae6fd', paddingTop: 3 }}>Total monthly COGS</span>
        <span></span>
        <span style={{ textAlign: 'right', fontWeight: 700, borderTop: '1px solid #bae6fd', paddingTop: 3 }}>${total.toFixed(2)}</span>
      </div>
      <div style={{ fontSize: 9, color: '#0369a1', marginTop: 5, fontStyle: 'italic' }}>
        Reference only — actual COGS scales with the quote's real volumes. Engine cutover ships in v3.5.41.
      </div>
    </div>
  );
}

// ─── PRICING CALCULATOR MODAL (v3.5.35) ──────────────────────────────────────
// Helps admin derive package per-unit rates that hit a target gross margin.
// Workflow:
//   1. Admin clicks "Calculate" on a package row in PackagesAdmin
//   2. Modal opens with default reference scenario (25 users / 30 ws / 1 server / 1 loc)
//   3. Admin can edit scenario + target GM; current state recomputes live via calcQuote
//   4. Modal shows current GM vs target, with a suggested-rates table (proportional uplift on labor)
//   5. Admin clicks "Apply Suggested Rates" → updates the package row in DB, closes modal,
//      parent reloads packages list
//
// Math:
//   - target_revenue = current_cogs / (1 - target_gm)
//   - gap = target_revenue - current_revenue
//   - labor_uplift = (current_labor + gap) / current_labor
//   - new_rate = current_rate × labor_uplift  (applied to ws_rate, user_rate, server_rate, location_rate, tenant_rate, vendor_rate)
//
// Edge cases handled inline (current revenue = 0, already above target, etc.)
function PricingCalculatorModal({ pkg, packages, marketTiers, products, settings, profile, onClose, onApplied }) {
  // Default reference scenario per design — admin can adjust
  const [scenario, setScenario] = useState({
    users:        25,
    workstations: 30,
    servers:      1,
    locations:    1,
    cloudTenants: 1,
    vendors:      4,
    endpoints:    30,         // mirror workstations by default — no density uplift
    sharedMailboxes: 0,
    mobileDevices:   0,
    // v3.5.96.0: domains added as a Reference Scenario input so admins
    // can preview pricing for packages with domain-driven cost components
    // or per-domain product line items. Default 0 so the scenario doesn't
    // change baseline pricing for existing packages — admin opts in by
    // setting a non-zero value.
    domains:         0,
  });
  // Target GM defaults to settings.target_gross_margin if available, else 0.40
  const settingsTarget = parseFloat(settings?.target_gross_margin);
  const [targetGM, setTargetGM] = useState(
    Number.isFinite(settingsTarget) && settingsTarget > 0 ? settingsTarget : 0.40
  );
  // Package includes — load on mount so the engine has them
  const [includes, setIncludes] = useState([]);
  const [includesLoading, setIncludesLoading] = useState(true);
  // Apply state
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState('');

  useEffect(() => {
    if (!pkg?.id) return;
    setIncludesLoading(true);
    loadPackageIncludes(pkg.id).then(rows => {
      setIncludes(rows || []);
      setIncludesLoading(false);
    });
  }, [pkg?.id]);

  // Pick the mid-market tier as the reference (matches QuotePage default)
  const refMarketTier = marketTiers.find(t => t.tier_key === 'mid_market') || marketTiers[0] || null;

  // Build inputs for calcQuote — clean defaults, no add-ons selected, neutral multipliers
  const inputs = {
    users:        parseInt(scenario.users)        || 0,
    workstations: parseInt(scenario.workstations) || 0,
    servers:      parseInt(scenario.servers)      || 0,
    locations:    parseInt(scenario.locations)    || 0,
    cloudTenants: parseInt(scenario.cloudTenants) || 0,
    vendors:      parseInt(scenario.vendors)      || 0,
    endpoints:    parseInt(scenario.endpoints)    || parseInt(scenario.workstations) || 0,
    sharedMailboxes: parseInt(scenario.sharedMailboxes) || 0,
    mobileDevices:   parseInt(scenario.mobileDevices)   || 0,
    selectedProducts: [],            // calculator doesn't model add-on selection
    contractTerm:     12,            // no contract discount applied
    requestedCoverage: pkg?.coverage || 'business_hours',
    industryRisk:     'low',
    compliance:       'none',
    complexity:       'low',
    execReporting:    false,
    manualQuantities: {},
  };

  // Compute current state at this scenario
  const currentResult = (refMarketTier && pkg && settings && !includesLoading)
    ? calcQuote({ inputs, pkg, marketTier: refMarketTier, products, settings,
        packageIncludes: includes, excludedIncludes: [], includeSwaps: {} })
    : null;

  // Compute target revenue + suggested rates
  const targetCalc = (() => {
    if (!currentResult) return null;
    const currentRevenue = currentResult.finalMRR;
    const currentCogs    = currentResult.totalCost;
    const currentGM      = currentResult.impliedGM;
    if (!Number.isFinite(currentRevenue) || currentRevenue <= 0) {
      return { error: 'Reference scenario has zero revenue. Add users or workstations.' };
    }
    if (!Number.isFinite(targetGM) || targetGM <= 0 || targetGM >= 1) {
      return { error: 'Target GM must be between 0 and 1 (e.g., 0.45 for 45%).' };
    }
    if (currentGM >= targetGM) {
      return {
        currentRevenue, currentCogs, currentGM,
        alreadyAboveTarget: true,
        targetRevenue: currentRevenue,  // no change needed
      };
    }
    const targetRevenue = currentCogs / (1 - targetGM);
    const gap           = targetRevenue - currentRevenue;
    // Labor portion of revenue = itSubtotal-derived contributions through the engine.
    // We use itSubtotal directly (pre-multiplier) because the multipliers and discount apply
    // to the same value proportionally — the uplift factor is invariant under those transforms.
    // Note: addonRevenue and includedItemsCost are NOT driven by package rates,
    // so the gap can ONLY be closed by changing labor rates.
    const currentLabor = currentResult.itSubtotal;
    if (!Number.isFinite(currentLabor) || currentLabor <= 0) {
      return { error: 'Cannot suggest rates — labor portion of revenue is zero. Check rate fields.' };
    }
    const targetLabor   = currentLabor + gap;
    const upliftFactor  = targetLabor / currentLabor;
    // Edge case: when current revenue equals the floor exactly, the labor portion
    // we're calculating from may not be what's actually driving revenue. Suggest the
    // uplift but flag it so admin knows to verify.
    const floorBound = (currentResult.finalMRR <= currentResult.floor + 0.01);
    const suggestedRates = {
      ws_rate:       (parseFloat(pkg.ws_rate)       || 0) * upliftFactor,
      user_rate:     (parseFloat(pkg.user_rate)     || 0) * upliftFactor,
      server_rate:   (parseFloat(pkg.server_rate)   || 0) * upliftFactor,
      location_rate: (parseFloat(pkg.location_rate) || 0) * upliftFactor,
      tenant_rate:   (parseFloat(pkg.tenant_rate)   || 0) * upliftFactor,
      vendor_rate:   (parseFloat(pkg.vendor_rate)   || 0) * upliftFactor,
    };
    return {
      currentRevenue, currentCogs, currentGM,
      targetRevenue, gap, currentLabor, upliftFactor, suggestedRates,
      floorBound,
    };
  })();

  async function applyRates() {
    if (!targetCalc?.suggestedRates) return;
    setApplying(true);
    setApplyError('');
    try {
      const updates = {
        ws_rate:       Number(targetCalc.suggestedRates.ws_rate.toFixed(2)),
        user_rate:     Number(targetCalc.suggestedRates.user_rate.toFixed(2)),
        server_rate:   Number(targetCalc.suggestedRates.server_rate.toFixed(2)),
        location_rate: Number(targetCalc.suggestedRates.location_rate.toFixed(2)),
        tenant_rate:   Number(targetCalc.suggestedRates.tenant_rate.toFixed(2)),
        vendor_rate:   Number(targetCalc.suggestedRates.vendor_rate.toFixed(2)),
        updated_by:    profile?.id,
      };
      const old = packages.find(p => p.id === pkg.id);
      const { error } = await supabase.from('packages').update(updates).eq('id', pkg.id);
      if (error) throw new Error(error.message);
      // Build a diff of just the rate fields for the activity log
      const diff = {};
      for (const k of Object.keys(updates)) {
        if (k === 'updated_by') continue;
        if (old?.[k] !== updates[k]) diff[k] = { from: old?.[k], to: updates[k] };
      }
      await logActivity({
        action: 'UPDATE', entityType: 'package',
        entityId: pkg.id, entityName: pkg.name,
        changes: { ...diff, _via: 'pricing_calculator', _target_gm: targetGM, _scenario: scenario },
      });
      onApplied();
    } catch (e) {
      console.error('Apply rates failed:', e);
      setApplyError(e.message || 'Failed to apply rates.');
      setApplying(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (includesLoading || !refMarketTier) {
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:600 }}>
        <div style={{ background:'white', borderRadius:8, padding:24, fontSize:12, color:'#6b7280' }}>
          Loading calculator…
        </div>
      </div>
    );
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:600 }}>
      <div style={{ background:'white', borderRadius:8, padding:20, width:760, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1e3c', margin:0 }}>
            🧮 Pricing Calculator: {pkg.name}
          </h3>
          <button onClick={onClose} style={{ background:'transparent', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>
        <p style={{ fontSize:11, color:'#6b7280', marginTop:0, marginBottom:14 }}>
          Derives suggested per-unit rates that hit the target gross margin at a reference client size.
          Suggestions are starting points — admin should review and adjust as needed before applying.
        </p>

        {/* Reference Scenario */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
            Reference Scenario
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10 }}>
            {[
              ['users',         'Users'],
              ['workstations',  'Workstations'],
              ['servers',       'Servers'],
              ['locations',     'Locations'],
              ['cloudTenants',  'Cloud Tenants'],
              ['vendors',       'Vendors'],
              ['endpoints',     'Endpoints'],
              ['sharedMailboxes','Shared Mailboxes'],
              // v3.5.96.0: Domains added at the end of the scenario list so
              // admins can preview package pricing under a non-zero domain
              // count. Order mirrors the quote-page UI: infrastructure
              // first, then configuration items (tenants, domains).
              ['domains',       'Domains'],
            ].map(([key, label]) => (
              <div key={key}>
                <div style={{ fontSize:10, color:'#6b7280', marginBottom:2 }}>{label}</div>
                <input type="number" min="0" value={scenario[key]}
                  onChange={e => setScenario(s => ({ ...s, [key]: e.target.value }))}
                  style={{ width:'100%', padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace' }} />
              </div>
            ))}
          </div>
        </div>

        {/* Target GM */}
        <div style={{ marginBottom: 14, display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ fontSize:10, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Target Gross Margin
          </div>
          <input type="number" min="0" max="100" step="0.1"
            value={(targetGM * 100).toFixed(1)}
            onChange={e => setTargetGM((parseFloat(e.target.value) || 0) / 100)}
            style={{ width:80, padding:'5px 7px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace' }} />
          <span style={{ fontSize:11, color:'#6b7280' }}>%</span>
          {Number.isFinite(settingsTarget) && settingsTarget > 0 && (
            <span style={{ fontSize:9, color:'#9ca3af', fontStyle:'italic' }}>
              (settings default: {(settingsTarget * 100).toFixed(0)}%)
            </span>
          )}
        </div>

        {/* Current at scenario */}
        {targetCalc?.error ? (
          <div style={{ padding:'10px 12px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, fontSize:11, color:'#991b1b' }}>
            ⚠ {targetCalc.error}
          </div>
        ) : targetCalc ? (
          <>
            <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:6, padding:'10px 12px', marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>
                Current at this scenario
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:10, fontSize:11 }}>
                <div>
                  <div style={{ color:'#6b7280', fontSize:9, textTransform:'uppercase' }}>Revenue</div>
                  <div style={{ fontFamily:'DM Mono, monospace', fontWeight:700, color:'#0f1e3c' }}>${targetCalc.currentRevenue.toFixed(2)}/mo</div>
                </div>
                <div>
                  <div style={{ color:'#6b7280', fontSize:9, textTransform:'uppercase' }}>COGS</div>
                  <div style={{ fontFamily:'DM Mono, monospace', fontWeight:700, color:'#0f1e3c' }}>${targetCalc.currentCogs.toFixed(2)}/mo</div>
                </div>
                <div>
                  <div style={{ color:'#6b7280', fontSize:9, textTransform:'uppercase' }}>Implied GM</div>
                  <div style={{ fontFamily:'DM Mono, monospace', fontWeight:700,
                    color: targetCalc.currentGM >= targetGM ? '#166534' : targetCalc.currentGM >= targetGM - 0.05 ? '#92400e' : '#991b1b' }}>
                    {(targetCalc.currentGM * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div style={{ color:'#6b7280', fontSize:9, textTransform:'uppercase' }}>Gap</div>
                  <div style={{ fontFamily:'DM Mono, monospace', fontWeight:700,
                    color: targetCalc.alreadyAboveTarget ? '#166534' : '#991b1b' }}>
                    {targetCalc.alreadyAboveTarget
                      ? `+${((targetCalc.currentGM - targetGM) * 100).toFixed(1)} pts`
                      : `−${((targetGM - targetCalc.currentGM) * 100).toFixed(1)} pts`}
                  </div>
                </div>
              </div>
              <div style={{ fontSize:10, color:'#6b7280', marginTop:8, borderTop:'1px solid #e5e7eb', paddingTop:6 }}>
                <span>Cost breakdown — </span>
                <span style={{ fontFamily:'DM Mono, monospace' }}>
                  Tooling ${currentResult.toolingCost.toFixed(2)} · Labor ${currentResult.svcCost.toFixed(2)} · Includes ${currentResult.includedItemsCost.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Suggested rates */}
            {targetCalc.alreadyAboveTarget ? (
              <div style={{ padding:'10px 12px', background:'#dcfce7', border:'1px solid #86efac', borderRadius:6, fontSize:11, color:'#166534', marginBottom:14 }}>
                ✓ Already above target ({((targetCalc.currentGM - targetGM) * 100).toFixed(1)} points). Current rates are healthy at this scenario — no changes suggested.
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                    Suggested Rates (proportional uplift, {((targetCalc.upliftFactor - 1) * 100).toFixed(1)}%)
                  </div>
                </div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11, marginBottom:14 }}>
                  <thead>
                    <tr style={{ background:'#f9fafb' }}>
                      {['Rate','Current','Suggested','Change'].map(h => (
                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:'#6b7280', fontWeight:600, borderBottom:'1px solid #e5e7eb', fontSize:10, textTransform:'uppercase', letterSpacing:'0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['user_rate',     'User Rate',          pkg.user_rate,     targetCalc.suggestedRates.user_rate],
                      ['ws_rate',       'Workstation Rate',   pkg.ws_rate,       targetCalc.suggestedRates.ws_rate],
                      ['server_rate',   'Server Rate',        pkg.server_rate,   targetCalc.suggestedRates.server_rate],
                      ['location_rate', 'Location Rate',      pkg.location_rate, targetCalc.suggestedRates.location_rate],
                      ['tenant_rate',   'Cloud Tenant Rate',  pkg.tenant_rate,   targetCalc.suggestedRates.tenant_rate],
                      ['vendor_rate',   'Extra Vendor Rate',  pkg.vendor_rate,   targetCalc.suggestedRates.vendor_rate],
                    ].map(([key, label, current, suggested]) => {
                      const curN = parseFloat(current) || 0;
                      const pct  = curN > 0 ? ((suggested - curN) / curN) * 100 : 0;
                      return (
                        <tr key={key} style={{ borderBottom:'1px solid #f3f4f6' }}>
                          <td style={{ padding:'6px 10px', color:'#374151', fontWeight:600 }}>{label}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', color:'#6b7280' }}>${curN.toFixed(2)}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', color:'#6d28d9', fontWeight:700 }}>${suggested.toFixed(2)}</td>
                          <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', color: pct > 0 ? '#dc2626' : pct < 0 ? '#166534' : '#6b7280' }}>
                            {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Disclaimer */}
                {targetCalc.floorBound && (
                  <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'8px 10px', fontSize:10, color:'#92400e', marginBottom:10 }}>
                    ⚠ Current revenue is set by the minimum commitment floor (${currentResult.floor.toFixed(2)}). The suggested uplift may not behave as expected at this scenario — try a larger reference scenario (more users/workstations) for cleaner math.
                  </div>
                )}
                <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'8px 10px', fontSize:10, color:'#92400e', marginBottom:14 }}>
                  ⚠ Applying these rates affects all NEW quotes on <strong>{pkg.name}</strong>. Existing locked quotes are unaffected. Existing draft quotes will recompute on next reload — reps may see margin changes.
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{ padding:'20px', textAlign:'center', color:'#9ca3af', fontSize:11 }}>
            Enter a scenario above to see calculations.
          </div>
        )}

        {applyError && (
          <div style={{ padding:'7px 10px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, fontSize:11, color:'#dc2626', marginBottom:10 }}>
            ✗ {applyError}
          </div>
        )}

        {/* Actions */}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:6 }}>
          <button onClick={onClose} disabled={applying}
            style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:5, fontSize:11, color:'#374151', fontWeight:500, cursor: applying ? 'not-allowed' : 'pointer' }}>
            Cancel
          </button>
          <button onClick={applyRates}
            disabled={applying || !targetCalc?.suggestedRates || targetCalc?.error || targetCalc?.alreadyAboveTarget}
            style={{ padding:'7px 14px',
              background: (applying || !targetCalc?.suggestedRates || targetCalc?.error || targetCalc?.alreadyAboveTarget) ? '#d4d4d8' : '#7c3aed',
              color:'white', border:'none', borderRadius:5, fontSize:11, fontWeight:600,
              cursor: (applying || !targetCalc?.suggestedRates || targetCalc?.error || targetCalc?.alreadyAboveTarget) ? 'not-allowed' : 'pointer' }}>
            {applying ? 'Applying…' : 'Apply Suggested Rates'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MARKET TIERS ADMIN ───────────────────────────────────────────────────────
function MarketTiersAdmin() {
  const [tiers,   setTiers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const { profile } = useAuth();

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('market_tiers').select('*').order('sort_order');
    setTiers(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    // v3.5.81.21: labor_multiplier no longer included in the save payload —
    // the engine has used a hardcoded 1.0 multiplier since v3.5.37 (Market
    // Intelligence drives pricing adjustments now), so the field was
    // vestigial. UI removed from the modal in this deploy; DB column drop
    // ships in the v3.5.81.21 SQL migration. Until that migration runs the
    // column remains in the DB but is no longer written.
    const payload = { ...editing, updated_by: profile?.id };
    delete payload.labor_multiplier;
    const isNew = !editing.id;
    const old = tiers.find(t => t.id === editing.id);
    const { error } = isNew
      ? await supabase.from('market_tiers').insert(payload)
      : await supabase.from('market_tiers').update(payload).eq('id', editing.id);
    if (!error) {
      await logActivity({ action: isNew ? 'CREATE' : 'UPDATE', entityType: 'market_tier', entityId: editing.id, entityName: editing.name, changes: isNew ? payload : diffObjects(old, editing) });
      setEditing(null); load();
    }
    setSaving(false);
  }

  // v3.5.81.21: 'mult' computed column removed from the rows decoration —
  // was '${(t.labor_multiplier * 100).toFixed(0)}%' which surfaced the
  // legacy multiplier column in the table. Now that the field is dropped
  // from the table cols array below, the computed value isn't needed.
  const rows = tiers.map(t => ({
    ...t,
    // v3.5.37: surface the unified MI label so admins see what reps see
    'display label': staticTierToMILabel(t.tier_key),
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Market Tiers</h2>
          {/* v3.5.81.21: copy updated to drop the "Labor cost multipliers"
              framing — the multiplier field is gone, geographic tiers now
              just provide the vocabulary that maps to Market Intelligence
              tiers. The underlying engine math comes from MI lookups, not
              from per-tier multipliers in this table. */}
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Geographic market tier vocabulary. Reps see these tiers using unified Market Intelligence labels (Premium / Standard / Adjusted / Secondary); the underlying tier_key values map to MI lookups that drive engine pricing adjustments.</p>
        </div>
        {/* v3.5.81.21: + Add Tier no longer seeds labor_multiplier */}
        <button onClick={() => setEditing({ name:'', tier_key:'', description:'', examples:'', sort_order:0, active:true })}
          style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600 }}>+ Add Tier</button>
      </div>
      {/* v3.5.81.21: 'mult' column removed from cols array */}
      <AdminTable cols={['name','tier_key','display label','description']} rows={rows} onEdit={setEditing} onToggle={null} loading={loading} />
      {editing && (
        <Modal title={editing.id ? 'Edit Market Tier' : 'New Market Tier'} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <Field label="Display Name"><Input value={editing.name} onChange={v => setEditing(e => ({...e, name: v}))} /></Field>
          <Field label="Tier Key (lowercase, underscores)"><Input value={editing.tier_key} onChange={v => setEditing(e => ({...e, tier_key: v}))} placeholder="e.g. major_metro" /></Field>
          {/* v3.5.81.21: Pricing Multiplier field removed — the engine uses
              a hardcoded 1.0 since v3.5.37 (Market Intelligence drives
              pricing adjustments now). Field was vestigial. DB column
              drops via the v3.5.81.21 SQL migration. */}
          <Field label="Description"><Input value={editing.description || ''} onChange={v => setEditing(e => ({...e, description: v}))} /></Field>
          <Field label="Example Cities"><Input value={editing.examples || ''} onChange={v => setEditing(e => ({...e, examples: v}))} placeholder="Chicago · NYC · Dallas" /></Field>
        </Modal>
      )}
    </div>
  );
}

// ─── v3.5.99.3: ADDENDUMS ADMIN ───────────────────────────────────────────
// Full CRUD over the addendum_templates table. Templates are SO addendums
// (Supersedes Prior Agreements, M2M renewal, Waive ETF after M&A, future
// custom ones) that reps will select per-quote in v3.5.99.4 and the
// SO PDF renderer will inject between OOS Rate Schedule and Acceptance
// Terms in v3.5.99.5.
//
// Schema:
//   name             — unique display name
//   description      — short admin-facing description, why this template exists
//   body             — template body with {{placeholder_key}} tokens
//   placeholders     — JSONB [{key, label, help, type}] — declares the inputs
//                      the per-quote UI should collect. type: 'text' or
//                      'multiline_list' (the latter renders bullet list)
//   optional_clauses — JSONB [{key, label, body, default_enabled}] —
//                      toggleable sub-clauses appended when enabled
//   active           — soft-delete flag
//   sort_order       — lower-first
//
// UI structure:
//   - List view: rows of template name + description + sort + active toggle
//   - Edit view: full form with sub-editors for placeholders and optional
//     clauses. Live token detection in the body, warns when used vs.
//     declared drift.
//
// No enforcement of token/placeholder sync at save time — the per-quote
// renderer (v3.5.99.4) will handle unmapped tokens (leaves {{key}} as
// visible text so reps catch the error visually).
function AddendumsAdmin() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);   // template being edited (null = list view)
  const [draft, setDraft]         = useState(null);   // working copy of editing
  const [saving, setSaving]       = useState(false);
  const { profile } = useAuth();

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('addendum_templates')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('name',       { ascending: true });
    if (error) {
      console.error('Load addendum_templates error:', error);
      alert(`Load failed: ${error.message}`);
    }
    setTemplates(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function startNew() {
    const blank = {
      id: null,
      name: '',
      description: '',
      body: '',
      placeholders: [],
      optional_clauses: [],
      active: true,
      sort_order: 100,
    };
    setEditing(blank);
    setDraft(blank);
  }

  function startEdit(t) {
    setEditing(t);
    setDraft({
      ...t,
      placeholders:     Array.isArray(t.placeholders)     ? t.placeholders     : [],
      optional_clauses: Array.isArray(t.optional_clauses) ? t.optional_clauses : [],
    });
  }

  function cancelEdit() {
    setEditing(null);
    setDraft(null);
  }

  async function save() {
    if (!draft.name.trim()) { alert('Name is required.'); return; }
    if (!draft.body.trim()) { alert('Body is required.'); return; }
    // Validate placeholders array shape — every entry needs a key + label.
    for (const ph of draft.placeholders) {
      if (!ph.key || !ph.key.trim() || !ph.label || !ph.label.trim()) {
        alert('Every placeholder needs a key and a label.');
        return;
      }
      if (!/^[a-z0-9_]+$/i.test(ph.key.trim())) {
        alert(`Invalid placeholder key "${ph.key}". Use letters, digits, and underscores only.`);
        return;
      }
    }
    for (const oc of draft.optional_clauses) {
      if (!oc.key || !oc.key.trim() || !oc.label || !oc.label.trim() || !oc.body || !oc.body.trim()) {
        alert('Every optional clause needs a key, label, and body.');
        return;
      }
      if (!/^[a-z0-9_]+$/i.test(oc.key.trim())) {
        alert(`Invalid optional clause key "${oc.key}". Use letters, digits, and underscores only.`);
        return;
      }
    }
    setSaving(true);
    const payload = {
      name:             draft.name.trim(),
      description:      draft.description?.trim() || null,
      body:             draft.body,
      placeholders:     draft.placeholders,
      optional_clauses: draft.optional_clauses,
      active:           !!draft.active,
      sort_order:       parseInt(draft.sort_order) || 100,
      updated_by:       profile?.id || null,
    };
    let result;
    if (draft.id) {
      result = await supabase.from('addendum_templates').update(payload).eq('id', draft.id).select().single();
    } else {
      payload.created_by = profile?.id || null;
      result = await supabase.from('addendum_templates').insert(payload).select().single();
    }
    if (result.error) {
      console.error('Save addendum_template error:', result.error);
      alert(`Save failed: ${result.error.message}`);
      setSaving(false);
      return;
    }
    // Activity log entry — diff-style for edits, simple for new.
    const oldRow = editing?.id ? editing : null;
    const newRow = result.data;
    const changes = oldRow ? diffObjects(oldRow, newRow) : null;
    await logActivity({
      action: oldRow ? 'UPDATE' : 'CREATE',
      entityType: 'addendum_template',
      entityId: newRow.id,
      entityName: newRow.name,
      changes,
    });
    setSaving(false);
    await load();
    cancelEdit();
  }

  async function softDelete(t) {
    if (!window.confirm(`Deactivate "${t.name}"? Existing quotes with this template attached will still render; it just won't appear in the picker for new quotes.`)) return;
    const { error } = await supabase
      .from('addendum_templates')
      .update({ active: false, updated_by: profile?.id || null })
      .eq('id', t.id);
    if (error) { alert(`Deactivate failed: ${error.message}`); return; }
    await logActivity({
      action: 'UPDATE',
      entityType: 'addendum_template',
      entityId: t.id,
      entityName: t.name,
      changes: { active: { from: true, to: false } },
    });
    await load();
  }

  async function reactivate(t) {
    const { error } = await supabase
      .from('addendum_templates')
      .update({ active: true, updated_by: profile?.id || null })
      .eq('id', t.id);
    if (error) { alert(`Reactivate failed: ${error.message}`); return; }
    await logActivity({
      action: 'UPDATE',
      entityType: 'addendum_template',
      entityId: t.id,
      entityName: t.name,
      changes: { active: { from: false, to: true } },
    });
    await load();
  }

  // Detect {{tokens}} in the body and compare against declared placeholders.
  function bodyTokens(body) {
    if (!body) return [];
    const found = new Set();
    const re = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
    let m;
    while ((m = re.exec(body))) found.add(m[1]);
    return [...found];
  }

  if (loading) {
    return <div style={{ padding: 20, fontSize: 12, color: '#6b7280' }}>Loading addendum templates...</div>;
  }

  // ─── EDIT VIEW ─────────────────────────────────────────────────────────
  if (editing) {
    const tokensInBody  = bodyTokens(draft.body);
    const declaredKeys  = (draft.placeholders || []).map(p => p.key);
    const undeclared    = tokensInBody.filter(t => !declaredKeys.includes(t));
    const unusedDeclared = declaredKeys.filter(k => !tokensInBody.includes(k));

    return (
      <div>
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={cancelEdit} style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>← Back</button>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c', margin: 0 }}>
            {draft.id ? 'Edit Addendum Template' : 'New Addendum Template'}
          </h2>
        </div>

        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Name *</label>
            <input
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Supersedes Prior Agreements"
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Description</label>
            <textarea
              value={draft.description || ''}
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="Short admin-facing note — when to use this template"
              rows={2}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', border: '1px solid #d1d5db', borderRadius: 4, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Sort Order</label>
              <input
                type="number"
                value={draft.sort_order}
                onChange={e => setDraft(d => ({ ...d, sort_order: e.target.value }))}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '6px 10px', fontSize: 12, border: '1px solid #d1d5db', borderRadius: 4 }}
              />
            </div>
            <div style={{ flex: 2, display: 'flex', alignItems: 'center', paddingTop: 18 }}>
              <label style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={!!draft.active} onChange={e => setDraft(d => ({ ...d, active: e.target.checked }))} />
                Active (offered in per-quote picker)
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Body * <span style={{ fontWeight: 400, textTransform: 'none', color: '#6b7280', marginLeft: 6 }}>Use {`{{token_name}}`} for placeholders</span></label>
            <textarea
              value={draft.body}
              onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
              placeholder="Effective as of the Effective Date of this Service Order…"
              rows={12}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.5, border: '1px solid #d1d5db', borderRadius: 4, resize: 'vertical' }}
            />
            {tokensInBody.length > 0 && (
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>
                Tokens detected: {tokensInBody.map(t => <span key={t} style={{ display: 'inline-block', background: '#f3f4f6', padding: '2px 6px', borderRadius: 3, marginRight: 4, fontFamily: 'monospace' }}>{`{{${t}}}`}</span>)}
              </div>
            )}
            {(undeclared.length > 0 || unusedDeclared.length > 0) && (
              <div style={{ fontSize: 10, color: '#92400e', marginTop: 6, padding: '6px 8px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3 }}>
                {undeclared.length > 0 && <div>⚠ Tokens used in body but not declared as placeholders: {undeclared.join(', ')}</div>}
                {unusedDeclared.length > 0 && <div>⚠ Placeholders declared but not used in body: {unusedDeclared.join(', ')}</div>}
              </div>
            )}
          </div>
        </div>

        {/* Placeholders sub-editor */}
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c' }}>Placeholders</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Declared inputs the per-quote panel will collect. Body references via {`{{key}}`}.</div>
            </div>
            <button
              onClick={() => setDraft(d => ({ ...d, placeholders: [...(d.placeholders || []), { key: '', label: '', help: '', type: 'text' }] }))}
              style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}
            >+ Add Placeholder</button>
          </div>
          {(draft.placeholders || []).length === 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af', padding: 12, textAlign: 'center', fontStyle: 'italic' }}>No placeholders declared.</div>
          )}
          {(draft.placeholders || []).map((ph, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 2fr 0.8fr 32px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <input value={ph.key}   onChange={e => setDraft(d => { const next = [...d.placeholders]; next[idx] = { ...next[idx], key: e.target.value }; return { ...d, placeholders: next }; })} placeholder="key (snake_case)" style={{ padding: '5px 8px', fontSize: 11, fontFamily: 'monospace', border: '1px solid #d1d5db', borderRadius: 3 }} />
              <input value={ph.label} onChange={e => setDraft(d => { const next = [...d.placeholders]; next[idx] = { ...next[idx], label: e.target.value }; return { ...d, placeholders: next }; })} placeholder="Label" style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }} />
              <input value={ph.help || ''} onChange={e => setDraft(d => { const next = [...d.placeholders]; next[idx] = { ...next[idx], help: e.target.value }; return { ...d, placeholders: next }; })} placeholder="Help text" style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }} />
              <select value={ph.type || 'text'} onChange={e => setDraft(d => { const next = [...d.placeholders]; next[idx] = { ...next[idx], type: e.target.value }; return { ...d, placeholders: next }; })} style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }}>
                <option value="text">text</option>
                <option value="multiline_list">list</option>
              </select>
              <button onClick={() => setDraft(d => ({ ...d, placeholders: d.placeholders.filter((_, i) => i !== idx) }))} style={{ padding: '4px 6px', fontSize: 14, background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer' }} title="Remove">×</button>
            </div>
          ))}
        </div>

        {/* Optional Clauses sub-editor */}
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c' }}>Optional Clauses</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>Toggleable sub-sections appended when the rep enables them per-quote.</div>
            </div>
            <button
              onClick={() => setDraft(d => ({ ...d, optional_clauses: [...(d.optional_clauses || []), { key: '', label: '', body: '', default_enabled: false }] }))}
              style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer' }}
            >+ Add Clause</button>
          </div>
          {(draft.optional_clauses || []).length === 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af', padding: 12, textAlign: 'center', fontStyle: 'italic' }}>No optional clauses.</div>
          )}
          {(draft.optional_clauses || []).map((oc, idx) => (
            <div key={idx} style={{ marginBottom: 12, padding: 10, background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto 32px', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                <input value={oc.key}   onChange={e => setDraft(d => { const next = [...d.optional_clauses]; next[idx] = { ...next[idx], key: e.target.value }; return { ...d, optional_clauses: next }; })} placeholder="key (snake_case)" style={{ padding: '5px 8px', fontSize: 11, fontFamily: 'monospace', border: '1px solid #d1d5db', borderRadius: 3 }} />
                <input value={oc.label} onChange={e => setDraft(d => { const next = [...d.optional_clauses]; next[idx] = { ...next[idx], label: e.target.value }; return { ...d, optional_clauses: next }; })} placeholder="Display label" style={{ padding: '5px 8px', fontSize: 11, border: '1px solid #d1d5db', borderRadius: 3 }} />
                <label style={{ fontSize: 10, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={!!oc.default_enabled} onChange={e => setDraft(d => { const next = [...d.optional_clauses]; next[idx] = { ...next[idx], default_enabled: e.target.checked }; return { ...d, optional_clauses: next }; })} />
                  default on
                </label>
                <button onClick={() => setDraft(d => ({ ...d, optional_clauses: d.optional_clauses.filter((_, i) => i !== idx) }))} style={{ padding: '4px 6px', fontSize: 14, background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer' }} title="Remove">×</button>
              </div>
              <textarea
                value={oc.body}
                onChange={e => setDraft(d => { const next = [...d.optional_clauses]; next[idx] = { ...next[idx], body: e.target.value }; return { ...d, optional_clauses: next }; })}
                placeholder="Clause body — also supports {{tokens}}"
                rows={4}
                style={{ display: 'block', width: '100%', padding: '6px 8px', fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', lineHeight: 1.5, border: '1px solid #d1d5db', borderRadius: 3, resize: 'vertical' }}
              />
            </div>
          ))}
        </div>

        {/* Save/Discard */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={saving} style={{ padding: '8px 18px', fontSize: 11, fontWeight: 700, background: saving ? '#e5e7eb' : '#0f1e3c', color: saving ? '#9ca3af' : 'white', border: 'none', borderRadius: 4, cursor: saving ? 'default' : 'pointer' }}>{saving ? 'Saving…' : (draft.id ? 'Save Changes' : 'Create Template')}</button>
          <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 18px', fontSize: 11, fontWeight: 600, background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    );
  }

  // ─── LIST VIEW ─────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c', margin: 0 }}>Addendum Templates</h2>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4, lineHeight: 1.5, maxWidth: 720 }}>
            Reusable addendums for Service Orders (Supersedes, M2M renewal, ETF waiver, etc.). Templates appear in the per-quote Special Terms panel for reps to select and fill placeholders. Each addendum renders in the SO PDF between the OOS Rate Schedule and the Acceptance Terms.
          </p>
        </div>
        <button onClick={startNew} style={{ padding: '6px 14px', fontSize: 11, fontWeight: 700, background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>+ New Template</button>
      </div>

      {templates.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, color: '#6b7280', fontSize: 12 }}>
          No templates yet. The migration should have seeded three (Supersedes, M2M, Waive ETF) — if you don't see them, verify the v3.5.99.3 SQL ran successfully.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Name</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Description</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Placeholders</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Clauses</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Sort</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '.05em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t, i) => (
              <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6', opacity: t.active ? 1 : 0.55 }}>
                <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, color: '#0f1e3c' }}>{t.name}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: '#6b7280', maxWidth: 360 }}>{t.description || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: '#374151', textAlign: 'center' }}>{(t.placeholders || []).length}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: '#374151', textAlign: 'center' }}>{(t.optional_clauses || []).length}</td>
                <td style={{ padding: '10px 12px', fontSize: 11, color: '#374151', textAlign: 'center' }}>{t.sort_order}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: t.active ? '#dcfce7' : '#fef3c7', color: t.active ? '#166534' : '#92400e' }}>
                    {t.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button onClick={() => startEdit(t)} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: 'white', color: '#0f1e3c', border: '1px solid #d1d5db', borderRadius: 3, cursor: 'pointer', marginRight: 4 }}>Edit</button>
                  {t.active
                    ? <button onClick={() => softDelete(t)} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: 'white', color: '#92400e', border: '1px solid #fde68a', borderRadius: 3, cursor: 'pointer' }}>Deactivate</button>
                    : <button onClick={() => reactivate(t)} style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: 'white', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 3, cursor: 'pointer' }}>Reactivate</button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── v3.5.99.0: NOTIFICATIONS ADMIN ───────────────────────────────────────────
// Manages global default CC lists for internal notification emails (proposal
// approved, future: Service Order signed, LOA signed, quote expired). Each
// row is a pricing_settings entry keyed `notify_<event>_cc_global` storing
// a comma-separated email list. Per-quote overrides live in the quote's
// inputs.notificationCc — those win when non-empty; otherwise the global
// default fills in. Resolution happens server-side in proposal.js
// (resolveNotificationCc + parseEmailList).
//
// For v1 only the proposal-approved event is wired. To add a new event:
//   1. Insert a new pricing_settings row `notify_<event>_cc_global`
//   2. Add it to the NOTIFY_EVENTS list below
//   3. Wire the resolveNotificationCc call wherever the email send fires
function NotificationsAdmin() {
  const [rows, setRows]       = useState({});      // key → value
  const [drafts, setDrafts]   = useState({});      // key → in-progress edits
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(null);    // key currently saving
  const { profile } = useAuth();

  // Order + labels for each notify_* event. Add to this list when
  // wiring new notification events.
  const NOTIFY_EVENTS = [
    {
      key: 'notify_approval_cc_global',
      label: 'Proposal Approved',
      description: 'CC\'d on the email sent when a client approves a proposal via the public token URL. The rep who owns the deal is always on the TO line; this is the internal visibility list. Per-quote override available on each quote page under "Internal Notifications".',
    },
  ];

  async function load() {
    setLoading(true);
    const keys = NOTIFY_EVENTS.map(e => e.key);
    const { data } = await supabase.from('pricing_settings').select('key, value').in('key', keys);
    const next = {};
    (data || []).forEach(r => { next[r.key] = r.value || ''; });
    setRows(next);
    setDrafts(next);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // Same email validation regex used in proposal.js#handleApprove +
  // parseEmailList. Keep them in sync if the rule ever changes.
  function validateList(s) {
    const errors = [];
    const seen = new Set();
    const clean = [];
    for (const raw of (s || '').split(',')) {
      const email = raw.trim().toLowerCase();
      if (!email) continue;
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        errors.push(email);
        continue;
      }
      if (seen.has(email)) continue;
      seen.add(email);
      clean.push(email);
    }
    return { clean, errors };
  }

  async function saveOne(key) {
    setSaving(key);
    const draft = drafts[key] || '';
    const { clean, errors } = validateList(draft);
    if (errors.length > 0) {
      alert(`Invalid email format: ${errors.join(', ')}\n\nPlease fix and try again.`);
      setSaving(null);
      return;
    }
    const normalized = clean.join(', ');
    const { error } = await supabase
      .from('pricing_settings')
      .update({ value: normalized, updated_by: profile?.id })
      .eq('key', key);
    if (error) {
      console.error('Save notification setting error:', error);
      alert(`Save failed: ${error.message}`);
    } else {
      const old = rows[key] || '';
      await logActivity({
        action: 'UPDATE',
        entityType: 'notification_setting',
        entityId: key,
        entityName: key,
        changes: { value: { from: old, to: normalized } },
      });
      setRows(r => ({ ...r, [key]: normalized }));
      setDrafts(d => ({ ...d, [key]: normalized }));
    }
    setSaving(null);
  }

  if (loading) {
    return <div style={{ padding: 20, fontSize: 12, color: '#6b7280' }}>Loading notification settings...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Notifications</h2>
        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>
          Default CC lists for internal notification emails. The rep who owns the deal is always on the TO line; entries here are CC'd on every notification of that event type. Per-quote overrides on each quote page take precedence when set.
        </p>
      </div>

      {NOTIFY_EVENTS.map(evt => {
        const draft = drafts[evt.key] ?? '';
        const saved = rows[evt.key] ?? '';
        const dirty = draft !== saved;
        const { clean: previewList } = validateList(draft);
        const isSaving = saving === evt.key;
        return (
          <div key={evt.key} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>{evt.label}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, lineHeight: 1.5 }}>{evt.description}</div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Default CCs (comma-separated)</label>
              <textarea
                value={draft}
                onChange={e => setDrafts(d => ({ ...d, [evt.key]: e.target.value }))}
                placeholder="alice@ferrumit.com, bob@ferrumit.com"
                rows={2}
                style={{
                  display: 'block', width: '100%', marginTop: 4, padding: '8px 10px',
                  fontSize: 12, fontFamily: 'inherit', color: '#0f1e3c',
                  border: '1px solid #d1d5db', borderRadius: 4, resize: 'vertical',
                }}
              />
              {previewList.length > 0 && (
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>
                  Will CC: {previewList.map(e => <span key={e} style={{ display: 'inline-block', background: '#f3f4f6', padding: '2px 6px', borderRadius: 3, marginRight: 4, fontFamily: 'monospace' }}>{e}</span>)}
                </div>
              )}
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                  onClick={() => saveOne(evt.key)}
                  disabled={!dirty || isSaving}
                  style={{
                    padding: '6px 14px', fontSize: 11, fontWeight: 600,
                    background: dirty && !isSaving ? '#0f1e3c' : '#e5e7eb',
                    color: dirty && !isSaving ? 'white' : '#9ca3af',
                    border: 'none', borderRadius: 4, cursor: dirty && !isSaving ? 'pointer' : 'default',
                  }}
                >{isSaving ? 'Saving…' : 'Save'}</button>
                {dirty && (
                  <button
                    onClick={() => setDrafts(d => ({ ...d, [evt.key]: saved }))}
                    disabled={isSaving}
                    style={{
                      padding: '6px 14px', fontSize: 11, fontWeight: 600,
                      background: 'white', color: '#6b7280',
                      border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer',
                    }}
                  >Discard</button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── PRICING SETTINGS ADMIN ───────────────────────────────────────────────────
function SettingsAdmin() {
  const [settings, setSettings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [search,   setSearch]   = useState('');
  const { profile } = useAuth();

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('pricing_settings').select('*');
    setSettings(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    const old = settings.find(s => s.id === editing.id);
    const { error } = await supabase.from('pricing_settings').update({ value: editing.value, updated_by: profile?.id }).eq('id', editing.id);
    if (!error) {
      await logActivity({ action: 'UPDATE', entityType: 'setting', entityId: editing.id, entityName: editing.key, changes: { old: old?.value, new: editing.value } });
      setEditing(null); load();
    }
    setSaving(false);
  }

  const filteredSettings = settings.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.key || '').toLowerCase().includes(q)
        || (s.label || '').toLowerCase().includes(q)
        || (s.description || '').toLowerCase().includes(q)
        || (s.value || '').toString().toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Pricing Settings</h2>
        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Global rates: onboarding costs, stack costs, burdened rate, contract discounts</p>
      </div>

      {/* Search — consistent with IT & Security Products and Voice Hardware tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:10, alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by key, label, value, or description..."
          style={{ padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', width:320 }}/>
        {search && (
          <>
            <button onClick={()=>setSearch('')}
              style={{ padding:'4px 10px', borderRadius:4, border:'1px solid #e5e7eb', background:'white', color:'#374151', fontSize:10, fontWeight:500, cursor:'pointer' }}>
              Clear
            </button>
            <span style={{ fontSize:10, color:'#6b7280', marginLeft:'auto' }}>
              {filteredSettings.length} of {settings.length} shown
            </span>
          </>
        )}
      </div>

      {loading ? <div style={{ padding: 20, color: '#6b7280', fontSize: 12 }}>Loading...</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout:'fixed' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Setting','Value','Description'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
              <th style={{ padding: '8px 10px', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSettings.length === 0 && (
              <tr><td colSpan={4} style={{ padding:'20px 10px', textAlign:'center', color:'#9ca3af', fontSize:11 }}>
                {search ? `No settings match "${search}"` : 'No settings found'}
              </td></tr>
            )}
            {filteredSettings.map((s, i) => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '8px 10px', fontWeight: 600, color: '#374151' }}>{s.label || s.key}</td>
                <td style={{ padding: '8px 10px', maxWidth: 280, overflow: 'hidden' }}>
                  {s.key === 'company_logo_url' ? (
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      {s.value?.startsWith('data:image') ? (
                        <img src={s.value} alt="logo" style={{ height:24, maxWidth:80, objectFit:'contain', background:'#0f1e3c', borderRadius:3, padding:'2px 4px' }} />
                      ) : (
                        <span style={{ fontFamily:'DM Mono, monospace', color:'#1e40af', fontWeight:600, fontSize:10, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:200, display:'block' }}>
                          {s.value?.length > 40 ? s.value.slice(0,40) + '…' : s.value}
                        </span>
                      )}
                      <span style={{ fontSize:9, color:'#9ca3af' }}>{s.value?.startsWith('data:image') ? '(uploaded image)' : ''}</span>
                    </div>
                  ) : (
                    <span style={{ fontFamily:'DM Mono, monospace', color:'#1e40af', fontWeight:600, fontSize:11,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block', maxWidth:260 }}>
                      {s.value?.length > 60 ? s.value.slice(0,60) + '…' : s.value}
                    </span>
                  )}
                </td>
                <td style={{ padding: '8px 10px', color: '#6b7280', fontSize: 11 }}>{s.description}</td>
                <td style={{ padding: '8px 10px' }}><button onClick={() => setEditing({...s})} style={btnStyle('#eff6ff','#1e40af')}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <Modal title={`Edit: ${editing.label || editing.key}`} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>{editing.description}</p>
          <Field label="Value"><Input value={editing.value} onChange={v => setEditing(e => ({...e, value: v}))} /></Field>
        </Modal>
      )}
    </div>
  );
}

// ─── OOS RATES ADMIN ─────────────────────────────────────────────────────────
// v3.5.83.5: dedicated admin tab for the Out-of-Scope rate fee structure.
// These rows have always been editable through the generic Pricing Settings
// tab (SettingsAdmin loads ALL pricing_settings rows), but they're easy to
// miss among 50+ other settings, and the relationships between them aren't
// obvious from a flat list (e.g. why are there THREE after-hours dispatch
// fees? what's the difference between same_day and next_day?).
//
// This tab surfaces only the 11 oos_* rows, groups them by category
// (after-hours / dispatch-fees / special-charges / multipliers), provides
// inline help text explaining each rate, and offers a "Reset to Defaults"
// button that re-seeds all 11 values from the OOS_DEFAULTS constant.
//
// The defaults are baked into this component (not pulled from a shared lib)
// because they're not derived values — they're flat operational fees set by
// Ferrum business policy. If business policy changes, update the constant
// here AND update any in-flight rows. The defaults are also documented in
// the changelog entry for this version so future audits can find them.

const OOS_DEFAULTS = {
  // After-hours multipliers (applied to onsite_additional rate)
  oos_afterhours_mult_standard:    '1.5',   // Weekday evening + weekend day
  oos_afterhours_mult_graveyard:   '2.0',   // Graveyard shift
  // After-hours dispatch fees (flat per-incident charges)
  oos_afterhours_weekday_disp:     '300',   // Weekday evening dispatch
  oos_afterhours_weekend_disp:     '285',   // Weekend day dispatch
  oos_afterhours_satnight_disp:    '285',   // Saturday night dispatch
  oos_afterhours_graveyard_disp:   '380',   // Graveyard shift dispatch
  // Same-day / next-day expedite fees
  oos_same_day_fee:                '200',
  oos_next_day_fee:                '100',
  // Penalty fees
  oos_cancellation_fee:            '125',
  oos_abort_fee:                   '195',
  // Markup on exceptional one-off charges (e.g. cost-plus pass-throughs)
  oos_exceptional_markup:          '0.20',
};

// Friendly labels + help text per key. Surfaced as the row label and an
// inline description so admins don't have to remember what each cryptic
// key controls.
const OOS_FIELD_META = {
  oos_afterhours_mult_standard: {
    label: 'After-Hours Standard Multiplier',
    group: 'Multipliers',
    help:  'Applied to the onsite hourly rate for weekday evenings and weekend days. Default 1.5x.',
    unit:  'x',
  },
  oos_afterhours_mult_graveyard: {
    label: 'After-Hours Graveyard Multiplier',
    group: 'Multipliers',
    help:  'Applied to the onsite hourly rate for graveyard shift (overnight). Default 2.0x.',
    unit:  'x',
  },
  oos_afterhours_weekday_disp: {
    label: 'Weekday Evening Dispatch Fee',
    group: 'After-Hours Dispatch Fees',
    help:  'Flat fee charged per dispatch for weekday evening calls (in addition to the hourly).',
    unit:  '$',
  },
  oos_afterhours_weekend_disp: {
    label: 'Weekend Day Dispatch Fee',
    group: 'After-Hours Dispatch Fees',
    help:  'Flat fee charged per dispatch for weekend day calls (Saturday/Sunday).',
    unit:  '$',
  },
  oos_afterhours_satnight_disp: {
    label: 'Saturday Night Dispatch Fee',
    group: 'After-Hours Dispatch Fees',
    help:  'Flat fee charged per dispatch for Saturday night calls.',
    unit:  '$',
  },
  oos_afterhours_graveyard_disp: {
    label: 'Graveyard Dispatch Fee',
    group: 'After-Hours Dispatch Fees',
    help:  'Flat fee charged per dispatch for graveyard shift (overnight) calls.',
    unit:  '$',
  },
  oos_same_day_fee: {
    label: 'Same-Day Expedite Fee',
    group: 'Expedite Fees',
    help:  'Charged for service requests that need same-day attention (in addition to the hourly).',
    unit:  '$',
  },
  oos_next_day_fee: {
    label: 'Next-Day Expedite Fee',
    group: 'Expedite Fees',
    help:  'Charged for service requests that need next-business-day attention.',
    unit:  '$',
  },
  oos_cancellation_fee: {
    label: 'Cancellation Fee',
    group: 'Penalty Fees',
    help:  'Charged when a scheduled service is cancelled with insufficient notice.',
    unit:  '$',
  },
  oos_abort_fee: {
    label: 'Abort Fee',
    group: 'Penalty Fees',
    help:  'Charged when a technician arrives onsite but is unable to perform the work due to client-side issues.',
    unit:  '$',
  },
  oos_exceptional_markup: {
    label: 'Exceptional Charges Markup',
    group: 'Special Charges',
    help:  'Markup applied to cost-plus pass-throughs (specialty parts, third-party services, etc.). Stored as a decimal (0.20 = 20%).',
    unit:  '× cost',
  },
};

const OOS_GROUP_ORDER = [
  'Multipliers',
  'After-Hours Dispatch Fees',
  'Expedite Fees',
  'Penalty Fees',
  'Special Charges',
];

function OosRatesAdmin() {
  const [rows,    setRows]    = useState([]);    // pricing_settings rows for oos_*
  const [edits,   setEdits]   = useState({});    // unsaved edits keyed by row.id
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState({});    // per-row save in flight
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { profile } = useAuth();

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('pricing_settings')
      .select('*')
      .like('key', 'oos_%');
    if (error) {
      console.error('OOS rates load error:', error);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setEdits({});
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // ── Save a single row ──────────────────────────────────────────────────
  async function saveRow(row) {
    const newValue = edits[row.id];
    if (newValue == null || newValue === row.value) return;

    setSaving(s => ({ ...s, [row.id]: true }));
    const { error } = await supabase
      .from('pricing_settings')
      .update({ value: newValue, updated_by: profile?.id })
      .eq('id', row.id);

    if (error) {
      console.error('OOS rate save error:', error);
      alert(`Save failed: ${error.message}`);
    } else {
      // Audit log: rate changes affect every future quote, so we want a
      // clear trail. Mirrors the pattern used by SettingsAdmin (line 1969)
      // and other rate-mutation sites across AdminPage.
      await logActivity({
        action: 'UPDATE',
        entityType: 'oos_rate',
        entityId: row.id,
        entityName: OOS_FIELD_META[row.key]?.label || row.key,
        changes: { old: row.value, new: newValue },
        metadata: { key: row.key },
      });
      // Clear the edit and reload to pick up updated_at + verify persistence
      setEdits(e => { const n = { ...e }; delete n[row.id]; return n; });
      await load();
    }
    setSaving(s => { const n = { ...s }; delete n[row.id]; return n; });
  }

  // ── Reset all OOS rates to defaults ────────────────────────────────────
  // Iterates the OOS_DEFAULTS keys, finds the matching row in `rows`,
  // updates its value if different from the default. Logs each change
  // separately so the audit trail shows what was actually different.
  async function resetAll() {
    setResetting(true);
    const changes = [];

    for (const [key, defaultValue] of Object.entries(OOS_DEFAULTS)) {
      const row = rows.find(r => r.key === key);
      if (!row) {
        // Missing row — skip. Could insert here but that's beyond reset scope.
        console.warn(`OOS reset: row missing for key ${key}`);
        continue;
      }
      if (row.value === defaultValue) continue;

      const { error } = await supabase
        .from('pricing_settings')
        .update({ value: defaultValue, updated_by: profile?.id })
        .eq('id', row.id);
      if (error) {
        console.error(`Reset failed for ${key}:`, error);
        continue;
      }
      changes.push({ key, oldValue: row.value, newValue: defaultValue, rowId: row.id });
    }

    // Single audit entry summarizing the bulk reset, with per-row detail
    // in metadata. Keeps the activity log clean (one entry per reset) while
    // still preserving forensic detail.
    if (changes.length > 0) {
      await logActivity({
        action: 'BULK_RESET',
        entityType: 'oos_rate',
        entityId: 'oos_rates_bulk',
        entityName: 'OOS Rates Bulk Reset',
        metadata: {
          reset_count: changes.length,
          total_rows: rows.length,
          changed: changes.map(c => ({ key: c.key, from: c.oldValue, to: c.newValue })),
        },
      });
    }

    setShowResetConfirm(false);
    await load();
    setResetting(false);

    if (changes.length === 0) {
      alert('All OOS rates already match defaults — nothing to reset.');
    } else {
      alert(`Reset ${changes.length} of ${rows.length} OOS rate${changes.length === 1 ? '' : 's'} to default values.`);
    }
  }

  // ── Group rows by category for display ─────────────────────────────────
  const grouped = OOS_GROUP_ORDER.map(groupName => ({
    name: groupName,
    rows: rows
      .filter(r => OOS_FIELD_META[r.key]?.group === groupName)
      .sort((a, b) => (OOS_FIELD_META[a.key]?.label || a.key).localeCompare(OOS_FIELD_META[b.key]?.label || b.key)),
  })).filter(g => g.rows.length > 0);

  // Catch any rows whose keys are oos_* but aren't in OOS_FIELD_META
  // (e.g. a future oos_* row added in DB but not yet documented here).
  const ungrouped = rows.filter(r => !OOS_FIELD_META[r.key]);

  return (
    <div>
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Out-of-Scope Rates</h2>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2, maxWidth: 720 }}>
            Fee structure surrounding the labor rates: after-hours multipliers, dispatch fees, expedite charges, and penalty fees. These values flow into the Out-of-Scope Rate Schedule on every Service Order and into MarketRateCard suggestions. Changes affect future quotes only — already-locked rate sheets are not retroactively updated.
          </p>
        </div>
        <button
          onClick={() => setShowResetConfirm(true)}
          disabled={loading || resetting}
          style={{
            padding: '6px 12px', borderRadius: 4, border: '1px solid #fbbf24',
            background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600,
            cursor: (loading || resetting) ? 'not-allowed' : 'pointer',
            opacity: (loading || resetting) ? 0.5 : 1,
            whiteSpace: 'nowrap', marginLeft: 16,
          }}>
          ↺ Reset to Defaults
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 20, color: '#6b7280', fontSize: 12 }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 20, color: '#9ca3af', fontSize: 12, fontStyle: 'italic' }}>
          No OOS rate rows found in pricing_settings. Run a database seed to populate the standard 11 keys.
        </div>
      ) : (
        <>
          {grouped.map(group => (
            <div key={group.name} style={{ marginBottom: 22 }}>
              <h3 style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid #e5e7eb' }}>
                {group.name}
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '14%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Setting','Value','Description','Actions'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: '#6b7280', fontWeight: 600, borderBottom: '1px solid #e5e7eb', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, i) => {
                    const meta = OOS_FIELD_META[row.key] || {};
                    const isEdited = edits[row.id] != null && edits[row.id] !== row.value;
                    const isSaving = !!saving[row.id];
                    const defaultValue = OOS_DEFAULTS[row.key];
                    const isModifiedFromDefault = row.value !== defaultValue;
                    return (
                      <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '7px 10px', color: '#374151' }}>
                          <div style={{ fontWeight: 600 }}>{meta.label || row.key}</div>
                          <div style={{ fontFamily: 'DM Mono, monospace', color: '#9ca3af', fontSize: 9, marginTop: 1 }}>{row.key}</div>
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="text"
                              value={edits[row.id] != null ? edits[row.id] : row.value}
                              onChange={(e) => setEdits(prev => ({ ...prev, [row.id]: e.target.value }))}
                              disabled={isSaving}
                              style={{
                                padding: '4px 6px', border: `1px solid ${isEdited ? '#fbbf24' : '#d1d5db'}`,
                                borderRadius: 3, fontSize: 11, width: 70, fontFamily: 'DM Mono, monospace',
                                fontWeight: 600, color: '#1e40af',
                                background: isEdited ? '#fffbeb' : 'white',
                              }}
                            />
                            <span style={{ fontSize: 9, color: '#9ca3af' }}>{meta.unit}</span>
                          </div>
                          {isModifiedFromDefault && !isEdited && (
                            <div style={{ fontSize: 9, color: '#92400e', marginTop: 2 }}>
                              modified · default: {defaultValue}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '7px 10px', color: '#6b7280', fontSize: 11, lineHeight: 1.4 }}>
                          {meta.help || ''}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          {isEdited ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                onClick={() => saveRow(row)}
                                disabled={isSaving}
                                style={{
                                  padding: '3px 8px', borderRadius: 3, border: '1px solid #22c55e',
                                  background: '#dcfce7', color: '#166534', fontSize: 10, fontWeight: 600,
                                  cursor: isSaving ? 'wait' : 'pointer',
                                }}>
                                {isSaving ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={() => setEdits(e => { const n = { ...e }; delete n[row.id]; return n; })}
                                disabled={isSaving}
                                style={{
                                  padding: '3px 8px', borderRadius: 3, border: '1px solid #e5e7eb',
                                  background: 'white', color: '#6b7280', fontSize: 10, cursor: 'pointer',
                                }}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <span style={{ fontSize: 10, color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          {ungrouped.length > 0 && (
            <div style={{ marginTop: 18, padding: '10px 12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>⚠ Unrecognized OOS rows</div>
              <div style={{ fontSize: 10, color: '#78350f', marginBottom: 6 }}>
                These rows have oos_* keys but aren't documented in this admin tab. Add them to OOS_FIELD_META in AdminPage.js to display them with proper labels and help text.
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: '#78350f', fontFamily: 'DM Mono, monospace' }}>
                {ungrouped.map(r => <li key={r.id}>{r.key} = {r.value}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Reset confirmation modal */}
      {showResetConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
          <div style={{ background:'white', borderRadius:8, padding:24, width:460, boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:'#92400e', margin:'0 0 10px' }}>↺ Reset OOS Rates to Defaults?</h3>
            <p style={{ fontSize:12, color:'#374151', lineHeight:1.6, margin:'0 0 14px' }}>
              This will reset all OOS rate values to their factory defaults. Any current customizations will be overwritten. The change is logged to the activity log so it can be audited later.
            </p>
            <p style={{ fontSize:11, color:'#6b7280', margin:'0 0 18px', lineHeight:1.5 }}>
              <strong>Note:</strong> already-locked rate sheets on existing quotes are not affected — only new MarketRateCard analyses and new Service Order generations will see the reset values.
            </p>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setShowResetConfirm(false)} disabled={resetting}
                style={{ padding:'7px 14px', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:4, fontSize:12, cursor: resetting ? 'wait' : 'pointer' }}>
                Cancel
              </button>
              <button onClick={resetAll} disabled={resetting}
                style={{ padding:'7px 18px', background:'#92400e', color:'white', border:'none', borderRadius:4, fontSize:12, fontWeight:700, cursor: resetting ? 'wait' : 'pointer' }}>
                {resetting ? 'Resetting...' : 'Reset to Defaults'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── USERS ADMIN ─────────────────────────────────────────────────────────────
function UsersAdmin() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const { profile: myProfile } = useAuth();

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').order('created_at');
    setUsers(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    const old = users.find(u => u.id === editing.id);
    // v3.5.67: persist allowed_quote_types as a Postgres text[]. Filter to
    // valid keys defensively so a stale/extraneous value can't sneak through;
    // null -> all five (server-side default). Admins always have implicit
    // access (the userCanCreateQuoteType helper short-circuits on role), so
    // admins' allowed_quote_types value is informational only.
    const VALID_TYPES = ['managed_it','voice','bundle','multisite','flexit'];
    const allowedTypes = Array.isArray(editing.allowed_quote_types)
      ? editing.allowed_quote_types.filter(t => VALID_TYPES.includes(t))
      : VALID_TYPES;
    const { error } = await supabase.from('profiles').update({
      full_name: editing.full_name,
      role: editing.role,
      commission_rate: editing.commission_rate != null && editing.commission_rate !== '' ? parseFloat(editing.commission_rate) : null,
      allowed_quote_types: allowedTypes,
      // v3.5.81.15: per-rep contact info that flows into the proposal cover.
      // Reps can also self-edit these two fields via the Layout chrome's
      // "My Profile" modal (top-right user dropdown). Empty string is
      // normalized to null so the cover's fallback chain works correctly
      // (rep profile field → settings default → hardcoded).
      phone_number: editing.phone_number?.trim() || null,
      job_title:    editing.job_title?.trim()    || null,
    }).eq('id', editing.id);
    if (!error) {
      await logActivity({ action: 'UPDATE', entityType: 'user', entityId: editing.id, entityName: editing.email, changes: diffObjects(old, editing) });
      setEditing(null); load();
    }
    setSaving(false);
  }

  const rows = users.map(u => ({ ...u, last_login: u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never', commission: u.commission_rate != null ? `${(u.commission_rate * 100).toFixed(1)}%` : 'Global default' }));

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Users</h2>
        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Manage roles. New users sign up via Supabase Auth invite.</p>
      </div>
      <AdminTable cols={['email','full_name','role','commission','last_login']} rows={rows} onEdit={setEditing} loading={loading} />
      {editing && (
        <Modal title={`Edit User: ${editing.email}`} onClose={() => setEditing(null)} onSave={save} saving={saving}>
          <Field label="Full Name"><Input value={editing.full_name || ''} onChange={v => setEditing(e => ({...e, full_name: v}))} /></Field>
          {/* v3.5.81.15: per-rep contact fields — flow into the proposal
              cover (rep's job title appears under their name; phone_number
              appears in the contact column on the cover). Reps can also
              self-edit these two fields from the Layout chrome's "My
              Profile" modal. Admins can set initial values here for any
              user. */}
          <Field label="Job Title">
            <Input value={editing.job_title || ''} onChange={v => setEditing(e => ({...e, job_title: v}))}
              placeholder="e.g. Technology Strategist · Account Executive · vCIO" />
          </Field>
          <Field label="Phone Number">
            <Input value={editing.phone_number || ''} onChange={v => setEditing(e => ({...e, phone_number: v}))}
              placeholder="e.g. (555) 123-4567" />
          </Field>
          <Field label="Role">
            <Select value={editing.role} onChange={v => setEditing(e => ({...e, role: v}))} opts={[['user','User'],['admin','Admin']]} />
          </Field>
          <Field label="Commission Rate (leave blank to use global default)">
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Input type="number" value={editing.commission_rate != null ? (editing.commission_rate * 100).toFixed(1) : ''} step="0.1" min="0" max="100"
                placeholder="e.g. 10 for 10% — blank = global default"
                onChange={v => setEditing(e => ({...e, commission_rate: v === '' ? null : parseFloat(v) / 100}))} />
              <span style={{ fontSize:11, color:'#6b7280' }}>%</span>
            </div>
            <div style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>Global default: from Admin → Pricing Settings → commission_rate</div>
          </Field>

          {/* v3.5.67: Allowed Quote Types — multi-select, drives which quote
              types this user can create from the Saved Quotes page. Admins
              implicitly have all permissions (the gate helper short-circuits
              on role), so this field is informational for admin accounts but
              still respected on save in case a user is later demoted. Empty
              array = no quote-creation access (read-only of existing quotes
              they have access to via RLS). */}
          <Field label="Allowed Quote Types">
            <div style={{ display:'flex', flexDirection:'column', gap:6, padding:'8px 10px', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:5 }}>
              {[
                { key:'managed_it', label:'Managed IT',          desc:'Standalone IT services per package' },
                { key:'voice',      label:'Hosted Voice',        desc:'Hosted seats, hybrid, or SIP trunking' },
                { key:'bundle',     label:'Bundle (IT + Voice)', desc:'Combined IT + Voice with bundle discount' },
                { key:'multisite',  label:'Multi-Site IT',       desc:'IT services across multiple locations' },
                { key:'flexit',     label:'FlexIT On-Demand',    desc:'On-demand T&M block hours or pay-as-you-go' },
              ].map(({ key, label, desc }) => {
                const allowed = Array.isArray(editing.allowed_quote_types)
                  ? editing.allowed_quote_types
                  : ['managed_it','voice','bundle','multisite','flexit']; // legacy/unmigrated → assume all
                const checked = allowed.includes(key);
                return (
                  <label key={key} style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={e => {
                      const cur = Array.isArray(editing.allowed_quote_types) ? editing.allowed_quote_types : ['managed_it','voice','bundle','multisite','flexit'];
                      const next = e.target.checked ? [...new Set([...cur, key])] : cur.filter(k => k !== key);
                      setEditing(ed => ({ ...ed, allowed_quote_types: next }));
                    }} style={{ marginTop:2 }} />
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>{label}</div>
                      <div style={{ fontSize:9, color:'#6b7280' }}>{desc}</div>
                    </div>
                  </label>
                );
              })}
              {Array.isArray(editing.allowed_quote_types) && editing.allowed_quote_types.length === 0 && editing.role !== 'admin' && (
                <div style={{ fontSize:10, color:'#92400e', fontWeight:600, marginTop:4, padding:'5px 8px', background:'#fef3c7', borderRadius:4 }}>
                  ⚠ Zero quote types selected — this user will be unable to create any new quotes (existing quotes remain view-only).
                </div>
              )}
              {editing.role === 'admin' && (
                <div style={{ fontSize:9, color:'#6b7280', fontStyle:'italic', marginTop:4 }}>
                  Admins implicitly have all permissions regardless of these checkboxes; values are saved in case the user is later demoted.
                </div>
              )}
            </div>
          </Field>

          {editing.id === myProfile?.id && (
            <div style={{ padding: '7px 10px', background: '#fef3c7', borderRadius: 5, fontSize: 11, color: '#92400e', marginTop: 4 }}>You are editing your own account.</div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ─── DIFF HELPER ──────────────────────────────────────────────────────────────
function diffObjects(oldObj, newObj) {
  if (!oldObj) return newObj;
  const changes = {};
  for (const key of new Set([...Object.keys(oldObj), ...Object.keys(newObj)])) {
    if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key]))
      changes[key] = { from: oldObj[key], to: newObj[key] };
  }
  return changes;
}

// ─── DOCUMENTS ADMIN ─────────────────────────────────────────────────────────
// Manages the editable HTML for the Acceptance Terms / legal page that gets
// prepended to every signature document, plus the countersign threshold and
// default company signer info.
function DocumentsAdmin() {
  const [legalHtml,      setLegalHtml]      = useState('');
  const [threshold,      setThreshold]      = useState('5000');
  const [signerName,     setSignerName]     = useState('');
  const [signerTitle,    setSignerTitle]    = useState('');
  const [signerEmail,    setSignerEmail]    = useState('');
  const [showPreview,    setShowPreview]    = useState(false);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [saved,          setSaved]          = useState(false);
  const [saveErr,        setSaveErr]        = useState('');
  // v3.5.81: client-facing proposal template state. Mirrors the pattern
  // for legal_acceptance_terms_html — admin-editable HTML rendered into
  // the public proposal page (proposal.js Netlify function). The validity
  // days default seeds new proposals' expires_at; reps can already
  // override per-proposal via state on QuotePage.
  const [aboutHtml,        setAboutHtml]        = useState('');
  const [introHtml,        setIntroHtml]        = useState('');
  const [paymentHtml,      setPaymentHtml]      = useState('');
  const [validityDays,     setValidityDays]     = useState('30');
  // v3.5.81.1: service-order auto-fire on proposal approval. Two flags:
  //   autofireEnabled — global on/off (default false until Shaun verifies the round-trip)
  //   autofireTestMode — when on, sends as SignWell test docs (free, watermarked).
  // Stored as 'true'/'false' strings in pricing_settings since the table
  // is text-typed; coerced to boolean here. Keep as strings on save to
  // match the format read by proposal.js's auto-fire helper.
  const [autofireEnabled,  setAutofireEnabled]  = useState(false);
  const [autofireTestMode, setAutofireTestMode] = useState(true);
  const { profile }                         = useAuth();

  useEffect(() => {
    Promise.all([
      supabase.from('pricing_settings').select('value').eq('key', 'legal_acceptance_terms_html').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'legal_countersign_threshold').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'legal_default_company_signer_name').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'legal_default_company_signer_title').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'legal_default_company_signer_email').single(),
      // v3.5.81: proposal template loads
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_about_us_html').maybeSingle(),
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_intro_html').maybeSingle(),
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_payment_terms_html').maybeSingle(),
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_validity_days_default').maybeSingle(),
      // v3.5.81.1: auto-fire flags
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_autofire_enabled').maybeSingle(),
      supabase.from('pricing_settings').select('value').eq('key', 'proposal_autofire_test_mode').maybeSingle(),
    ]).then(([html, thr, n, t, e, about, intro, pay, valid, afEn, afTest]) => {
      if (html.data?.value !== undefined) setLegalHtml(html.data.value || '');
      if (thr.data?.value)  setThreshold(thr.data.value);
      if (n.data?.value)    setSignerName(n.data.value);
      if (t.data?.value)    setSignerTitle(t.data.value);
      if (e.data?.value)    setSignerEmail(e.data.value);
      // v3.5.81: proposal templates — values may be missing (settings rows
      // not yet seeded), in which case state stays at empty string.
      if (about?.data?.value !== undefined) setAboutHtml(about.data.value || '');
      if (intro?.data?.value !== undefined) setIntroHtml(intro.data.value || '');
      if (pay?.data?.value !== undefined)   setPaymentHtml(pay.data.value || '');
      if (valid?.data?.value)               setValidityDays(valid.data.value);
      // v3.5.81.1: auto-fire flags. Defaults match proposal.js (enabled=false,
      // testMode=true) so the UI reflects the live behavior even when rows
      // haven't been seeded.
      if (afEn?.data?.value !== undefined)   setAutofireEnabled(String(afEn.data.value).toLowerCase() === 'true');
      if (afTest?.data?.value !== undefined) setAutofireTestMode(String(afTest.data.value).toLowerCase() === 'true');
      setLoading(false);
    });
  }, []);

  async function save() {
    setSaving(true); setSaveErr(''); setSaved(false);
    try {
      await Promise.all([
        supabase.from('pricing_settings').upsert({ key: 'legal_acceptance_terms_html',          value: legalHtml,    description: 'Editable HTML for the Acceptance Terms / Exhibit A page that prepends every signature document.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'legal_countersign_threshold',          value: threshold,    description: 'Dollar threshold at or above which the company countersignature checkbox defaults ON.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'legal_default_company_signer_name',    value: signerName,   description: 'Default name for the company countersignature block.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'legal_default_company_signer_title',   value: signerTitle,  description: 'Default title for the company countersignature block.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'legal_default_company_signer_email',   value: signerEmail,  description: 'Default email for the company countersignature recipient.' }, { onConflict: 'key' }),
        // v3.5.81: proposal template upserts
        supabase.from('pricing_settings').upsert({ key: 'proposal_about_us_html',       value: aboutHtml,    description: 'HTML rendered in the About Us section of client-facing proposal documents.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'proposal_intro_html',          value: introHtml,    description: 'HTML rendered as the introduction on the proposal cover page.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'proposal_payment_terms_html',  value: paymentHtml,  description: 'HTML rendered as the Payment & Billing Terms section on client-facing proposals.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'proposal_validity_days_default', value: String(parseInt(validityDays) || 30), description: 'Default validity period in days for new proposals.' }, { onConflict: 'key' }),
        // v3.5.81.1: auto-fire flag upserts. Stored as 'true'/'false' strings
        // so the proposal.js Netlify function can do String(...).toLowerCase()
        // comparisons without type coercion surprises. Default behavior when
        // these rows are missing matches what the function defaults to
        // (enabled=false, testMode=true).
        supabase.from('pricing_settings').upsert({ key: 'proposal_autofire_enabled',    value: autofireEnabled  ? 'true' : 'false', description: 'When true, approving a proposal auto-creates the SignWell service order. When false, rep manually triggers via existing flow.' }, { onConflict: 'key' }),
        supabase.from('pricing_settings').upsert({ key: 'proposal_autofire_test_mode',  value: autofireTestMode ? 'true' : 'false', description: 'When true, auto-fired SignWell documents are sent in test mode (free, watermarked TEST). Flip to false once the round-trip is verified.' }, { onConflict: 'key' }),
      ]);
      await logActivity({ action: 'UPDATE', entityType: 'setting', entityId: null, entityName: 'documents_legal_terms', changes: { html_length: legalHtml.length, threshold, proposal_about_len: aboutHtml.length, proposal_intro_len: introHtml.length, proposal_payment_len: paymentHtml.length, proposal_validity_days: validityDays, autofire_enabled: autofireEnabled, autofire_test_mode: autofireTestMode } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setSaveErr(e.message); }
    setSaving(false);
  }

  if (loading) return <div style={{ padding: 20, color: '#6b7280', fontSize: 12 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c', margin: 0 }}>Documents — Legal & Signature Defaults</h2>
        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          Manage the Acceptance Terms / Exhibit A page that's prepended to every signature document, plus countersignature defaults.
          Changes apply to all future documents — already-sent documents are not retroactively updated.
        </p>
      </div>

      {/* Legal HTML */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>Acceptance Terms (HTML)</div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
              Edit the legal page below. HTML is supported (links, lists, headings).
              When agreement links change, update the URLs here — no deploy needed.
            </div>
          </div>
          <button onClick={() => setShowPreview(s => !s)}
            style={{ padding: '6px 12px', background: showPreview ? '#0f1e3c' : 'white', color: showPreview ? 'white' : '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>

        <textarea
          value={legalHtml}
          onChange={e => setLegalHtml(e.target.value)}
          rows={showPreview ? 12 : 22}
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 5,
            fontSize: 11, fontFamily: 'DM Mono, monospace', outline: 'none', boxSizing: 'border-box',
            lineHeight: 1.5,
          }}
        />

        {showPreview && (
          <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 5, padding: 14, background: '#f8fafc', maxHeight: 400, overflow: 'auto' }}>
            <div style={{ fontSize: 9, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, fontWeight: 600 }}>Live Preview</div>
            <div style={{ background: 'white', padding: 16, borderRadius: 4, fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
              dangerouslySetInnerHTML={{ __html: legalHtml }} />
          </div>
        )}
      </div>

      {/* Countersign defaults */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>Countersignature Rule</div>
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 12 }}>
          When a quote's upfront amount is at or above this threshold, the company countersignature checkbox defaults ON in the Send for Signature modal.
          Reps can override either way per document.
        </div>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
          Threshold (USD)
        </label>
        <input
          type="number"
          min="0"
          value={threshold}
          onChange={e => setThreshold(e.target.value)}
          style={{ width: 200, padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace' }}
        />
        <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 4 }}>
          Default: $5,000. FlexIT block sizes range from a few hundred (3-hour block) to several thousand (50+ hour block).
        </div>
      </div>

      {/* Company signer */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>Default Company Signer</div>
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 12 }}>
          When countersignature is required, SignWell sends the second signature request to this person.
          The name and title appear in the signature block; the email determines where the request is sent.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Full Name</label>
            <input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Shaun Lang"
              style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Title</label>
            <input value={signerTitle} onChange={e => setSignerTitle(e.target.value)} placeholder="CEO"
              style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
        </div>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 12, marginBottom: 4 }}>Email (where SignWell sends countersignature requests)</label>
        <input value={signerEmail} onChange={e => setSignerEmail(e.target.value)} type="email" placeholder="signer@ferrumit.com"
          style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', boxSizing: 'border-box', fontFamily: 'DM Mono, monospace' }} />
      </div>

      {/* v3.5.81: Proposal Templates — HTML and config for the client-
          facing proposal page (proposal.js Netlify function). Three
          editable HTML blocks (about us, intro, payment terms) plus the
          default validity window in days. Mirrors the legal_acceptance
          terms textarea pattern above. Templates are loaded by the
          public proposal renderer at request time, so edits propagate
          immediately to all in-flight proposals — no deploy required. */}
      <div style={{ background: 'white', border: '1px solid #fbcfd5', borderRadius: 8, padding: 18, marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#9b1c2a' }}>Proposal Templates</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
            HTML rendered into the client-facing proposal page. Edits apply to all in-flight proposals immediately
            (live read while in review). Once a proposal is approved, the snapshot freezes the version the client agreed to.
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 4, marginBottom: 4 }}>About Us (HTML)</label>
        <textarea value={aboutHtml} onChange={e => setAboutHtml(e.target.value)} rows={6}
          placeholder="HTML for the About Ferrum section of the proposal."
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, fontFamily: 'DM Mono, monospace', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }} />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 12, marginBottom: 4 }}>Cover-Page Introduction (HTML)</label>
        <textarea value={introHtml} onChange={e => setIntroHtml(e.target.value)} rows={4}
          placeholder="HTML rendered as the intro paragraph on the proposal cover."
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, fontFamily: 'DM Mono, monospace', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }} />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 12, marginBottom: 4 }}>Payment &amp; Billing Terms (HTML)</label>
        <textarea value={paymentHtml} onChange={e => setPaymentHtml(e.target.value)} rows={6}
          placeholder="HTML for the Payment Terms section, immediately above the Acceptance Terms."
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, fontFamily: 'DM Mono, monospace', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, resize: 'vertical' }} />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 12, marginBottom: 4 }}>Default Validity Window (days)</label>
        <input type="number" min="1" max="365" value={validityDays} onChange={e => setValidityDays(e.target.value)}
          style={{ width: 100, padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace' }} />
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
          Sets expires_at on new proposals. Reps can override per-proposal in a future release.
        </div>

        {/* v3.5.81.1: Service-order auto-fire toggles. Two flags gate the
            behavior introduced in v3.5.81.1 — global on/off, and a test-mode
            switch for the initial rollout. The toggles render as a separate
            sub-card so they're visually distinct from the template HTML
            editors above (different concern: behavior, not content). */}
        <div style={{ marginTop: 18, padding: 14, background: '#fff5f6', border: '1px solid #fbcfd5', borderRadius: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9b1c2a', marginBottom: 4 }}>Service-Order Auto-Fire</div>
          <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
            When enabled, the SignWell service-order document is automatically created and emailed to the
            approver the moment they click Approve on the public proposal page. When disabled, the rep
            triggers the service order manually after seeing the &ldquo;Approved&rdquo; banner on the quote page.
            Auto-fire failures never block proposal approval — the rep falls back to manual sending.
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 0' }}>
            <input type="checkbox" checked={autofireEnabled} onChange={e => setAutofireEnabled(e.target.checked)}
              style={{ width: 14, height: 14, cursor: 'pointer' }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>Enable auto-fire on approval</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>Default: off. Turn on once you&rsquo;ve verified the round-trip end-to-end.</div>
            </div>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: autofireEnabled ? 'pointer' : 'not-allowed', padding: '6px 0', opacity: autofireEnabled ? 1 : 0.55 }}>
            <input type="checkbox" checked={autofireTestMode} onChange={e => setAutofireTestMode(e.target.checked)} disabled={!autofireEnabled}
              style={{ width: 14, height: 14, cursor: autofireEnabled ? 'pointer' : 'not-allowed' }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>Send as test mode</div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>SignWell test docs are free and watermarked &ldquo;TEST&rdquo;. Flip off once you&rsquo;re confident the flow works.</div>
            </div>
          </label>

          {autofireEnabled && !autofireTestMode && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, fontSize: 10, color: '#92400e', fontWeight: 600 }}>
              ⚠ Live mode active. Real SignWell documents will be created and emailed to clients on every approval. Each costs $0.75 after the first 25/month.
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={save} disabled={saving}
          style={{ padding: '8px 22px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save Document Settings'}
        </button>
        {saved && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
        {saveErr && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>✗ {saveErr}</span>}
      </div>
    </div>
  );
}

// ─── RECIPIENTS PANEL (v3.5.83.10) ────────────────────────────────────────────
// Replaced the single-shared-token PublishingPanel from v3.5.80 with a table
// of per-recipient tokens. Each recipient gets their own URL — independently
// revocable, with last_accessed_at + access_count audit visible in the table.
//
// Tokens live in the sos_tokens table (created by the v3.5.83.10 migration).
// The migration ALSO carried the old shared token forward as a row labeled
// "Legacy shared link" so the URL the law firm currently has continues to
// work — once all named recipients have been added and distributed, the
// admin revokes that legacy row from this UI.
//
// Activity log: RECIPIENT_ADDED on insert, RECIPIENT_REVOKED on status flip.
// Token values are NEVER logged (treated as credentials, same as v3.5.80
// PublishingPanel did with TOKEN_GENERATED / TOKEN_REGENERATED).
function RecipientsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [adding, setAdding]         = useState(false);
  const [newName, setNewName]       = useState('');
  const [newEmail, setNewEmail]     = useState('');
  const [newNotes, setNewNotes]     = useState('');
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId]     = useState(null);
  const [showJsonId, setShowJsonId] = useState(null);
  const { profile } = useAuth();

  async function loadRecipients() {
    const { data, error } = await supabase
      .from('sos_tokens')
      .select('*')
      // Active first, then revoked. Within each, oldest first so the order
      // is stable as new recipients are added — they go to the bottom of
      // the active block.
      .order('status',     { ascending: true })
      .order('created_at', { ascending: true });
    if (error) {
      console.error('Recipients load error:', error);
      setRecipients([]);
    } else {
      setRecipients(data || []);
    }
    setLoading(false);
  }
  useEffect(() => { loadRecipients(); }, []);

  // 32-char URL-safe random token. Same generator as the v3.5.80 implementation.
  function generateToken() {
    const bytes = new Uint8Array(24);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return;
    setGenerating(true);
    const token = generateToken();
    const { data, error } = await supabase.from('sos_tokens')
      .insert({
        token,
        recipient_name:  name,
        recipient_email: newEmail.trim() || null,
        notes:           newNotes.trim() || null,
        status:          'active',
        created_by:      profile?.id || null,
      })
      .select().single();
    setGenerating(false);
    if (error) {
      console.error('Recipient add error:', error);
      alert('Failed to add recipient: ' + error.message);
      return;
    }
    // Audit-log. Don't include the token value — it's a credential.
    try {
      await logActivity({
        action: 'RECIPIENT_ADDED',
        entityType: 'sos_tokens',
        entityId: data.id,
        entityName: data.recipient_name,
        metadata: {
          recipient_email: data.recipient_email,
          notes: data.notes,
        },
      });
    } catch (logErr) { console.warn('Activity log failed:', logErr); }
    setNewName(''); setNewEmail(''); setNewNotes('');
    setAdding(false);
    loadRecipients();
  }

  async function handleRevoke(r) {
    const ok = window.confirm(
      `Revoke access for "${r.recipient_name}"?\n\n` +
      `This invalidates their URL — they will get a 403 if they try to use it. ` +
      `If they need access again, add them as a new recipient with a fresh token.`
    );
    if (!ok) return;
    const { error } = await supabase.from('sos_tokens')
      .update({
        status:      'revoked',
        revoked_at:  new Date().toISOString(),
        revoked_by:  profile?.id || null,
      })
      .eq('id', r.id);
    if (error) {
      console.error('Revoke error:', error);
      alert('Failed to revoke: ' + error.message);
      return;
    }
    try {
      await logActivity({
        action: 'RECIPIENT_REVOKED',
        entityType: 'sos_tokens',
        entityId: r.id,
        entityName: r.recipient_name,
      });
    } catch (logErr) { console.warn('Activity log failed:', logErr); }
    loadRecipients();
  }

  function urlFor(token, format) {
    const fmt = format ? `&format=${format}` : '';
    return `${window.location.origin}/.netlify/functions/schedule-of-services?token=${token}${fmt}`;
  }

  async function copyUrl(url, id) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(prev => prev === id ? null : prev), 2000);
    } catch {
      // Fallback for browsers that block clipboard API in some contexts
      const ta = document.createElement('textarea');
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      setCopiedId(id);
      setTimeout(() => setCopiedId(prev => prev === id ? null : prev), 2000);
    }
  }

  const activeCount  = recipients.filter(r => r.status === 'active').length;
  const revokedCount = recipients.length - activeCount;

  return (
    <div style={{
      marginBottom: 12, padding: '12px 14px',
      background: '#fafbfc', border: '1px solid #e5e7eb', borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#0f1e3c', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            Recipients
          </div>
          <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
            {loading ? 'Loading…'
              : recipients.length === 0
                ? 'No recipients yet — add one to publish the schedule.'
                : `${activeCount} active${revokedCount > 0 ? ` · ${revokedCount} revoked` : ''}. Each recipient has their own URL with independent audit + revocation.`}
          </div>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} disabled={loading}
            style={{
              padding: '6px 14px', background: '#0f1e3c', color: 'white',
              border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1,
            }}>
            + Add Recipient
          </button>
        )}
      </div>

      {/* ── Add Recipient form ── */}
      {adding && (
        <div style={{
          marginBottom: 10, padding: 10,
          background: 'white', border: '1px solid #d1d5db', borderRadius: 4,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={fieldLabelStyle}>Recipient Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Scott & Scott LLP — Bob Scott"
                style={inputStyle}
                autoFocus />
            </div>
            <div>
              <label style={fieldLabelStyle}>Email <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
              <input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                placeholder="bob@scottandscottllp.com"
                type="email"
                style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={fieldLabelStyle}>Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional, internal only)</span></label>
            <input value={newNotes} onChange={e => setNewNotes(e.target.value)}
              placeholder="Primary attorney contact for quarterly review"
              style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button onClick={() => { setAdding(false); setNewName(''); setNewEmail(''); setNewNotes(''); }}
              style={{
                padding: '6px 12px', background: '#f3f4f6', color: '#374151',
                border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer',
              }}>
              Cancel
            </button>
            <button onClick={handleAdd} disabled={!newName.trim() || generating}
              style={{
                padding: '6px 14px', background: '#0f1e3c', color: 'white',
                border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600,
                cursor: (!newName.trim() || generating) ? 'not-allowed' : 'pointer',
                opacity: (!newName.trim() || generating) ? 0.5 : 1,
              }}>
              {generating ? 'Generating…' : 'Generate Token + URL'}
            </button>
          </div>
        </div>
      )}

      {/* ── Recipients table ── */}
      {recipients.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {recipients.map(r => {
            const isRevoked = r.status === 'revoked';
            const url       = urlFor(r.token);
            const jsonUrl   = urlFor(r.token, 'json');
            const isCopied  = copiedId === r.id;
            const showJson  = showJsonId === r.id;
            return (
              <div key={r.id}
                style={{
                  padding: 10, background: 'white',
                  border: '1px solid ' + (isRevoked ? '#e5e7eb' : '#d1d5db'),
                  borderRadius: 4,
                  opacity: isRevoked ? 0.55 : 1,
                }}>
                {/* Top row: name + meta + revoke */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {r.recipient_name}
                      {isRevoked && (
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: '#991b1b',
                          background: '#fee2e2', padding: '1px 6px', borderRadius: 3,
                        }}>REVOKED</span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {r.recipient_email && <span>{r.recipient_email}</span>}
                      <span>
                        {r.last_accessed_at
                          ? `Last accessed ${new Date(r.last_accessed_at).toLocaleString()}`
                          : 'Never accessed'}
                      </span>
                      <span>{r.access_count || 0} access{r.access_count === 1 ? '' : 'es'}</span>
                      {r.notes && <span style={{ fontStyle: 'italic' }}>· {r.notes}</span>}
                    </div>
                  </div>
                  {!isRevoked && (
                    <button onClick={() => handleRevoke(r)}
                      style={{
                        padding: '4px 10px', background: 'white',
                        color: '#991b1b', border: '1px solid #fecaca',
                        borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                        flexShrink: 0,
                      }}>
                      Revoke
                    </button>
                  )}
                </div>

                {/* URL row — only show for active rows; revoked URLs return 403
                    so showing them just clutters the UI and risks confusion. */}
                {!isRevoked && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        readOnly
                        value={url}
                        onClick={e => e.target.select()}
                        style={{
                          flex: 1, padding: '5px 8px',
                          fontFamily: 'DM Mono, ui-monospace, SFMono-Regular, monospace',
                          fontSize: 10,
                          border: '1px solid #e5e7eb', borderRadius: 3, background: '#fafbfc',
                          color: '#374151', outline: 'none',
                        }} />
                      <button onClick={() => copyUrl(url, r.id)}
                        style={{
                          padding: '5px 10px',
                          background: isCopied ? '#dcfce7' : '#f3f4f6',
                          color:      isCopied ? '#166534' : '#374151',
                          border: '1px solid ' + (isCopied ? '#86efac' : '#d1d5db'),
                          borderRadius: 3, fontSize: 10, fontWeight: 600,
                          cursor: 'pointer', flexShrink: 0, minWidth: 70,
                        }}>
                        {isCopied ? '✓ Copied' : 'Copy URL'}
                      </button>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{
                          padding: '5px 10px', background: 'white',
                          color: '#374151', border: '1px solid #d1d5db', borderRadius: 3,
                          fontSize: 10, fontWeight: 600,
                          textDecoration: 'none', flexShrink: 0,
                        }}>
                        ↗ Open
                      </a>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <button onClick={() => setShowJsonId(prev => prev === r.id ? null : r.id)}
                        style={{
                          background: 'none', border: 'none',
                          fontSize: 10, color: '#6b7280', cursor: 'pointer',
                          padding: 0, display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                        <span style={{ transform: showJson ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
                        JSON URL
                      </button>
                      {showJson && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                          <input
                            readOnly
                            value={jsonUrl}
                            onClick={e => e.target.select()}
                            style={{
                              flex: 1, padding: '4px 7px',
                              fontFamily: 'DM Mono, ui-monospace, SFMono-Regular, monospace',
                              fontSize: 10,
                              border: '1px solid #e5e7eb', borderRadius: 3, background: 'white',
                              color: '#6b7280', outline: 'none',
                            }} />
                          <button onClick={() => copyUrl(jsonUrl, r.id + ':json')}
                            style={{
                              padding: '4px 10px',
                              background: copiedId === r.id + ':json' ? '#dcfce7' : '#f3f4f6',
                              color:      copiedId === r.id + ':json' ? '#166534' : '#374151',
                              border: '1px solid #d1d5db', borderRadius: 3,
                              fontSize: 10, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                            }}>
                            {copiedId === r.id + ':json' ? '✓' : 'Copy'}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── SCHEDULE OF SERVICES ADMIN (v3.5.79) ─────────────────────────────────────
// Edit interface for the schedule_of_services table introduced in v3.5.78.
// One row per service surface (Managed IT package, product, voice tier, etc.)
// with four narrative fields (summary, included, assumptions, requirements)
// plus status/review_status workflow tracking.
//
// Lifecycle:
//   status: 'draft' (not in published schedule) → 'active' (published) → 'retired' (archived)
//   review_status: 'needs_review' → 'sent_to_attorney' → 'approved'
//
// Status/review_status changes are written to sos_review_log so the team
// has a queryable audit trail of what changed when, surfacing the
// quarterly cycle Shaun's team has historically struggled to track.
function ScheduleOfServicesAdmin() {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [editing,     setEditing]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState('');
  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const [catFilter,   setCatFilter]   = useState('');
  const { profile }                   = useAuth();

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('schedule_of_services')
      .select('*')
      .order('sort_order');
    if (error) console.error('Load SoS error:', error);
    setRows(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function startNew() {
    setSaveError('');
    // Sensible defaults: bottom of sort order, Managed IT category, draft/needs_review
    const maxSort = rows.length > 0 ? Math.max(...rows.map(r => r.sort_order || 0)) : 0;
    setEditing({
      id: null,
      part_number: '', category: '', display_name: '',
      sort_order: maxSort + 10,
      sos_summary: '',
      sos_included: [], sos_assumptions: [], sos_requirements: [],
      status: 'draft', review_status: 'needs_review',
      source_table: null, source_id: null,
      notes: '',
    });
  }

  async function save() {
    setSaving(true); setSaveError('');
    try {
      // Validate: part_number required, must be unique among other rows
      const partNum = (editing.part_number || '').trim();
      if (!partNum) {
        setSaveError('Part number is required.');
        setSaving(false); return;
      }
      const collision = rows.find(r =>
        r.part_number === partNum && r.id !== editing.id
      );
      if (collision) {
        setSaveError(`Part number "${partNum}" is already used by another row.`);
        setSaving(false); return;
      }
      if (!(editing.display_name || '').trim()) {
        setSaveError('Display name is required.');
        setSaving(false); return;
      }
      if (!(editing.category || '').trim()) {
        setSaveError('Category is required.');
        setSaving(false); return;
      }

      // Detect status/review_status changes for audit log
      const original = editing.id ? rows.find(r => r.id === editing.id) : null;
      const statusChanged       = original && original.status !== editing.status;
      const reviewStatusChanged = original && original.review_status !== editing.review_status;

      // Strip empty bullets from arrays before save
      const cleanArr = (arr) => (Array.isArray(arr) ? arr : [])
        .map(s => (typeof s === 'string' ? s.trim() : ''))
        .filter(s => s.length > 0);

      const payload = {
        part_number:       partNum,
        category:          editing.category.trim(),
        display_name:      editing.display_name.trim(),
        sort_order:        parseInt(editing.sort_order || 0),
        sos_summary:       editing.sos_summary || '',
        sos_included:      cleanArr(editing.sos_included),
        sos_assumptions:   cleanArr(editing.sos_assumptions),
        sos_requirements:  cleanArr(editing.sos_requirements),
        status:            editing.status,
        review_status:     editing.review_status,
        source_table:      editing.source_table,
        source_id:         editing.source_id,
        notes:             editing.notes || null,
        updated_at:        new Date().toISOString(),
        updated_by:        profile?.id || null,
      };

      let savedRow;
      if (editing.id) {
        const { data, error } = await supabase
          .from('schedule_of_services')
          .update(payload).eq('id', editing.id)
          .select().single();
        if (error) throw error;
        savedRow = data;
      } else {
        payload.created_by = profile?.id || null;
        const { data, error } = await supabase
          .from('schedule_of_services')
          .insert(payload).select().single();
        if (error) throw error;
        savedRow = data;
      }

      // Audit-log status / review_status changes. Use diffObjects to
      // produce the {fieldname: {from, to}} shape the Activity Log renderer
      // expects — matching every other save in AdminPage. Without this
      // shape, the renderer shows broken "From / To" cells.
      if (statusChanged || reviewStatusChanged) {
        try {
          await logActivity({
            action: 'STATUS_CHANGE',
            entityType: 'schedule_of_services',
            entityId: savedRow.id,
            entityName: savedRow.part_number,
            changes: diffObjects(
              { status: original?.status, review_status: original?.review_status },
              { status: savedRow.status, review_status: savedRow.review_status }
            ),
          });
        } catch (logErr) {
          // Activity log failure should never block the save
          console.warn('Activity log write failed:', logErr);
        }
      }

      await load();
      setEditing(null);
    } catch (err) {
      console.error('SoS save error:', err);
      setSaveError(err.message || 'Save failed.');
    }
    setSaving(false);
  }

  // Derived data — distinct categories from rows, filtered + decorated rows
  const categories = Array.from(new Set(rows.map(r => r.category).filter(Boolean))).sort();

  const counts = {
    total:    rows.length,
    active:   rows.filter(r => r.status === 'active').length,
    draft:    rows.filter(r => r.status === 'draft').length,
    retired:  rows.filter(r => r.status === 'retired').length,
    needs_review:     rows.filter(r => r.status === 'active' && r.review_status === 'needs_review').length,
    sent_to_attorney: rows.filter(r => r.status === 'active' && r.review_status === 'sent_to_attorney').length,
    approved:         rows.filter(r => r.status === 'active' && r.review_status === 'approved').length,
  };

  const filtered = rows.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (reviewFilter !== 'all' && r.review_status !== reviewFilter) return false;
    if (catFilter && r.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (r.part_number || '').toLowerCase().includes(q)
          || (r.display_name || '').toLowerCase().includes(q)
          || (r.category || '').toLowerCase().includes(q)
          || (r.sos_summary || '').toLowerCase().includes(q);
    }
    return true;
  });

  // Decorate rows for AdminTable rendering — short status badges, review badges.
  // CRITICAL: use distinct keys ($status, $review, $updated) so the badge
  // React elements don't clobber the original string fields. Without this,
  // editing a row would receive `r.status === <React element>` (because the
  // spread is overwritten by the JSX assignment), and the save payload
  // would serialize that React element, violating the status check
  // constraint. Same pattern ProductsAdmin uses with `$sell`/`$cost`.
  const tableRows = filtered.map(r => ({
    ...r,
    '$status':  <StatusBadge value={r.status} kind="status" />,
    '$review':  <StatusBadge value={r.review_status} kind="review" />,
    '$updated': r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—',
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Schedule of Services</h2>
          <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2, maxWidth: 640 }}>
            Edit the narrative content describing each service Ferrum offers. The schedule is reviewed quarterly by Scott &amp; Scott LLP — flag rows for review using the Review Status field. Active rows are what the rendered schedule shows; drafts and retired rows are hidden.
          </p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
          <button onClick={() => window.open('/admin/sos/preview', '_blank')}
            style={{ padding:'6px 12px', background:'white', border:'1px solid #d1d5db', borderRadius:5, fontSize:11, fontWeight:600, cursor:'pointer', color:'#374151' }}>
            👁 Preview Document
          </button>
          <button onClick={startNew}
            style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor:'pointer' }}>
            + Add Row
          </button>
        </div>
      </div>

      <RecipientsPanel />

      {/* Status summary bar */}
      <div style={{
        display:'flex', gap:14, marginBottom:12, padding:'10px 14px',
        background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, fontSize:11
      }}>
        <span><strong>{counts.total}</strong> total</span>
        <span style={{ color:'#9ca3af' }}>|</span>
        <span><strong style={{ color:'#16a34a' }}>{counts.active}</strong> active</span>
        <span><strong style={{ color:'#6b7280' }}>{counts.draft}</strong> draft</span>
        <span><strong style={{ color:'#9ca3af' }}>{counts.retired}</strong> retired</span>
        <span style={{ color:'#9ca3af' }}>|</span>
        <span><strong style={{ color:'#ca8a04' }}>{counts.needs_review}</strong> needs review</span>
        <span><strong style={{ color:'#2563eb' }}>{counts.sent_to_attorney}</strong> sent to attorney</span>
        <span><strong style={{ color:'#16a34a' }}>{counts.approved}</strong> approved</span>
      </div>

      {/* Filter bar */}
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search part #, name, category, summary..."
          style={{ padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', width:280 }}/>

        <span style={{ fontSize:10, color:'#6b7280', marginLeft:6 }}>Status:</span>
        {[['all', 'All'], ['active', 'Active'], ['draft', 'Draft'], ['retired', 'Retired']].map(([key, lbl]) => (
          <button key={key} onClick={() => setStatusFilter(key)}
            style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${statusFilter===key?'#2563eb':'#e5e7eb'}`, background:statusFilter===key?'#eff6ff':'white', color:statusFilter===key?'#1d4ed8':'#374151', fontSize:10, fontWeight:statusFilter===key?700:400, cursor:'pointer' }}>
            {lbl}
          </button>
        ))}

        <span style={{ fontSize:10, color:'#6b7280', marginLeft:6 }}>Review:</span>
        {[['all', 'All'], ['needs_review', 'Needs Review'], ['sent_to_attorney', 'Sent'], ['approved', 'Approved']].map(([key, lbl]) => (
          <button key={key} onClick={() => setReviewFilter(key)}
            style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${reviewFilter===key?'#2563eb':'#e5e7eb'}`, background:reviewFilter===key?'#eff6ff':'white', color:reviewFilter===key?'#1d4ed8':'#374151', fontSize:10, fontWeight:reviewFilter===key?700:400, cursor:'pointer' }}>
            {lbl}
          </button>
        ))}

        {(search || statusFilter !== 'all' || reviewFilter !== 'all' || catFilter) && (
          <span style={{ fontSize:10, color:'#6b7280', marginLeft:'auto' }}>
            {filtered.length} of {rows.length} shown
          </span>
        )}
      </div>

      {/* Category filter chips — separate row to fit */}
      <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:10, color:'#6b7280' }}>Category:</span>
        <button onClick={() => setCatFilter('')}
          style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${catFilter===''?'#2563eb':'#e5e7eb'}`, background:catFilter===''?'#eff6ff':'white', color:catFilter===''?'#1d4ed8':'#374151', fontSize:10, fontWeight:catFilter===''?700:400, cursor:'pointer' }}>
          All
        </button>
        {categories.map(cat => {
          const count = rows.filter(r => r.category === cat).length;
          const active = catFilter === cat;
          return (
            <button key={cat} onClick={() => setCatFilter(cat)}
              style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${active?'#2563eb':'#e5e7eb'}`, background:active?'#eff6ff':'white', color:active?'#1d4ed8':'#374151', fontSize:10, fontWeight:active?700:400, cursor:'pointer' }}>
              {cat} ({count})
            </button>
          );
        })}
      </div>

      <AdminTable
        cols={['part_number', 'category', 'display_name', '$status', '$review', 'sort_order', '$updated']}
        rows={tableRows}
        onEdit={r => { setSaveError(''); setEditing(r); }}
        loading={loading}
      />

      {editing && (
        <SoSEditModal
          row={editing}
          setRow={setEditing}
          onClose={() => { setEditing(null); setSaveError(''); }}
          onSave={save}
          saving={saving}
          error={saveError}
          existingCategories={categories}
        />
      )}
    </div>
  );
}

// Small status/review badge component used in the SoS table.
function StatusBadge({ value, kind }) {
  const palette = {
    status: {
      active:  { bg: '#dcfce7', fg: '#166534', label: 'Active' },
      draft:   { bg: '#f3f4f6', fg: '#6b7280', label: 'Draft' },
      retired: { bg: '#f3f4f6', fg: '#9ca3af', label: 'Retired' },
    },
    review: {
      needs_review:     { bg: '#fef3c7', fg: '#78350f', label: 'Needs Review' },
      sent_to_attorney: { bg: '#dbeafe', fg: '#1e3a8a', label: 'Sent' },
      approved:         { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
    },
  };
  const p = palette[kind]?.[value];
  if (!p) return <span>{value}</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px',
      background: p.bg, color: p.fg,
      fontSize: 10, fontWeight: 700, borderRadius: 3,
    }}>{p.label}</span>
  );
}

// ─── SOS EDIT MODAL — wider than the standard Modal to fit bullet editors ─────
function SoSEditModal({ row, setRow, onClose, onSave, saving, error, existingCategories }) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const update = (key, val) => setRow(prev => ({ ...prev, [key]: val }));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: 720, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f1e3c' }}>
            {row.id ? `Edit: ${row.part_number || '(unnamed)'}` : 'New Schedule Entry'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#6b7280', cursor: 'pointer' }}>×</button>
        </div>

        {/* Top row: identifying fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={fieldLabelStyle}>Part Number</label>
            <input value={row.part_number || ''} onChange={e => update('part_number', e.target.value)}
              placeholder="MIT-ESSENTIALS"
              style={inputStyle} />
          </div>
          <div>
            <label style={fieldLabelStyle}>Category</label>
            <input value={row.category || ''} onChange={e => update('category', e.target.value)}
              placeholder="Managed IT Services"
              list="sos-cat-list"
              style={inputStyle} />
            <datalist id="sos-cat-list">
              {existingCategories.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div>
            <label style={fieldLabelStyle}>Sort #</label>
            <input value={row.sort_order ?? ''} onChange={e => update('sort_order', e.target.value)}
              type="number" placeholder="100"
              style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabelStyle}>Display Name</label>
          <input value={row.display_name || ''} onChange={e => update('display_name', e.target.value)}
            placeholder="Business Essentials — Help Desk"
            style={inputStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabelStyle}>Summary <span style={{ color: '#9ca3af', fontWeight: 400 }}>(one paragraph, plain English)</span></label>
          <textarea value={row.sos_summary || ''} onChange={e => update('sos_summary', e.target.value)}
            rows={4}
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        <BulletListEditor label="What's Included" sub="Bullets shown to clients describing what they receive."
          items={row.sos_included} onChange={v => update('sos_included', v)} />

        <BulletListEditor label="Assumptions and Exceptions" sub="Scope limits and things NOT covered. Most legally protective field."
          items={row.sos_assumptions} onChange={v => update('sos_assumptions', v)} />

        <BulletListEditor label="Customer Requirements" sub="What the client must provide for the service to work."
          items={row.sos_requirements} onChange={v => update('sos_requirements', v)} />

        {/* Status / review_status row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14, marginTop: 18, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
          <div>
            <label style={fieldLabelStyle}>Status</label>
            <select value={row.status || 'draft'} onChange={e => update('status', e.target.value)} style={selectStyle}>
              <option value="draft">Draft (not in published schedule)</option>
              <option value="active">Active (published)</option>
              <option value="retired">Retired (archived)</option>
            </select>
          </div>
          <div>
            <label style={fieldLabelStyle}>Review Status</label>
            <select value={row.review_status || 'needs_review'} onChange={e => update('review_status', e.target.value)} style={selectStyle}>
              <option value="needs_review">Needs Review (internal edits pending)</option>
              <option value="sent_to_attorney">Sent to Attorney (awaiting feedback)</option>
              <option value="approved">Approved by Attorney</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabelStyle}>Internal Notes <span style={{ color: '#9ca3af', fontWeight: 400 }}>(NOT shown to clients or attorney)</span></label>
          <textarea value={row.notes || ''} onChange={e => update('notes', e.target.value)}
            rows={2}
            placeholder="Anything operations needs to verify, vendor-specific notes, internal reminders..."
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        {/* Advanced section — source linkage display */}
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
          <button onClick={() => setAdvancedOpen(o => !o)}
            style={{ background: 'none', border: 'none', fontSize: 11, color: '#6b7280', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ transform: advancedOpen ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
            Advanced
          </button>
          {advancedOpen && (
            <div style={{ marginTop: 10, padding: 12, background: '#f8fafc', borderRadius: 4, fontSize: 11, color: '#6b7280' }}>
              <div style={{ marginBottom: 6 }}>
                <strong style={{ color: '#374151' }}>Source linkage:</strong> {' '}
                {row.source_table
                  ? <code style={{ background: 'white', padding: '1px 5px', borderRadius: 3, fontSize: 10 }}>{row.source_table} · {row.source_id}</code>
                  : <em>None — this row is not linked to an operational table.</em>}
              </div>
              {row.created_at && (
                <div style={{ fontSize: 10 }}>
                  Created: {new Date(row.created_at).toLocaleString()} · Updated: {new Date(row.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, fontSize: 11, color: '#991b1b' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={btnStyle('#f3f4f6', '#374151')}>Cancel</button>
          <button onClick={onSave} disabled={saving}
            style={{ padding: '6px 16px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BULLET LIST EDITOR ───────────────────────────────────────────────────────
// Reusable editor for JSON-array bullet fields. Per Shaun's call:
// per-bullet input rows with up/down/remove buttons, plus "+ Add bullet"
// at the bottom. Bulk-paste import is a punch-list item for v3.5.79.x.
// v3.5.83.9: drag-and-drop reordering added on top of the existing up/down
// arrow controls. Each bullet now has a grip handle (⠿) on the left that can
// be dragged to reposition the row anywhere in the list. The arrows stay for
// keyboard / single-position moves. Drop indicators (blue top/bottom borders)
// show where the dragged item will land. Reorder calls the same onChange
// callback the arrows use, so save flow + activity logging are unchanged.
function BulletListEditor({ label, sub, items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  const [dragIndex,     setDragIndex]     = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  function addBullet()       { onChange([...list, '']); }
  function removeBullet(i)   { onChange(list.filter((_, idx) => idx !== i)); }
  function updateBullet(i, val) {
    const next = [...list]; next[i] = val; onChange(next);
  }
  function moveBullet(i, direction) {
    const j = i + direction;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  // v3.5.83.9: reorder by removing from `from` then inserting at `to`. Note
  // that `to` is the index in the POST-removal array — same as the visual
  // target index, because in a downward drag the target shifts up by 1 after
  // removal which exactly cancels the +1 you'd otherwise need for "insert
  // after". Symmetric for upward drags. See the test cases in the original
  // commit message if this looks suspicious.
  function reorderBullets(from, to) {
    if (from === to) return;
    if (from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  // ── Drag handlers ────────────────────────────────────────────────────────
  // The grip handle is the only element marked draggable; the text input is
  // a normal input so users can still select text inside without the row
  // following their cursor. The DnD state lives in component-local useState
  // (not refs) because we need to re-render to show drop indicators.
  function handleDragStart(e, i) {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers (notably Firefox) require setData for the drag to register
    try { e.dataTransfer.setData('text/plain', String(i)); } catch {}
  }
  function handleDragOver(e, i) {
    // preventDefault is required to allow drop on this element
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== i) setDragOverIndex(i);
  }
  function handleDragLeave(_e, i) {
    if (dragOverIndex === i) setDragOverIndex(null);
  }
  function handleDrop(e, i) {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== i) reorderBullets(dragIndex, i);
    setDragIndex(null);
    setDragOverIndex(null);
  }
  function handleDragEnd() {
    // Fires whether or not a drop happened — clean up indicator state
    setDragIndex(null);
    setDragOverIndex(null);
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={fieldLabelStyle}>
        {label}
        {sub && <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>{sub}</span>}
      </label>
      {list.length === 0 && (
        <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic', padding: '6px 0' }}>
          No bullets yet. Click "+ Add bullet" below.
        </div>
      )}
      {list.map((bullet, i) => {
        const isDragging          = dragIndex === i;
        const isDragTarget        = dragOverIndex === i && dragIndex !== null && dragIndex !== i;
        // Top indicator when dragging up (target is above source);
        // bottom indicator when dragging down (target is below source).
        const showTopIndicator    = isDragTarget && dragIndex > i;
        const showBottomIndicator = isDragTarget && dragIndex < i;
        return (
          <div key={i}
            onDragOver={(e) => handleDragOver(e, i)}
            onDragLeave={(e) => handleDragLeave(e, i)}
            onDrop={(e) => handleDrop(e, i)}
            style={{
              display: 'flex', gap: 4, marginBottom: 4, alignItems: 'flex-start',
              opacity: isDragging ? 0.4 : 1,
              borderTop:    showTopIndicator    ? '2px solid #2563eb' : '2px solid transparent',
              borderBottom: showBottomIndicator ? '2px solid #2563eb' : '2px solid transparent',
              transition: 'opacity 0.15s, border-color 0.1s',
            }}>
            <button
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragEnd={handleDragEnd}
              title="Drag to reorder"
              aria-label="Drag to reorder bullet"
              style={{
                padding: '4px 7px', background: 'white',
                color: '#9ca3af',
                border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 12,
                cursor: dragIndex === null ? 'grab' : 'grabbing',
                flexShrink: 0, lineHeight: 1, userSelect: 'none',
              }}>⠿</button>
            <input value={bullet} onChange={e => updateBullet(i, e.target.value)}
              placeholder="Bullet text..."
              style={{ ...inputStyle, flex: 1 }} />
            <button onClick={() => moveBullet(i, -1)} disabled={i === 0}
              title="Move up"
              style={bulletBtnStyle(i === 0)}>↑</button>
            <button onClick={() => moveBullet(i, 1)} disabled={i === list.length - 1}
              title="Move down"
              style={bulletBtnStyle(i === list.length - 1)}>↓</button>
            <button onClick={() => removeBullet(i)}
              title="Remove"
              style={{ ...bulletBtnStyle(false), color: '#dc2626' }}>×</button>
          </div>
        );
      })}
      <button onClick={addBullet}
        style={{ padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: '1px dashed #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer', marginTop: 4 }}>
        + Add bullet
      </button>
    </div>
  );
}

const fieldLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, outline: 'none', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, background: 'white' };
const bulletBtnStyle = (disabled) => ({
  padding: '4px 8px', background: disabled ? '#f9fafb' : 'white',
  color: disabled ? '#d1d5db' : '#6b7280',
  border: '1px solid #e5e7eb', borderRadius: 4, fontSize: 11,
  cursor: disabled ? 'not-allowed' : 'pointer',
  flexShrink: 0,
});

// ─── INTEGRATIONS ADMIN ───────────────────────────────────────────────────────
export function IntegrationsAdmin() {
  const [token,         setToken]         = useState('');
  const [saved,         setSaved]         = useState(false);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [testing,       setTesting]       = useState(false);
  const [testMsg,       setTestMsg]       = useState('');
  const [quoteUrlField, setQuoteUrlField] = useState('');
  const [urlFieldSaved, setUrlFieldSaved] = useState(false);
  const [hsProps,       setHsProps]       = useState([]);
  const [loadingProps,  setLoadingProps]  = useState(false);
  const [propsError,    setPropsError]    = useState('');
  const [logoUrl,    setLogoUrl]    = useState('');
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoMsg,    setLogoMsg]    = useState('');
  const [logoFile,   setLogoFile]   = useState(null);
  const [logoPreview,setLogoPreview]= useState(null);
  const [stageAwaitingPay, setStageAwaitingPay] = useState('');
  const [stageClosedWon,   setStageClosedWon]   = useState('');
  const [stagesSaved,      setStagesSaved]      = useState(false);
  const { profile } = useAuth();

  useEffect(() => {
    supabase.from('pricing_settings').select('value').eq('key','hubspot_token').single()
      .then(({ data }) => { if (data?.value) setToken(data.value); setLoading(false); });
    supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single()
      .then(({ data }) => { if (data?.value) setQuoteUrlField(data.value); });
    supabase.from('pricing_settings').select('value').eq('key','company_logo_url').single()
      .then(({ data }) => { if (data?.value) setLogoUrl(data.value); });
    supabase.from('pricing_settings').select('value').eq('key','hubspot_stage_awaiting_payment').single()
      .then(({ data }) => { if (data?.value) setStageAwaitingPay(data.value); });
    supabase.from('pricing_settings').select('value').eq('key','hubspot_stage_closed_won').single()
      .then(({ data }) => { if (data?.value) setStageClosedWon(data.value); });
  }, []);

  async function saveStages() {
    await Promise.all([
      supabase.from('pricing_settings').upsert({ key: 'hubspot_stage_awaiting_payment', value: stageAwaitingPay, description: 'HubSpot pipeline stage ID for deals awaiting payment after contract signed.' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'hubspot_stage_closed_won',       value: stageClosedWon,   description: 'HubSpot pipeline stage ID for deals where payment is collected.' }, { onConflict: 'key' }),
    ]);
    setStagesSaved(true);
    setTimeout(() => setStagesSaved(false), 2500);
  }

  async function saveLogo() {
    if (!logoUrl.trim() && !logoPreview) { setLogoMsg('Enter a URL or select a file.'); return; }
    setLogoSaving(true); setLogoMsg('');
    try {
      // Use base64 data URL if a file was selected, otherwise use the URL directly
      const finalUrl = logoPreview || logoUrl.trim();
      await supabase.from('pricing_settings').upsert(
        { key: 'company_logo_url', value: finalUrl, description: 'Company logo — shown on login page and navigation bar' },
        { onConflict: 'key' }
      );
      setLogoUrl(finalUrl);
      setLogoMsg('✓ Logo saved — refresh the page to see it applied');
    } catch (err) {
      setLogoMsg('✗ ' + err.message);
    }
    setLogoSaving(false);
  }

  function handleLogoFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = ev => {
      setLogoPreview(ev.target.result); // base64 data URL stored directly in DB
      setLogoUrl('');
    };
    reader.readAsDataURL(file);
  }

  async function saveToken() {
    setSaving(true); setSaved(false);
    // Upsert the token into pricing_settings
    const { error } = await supabase.from('pricing_settings').upsert({
      key: 'hubspot_token', value: token, label: 'HubSpot Private App Token',
      description: 'Private app token for HubSpot CRM integration', updated_by: profile?.id
    }, { onConflict: 'key' });
    if (!error) {
      await supabase.from('activity_log').insert({
        user_id: profile?.id, user_email: profile?.email,
        action: 'UPDATE', entity_type: 'setting', entity_name: 'hubspot_token',
        changes: { note: 'Token updated (value hidden)' }
      });
      setSaved(true);
    }
    setSaving(false);
  }

  async function saveQuoteUrlField() {
    await supabase.from('pricing_settings').upsert({
      key: 'hubspot_quote_url_field', value: quoteUrlField,
      description: 'HubSpot deal property name to write the Ferrum IQ quote URL into',
    }, { onConflict: 'key' });
    setUrlFieldSaved(true);
    setTimeout(() => setUrlFieldSaved(false), 2500);
  }

  async function loadHsProperties() {
    if (!token) { setPropsError('Save your HubSpot token first'); return; }
    setLoadingProps(true); setPropsError(''); setHsProps([]);
    try {
      const res = await fetch('/.netlify/functions/hubspot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_deal_properties', token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load properties');
      setHsProps(data.properties || []);
      if ((data.properties || []).length === 0) setPropsError('No string/text properties found on deals');
    } catch(e) { setPropsError('✗ ' + e.message); }
    setLoadingProps(false);
  }

  async function testConnection() {
    setTesting(true); setTestMsg('');
    try {
      const res = await fetch('/.netlify/functions/hubspot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'test', token })
      });
      const data = await res.json();
      if (res.ok) setTestMsg('✓ Connected successfully — HubSpot API is responding');
      else setTestMsg('✗ ' + (data.message || data.error || 'Connection failed — check your token'));
    } catch {
      setTestMsg('✗ Network error — could not reach the proxy function');
    }
    setTesting(false);
  }

  if (loading) return <div style={{ padding: 20, color: '#6b7280', fontSize: 12 }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#0f1e3c' }}>Integrations</h2>
        <p style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Connect FerrumIQ to your external tools</p>
      </div>

      {/* Company Logo */}
      <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:8, padding:20, marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#0f1e3c', marginBottom:4 }}>Company Logo</div>
        <p style={{ fontSize:11, color:'#6b7280', marginBottom:14 }}>Shown on the login page and navigation bar. Upload a file or paste a public URL.</p>

        {/* Preview */}
        {(logoPreview || logoUrl) && (
          <div style={{ background:'#0f1e3c', borderRadius:8, padding:'12px 20px', display:'inline-block', marginBottom:14 }}>
            <img src={logoPreview || logoUrl} alt="Logo preview"
              style={{ height:40, maxWidth:200, objectFit:'contain', display:'block' }} />
          </div>
        )}

        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {/* File upload */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#374151', marginBottom:4 }}>Upload image file (PNG, SVG, JPG)</label>
            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoFile}
              style={{ fontSize:11, color:'#374151' }} />
          </div>

          {/* URL input */}
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:'#374151', marginBottom:4 }}>Or paste a public image URL</label>
            <input value={logoUrl} onChange={e => { setLogoUrl(e.target.value); setLogoFile(null); setLogoPreview(null); }}
              placeholder="https://your-cdn.com/logo.png"
              style={{ width:'100%', padding:'7px 9px', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, outline:'none' }} />
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={saveLogo} disabled={logoSaving}
              style={{ padding:'7px 18px', background:'#0f1e3c', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:600, cursor:'pointer', opacity: logoSaving ? 0.6 : 1 }}>
              {logoSaving ? 'Saving...' : 'Save Logo'}
            </button>
            {logoMsg && <span style={{ fontSize:11, fontWeight:600, color: logoMsg.startsWith('✓') ? '#166534' : '#dc2626' }}>{logoMsg}</span>}
          </div>

          <div style={{ fontSize:9, color:'#9ca3af' }}>
            File uploads are stored as encoded data — no external storage required.
            For best results use a PNG or SVG with a transparent or dark background.
          </div>
        </div>
      </div>

      {/* Smart Pricing Table */}
      <SPTIntegration />
      <SignWellIntegration />
      <StripeIntegration />

      {/* HubSpot */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 32, height: 32, background: '#ff7a59', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>H</span>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>HubSpot CRM</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>Create and link deals from quotes</div>
          </div>
          {token && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: '#166534', background: '#dcfce7', padding: '2px 7px', borderRadius: 3 }}>Configured</span>}
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Private App Token
          </label>
          <input
            type="password"
            value={token}
            onChange={e => { setToken(e.target.value); setSaved(false); setTestMsg(''); }}
            placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, fontFamily: 'DM Mono, monospace', outline: 'none' }}
          />
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
            HubSpot → Settings → Integrations → Private Apps → your app → Token
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={saveToken} disabled={saving || !token}
            style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!token || saving) ? 0.6 : 1 }}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Token'}
          </button>
          <button onClick={testConnection} disabled={testing || !token}
            style={{ padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!token || testing) ? 0.6 : 1 }}>
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>

        {testMsg && (
          <div style={{ marginTop: 10, padding: '7px 10px', borderRadius: 5, fontSize: 12, fontWeight: 500,
            background: testMsg.startsWith('✓') ? '#dcfce7' : '#fef2f2',
            color: testMsg.startsWith('✓') ? '#166534' : '#dc2626',
            border: `1px solid ${testMsg.startsWith('✓') ? '#bbf7d0' : '#fecaca'}` }}>
            {testMsg}
          </div>
        )}

        {/* ── Quote URL field mapping ── */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>Quote URL Field Mapping</div>
          <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 12, lineHeight: 1.6 }}>
            Select which HubSpot deal property should receive the Ferrum IQ quote URL when a quote is saved.
            This lets anyone in HubSpot click directly into the quote. Only string/text properties are shown.
          </p>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <button onClick={loadHsProperties} disabled={loadingProps || !token}
              style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, opacity: (!token || loadingProps) ? 0.6 : 1 }}>
              {loadingProps ? 'Loading…' : hsProps.length ? '↻ Reload Fields' : 'Load Fields'}
            </button>
            {hsProps.length > 0 && (
              <span style={{ fontSize: 10, color: '#9ca3af' }}>{hsProps.length} properties loaded</span>
            )}
          </div>
          <select value={quoteUrlField} onChange={e => { setQuoteUrlField(e.target.value); setUrlFieldSaved(false); }}
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, background: 'white', outline: 'none', color: quoteUrlField ? '#0f1e3c' : '#9ca3af', marginBottom: 8 }}>
            <option value="">— select a HubSpot deal property —</option>
            {hsProps.map(p => (
              <option key={p.name} value={p.name}>
                {p.label} — {p.name}
              </option>
            ))}
            {quoteUrlField && !hsProps.find(p => p.name === quoteUrlField) && (
              <option value={quoteUrlField}>{quoteUrlField} (saved)</option>
            )}
          </select>
          <button onClick={saveQuoteUrlField} disabled={!quoteUrlField}
            style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: !quoteUrlField ? 0.6 : 1 }}>
            {urlFieldSaved ? '✓ Saved' : 'Save Field Selection'}
          </button>

          {propsError && <div style={{ fontSize: 11, color: '#dc2626', marginBottom: 6 }}>{propsError}</div>}

          {quoteUrlField && (
            <div style={{ fontSize: 10, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '5px 9px' }}>
              ✓ Active: quote URL will be written to <strong>{quoteUrlField}</strong> on every save
            </div>
          )}
          {!quoteUrlField && (
            <div style={{ fontSize: 10, color: '#9ca3af' }}>
              Click "Load Fields" to fetch your HubSpot deal properties, then pick the URL field.
            </div>
          )}
        </div>

        {/* Pipeline Stage Automation */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>📊 Pipeline Stage Automation</div>
          <p style={{ fontSize: 10, color: '#6b7280', marginBottom: 10, lineHeight: 1.6 }}>
            Optional. When set, deals automatically move through pipeline stages on contract signing and payment.
            Find stage IDs at <span style={{ fontFamily: 'DM Mono, monospace' }}>app.hubspot.com</span> → Settings → Objects → Deals → Pipelines &amp; Stages.
            Hover over a stage and copy the internal value (e.g., <span style={{ fontFamily: 'DM Mono, monospace' }}>contractsent</span> or <span style={{ fontFamily: 'DM Mono, monospace' }}>1234567</span>).
            Leave blank to skip stage updates.
          </p>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Stage when client signs contract (Awaiting Payment)
          </label>
          <input
            value={stageAwaitingPay}
            onChange={e => { setStageAwaitingPay(e.target.value); setStagesSaved(false); }}
            placeholder="e.g. contractsent or 12345678"
            style={{ width: '100%', padding: '6px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 10, boxSizing: 'border-box' }}
          />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Stage when payment collected (Closed Won)
          </label>
          <input
            value={stageClosedWon}
            onChange={e => { setStageClosedWon(e.target.value); setStagesSaved(false); }}
            placeholder="e.g. closedwon or 12345679"
            style={{ width: '100%', padding: '6px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 10, boxSizing: 'border-box' }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={saveStages}
              style={{ padding: '6px 14px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              Save Stage IDs
            </button>
            {stagesSaved && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
          </div>
        </div>
      </div>

      {/* Smart Pricing Table */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 32, height: 32, background: '#2563eb', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>S</span>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>Smart Pricing Table</div>
            <div style={{ fontSize: 11, color: '#6b7280' }}>JSON export available on every saved quote</div>
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, color: '#1e40af', background: '#dbeafe', padding: '2px 7px', borderRadius: 3 }}>Export Only</span>
        </div>
        <p style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
          Open any saved quote and click <strong>Export JSON</strong> to download a file ready to import into Smart Pricing Table.
          Send us a sample SPT JSON file and we'll map the fields to match their exact import format.
        </p>
      </div>
    </div>
  );
}


// ─── SignWell Integration ────────────────────────────────────────────────────
function SignWellIntegration() {
  const [key,        setKey]        = useState('');
  const [templateId,    setTemplateId]    = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [saved,      setSaved]      = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testMsg,    setTestMsg]    = useState('');
  const [loading,    setLoading]    = useState(true);
  const { profile } = useAuth();

  useEffect(() => {
    Promise.all([
      supabase.from('pricing_settings').select('value').eq('key', 'signwell_api_key').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'signwell_intl_waiver_template_id').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'signwell_webhook_secret').single(),
    ]).then(([keyRes, tplRes, whRes]) => {
      if (keyRes.data?.value) setKey(keyRes.data.value);
      if (tplRes.data?.value) setTemplateId(tplRes.data.value);
      if (whRes.data?.value)  setWebhookSecret(whRes.data.value);
      setLoading(false);
    });
  }, []);

  async function save() {
    setSaving(true); setSaved(false); setTestMsg('');
    await supabase.from('pricing_settings').upsert({ key: 'signwell_api_key', value: key, description: 'SignWell e-signature API key' }, { onConflict: 'key' });
    await supabase.from('pricing_settings').upsert({ key: 'signwell_intl_waiver_template_id', value: templateId, description: 'SignWell template ID for International Dialing Waiver signature page' }, { onConflict: 'key' });
    await supabase.from('pricing_settings').upsert({ key: 'signwell_webhook_secret', value: webhookSecret, description: 'Shared secret used to authenticate incoming SignWell webhooks (passed as ?secret= in webhook URL).' }, { onConflict: 'key' });
    await logActivity({ action: 'UPDATE', entityType: 'setting', entityId: null, entityName: 'signwell_api_key', changes: { updated: true } });
    setSaved(true); setSaving(false);
    setTimeout(() => setSaved(false), 2500);
  }

  function generateSecret() {
    // 32 random hex chars (128 bits) — sufficient entropy for an opaque shared secret
    const arr = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    const hex = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
    setWebhookSecret(hex);
  }

  async function saveQuoteUrlField() {
    await supabase.from('pricing_settings').upsert({
      key: 'hubspot_quote_url_field', value: quoteUrlField,
      description: 'HubSpot deal property name to write the Ferrum IQ quote URL into',
    }, { onConflict: 'key' });
    setUrlFieldSaved(true);
    setTimeout(() => setUrlFieldSaved(false), 2500);
  }

  async function loadHsProperties() {
    if (!token) { setPropsError('Save your HubSpot token first'); return; }
    setLoadingProps(true); setPropsError(''); setHsProps([]);
    try {
      const res = await fetch('/.netlify/functions/hubspot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_deal_properties', token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load properties');
      setHsProps(data.properties || []);
      if ((data.properties || []).length === 0) setPropsError('No string/text properties found on deals');
    } catch(e) { setPropsError('✗ ' + e.message); }
    setLoadingProps(false);
  }

  async function testConnection() {
    if (!key) { setTestMsg('✗ Enter an API key first'); return; }
    setTesting(true); setTestMsg('');
    try {
      const res = await fetch('/.netlify/functions/signwellProxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listDocuments', payload: { per_page: 1 } }),
      });
      const data = await res.json();
      if (res.ok) setTestMsg('✓ Connected successfully — SignWell API is responding');
      else setTestMsg('✗ ' + (data.error || data.errors?.base?.[0] || `Error ${res.status}`));
    } catch(e) { setTestMsg('✗ ' + e.message); }
    setTesting(false);
  }

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{ width: 24, height: 24, background: '#166534', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>SW</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>SignWell E-Signatures</div>
      </div>
      <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 14 }}>
        Legally binding e-signatures for International Dialing Waivers and other one-off documents.
        First 25 documents/month free, then $0.75/doc — no monthly minimum.
        Get your API key from{' '}
        <a href="https://app.signwell.com/account/api" target="_blank" rel="noopener noreferrer" style={{ color: '#166534' }}>
          SignWell → Account → API
        </a>.
      </p>
      <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 14 }}>
        Also register the webhook URL in{' '}
        <a href="https://app.signwell.com/account/api" target="_blank" rel="noopener noreferrer" style={{ color: '#166534' }}>
          SignWell → API → Webhooks
        </a>:{' '}
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, background: '#f1f5f9', padding: '1px 5px', borderRadius: 3 }}>
          https://lustrous-treacle-e0ca6a.netlify.app/.netlify/functions/signwellWebhook
        </span>
      </p>
      {loading ? <div style={{ fontSize: 11, color: '#9ca3af' }}>Loading...</div> : (
        <>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>API Key</label>
          <input
            type="password"
            value={key}
            onChange={e => setKey(e.target.value)}
            placeholder="your SignWell API key..."
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 14 }}
          />
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            International Dialing Waiver — Template ID
          </label>
          <input
            type="text"
            value={templateId}
            onChange={e => setTemplateId(e.target.value)}
            placeholder="e.g. 53906b87-b393-44c1-9ebb-d267c756cb66"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 4 }}
          />
          <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 14 }}>
            Found in SignWell → Templates → your template URL. Update here when you replace the template.
          </div>

          {/* Webhook configuration — for v3.5.18 auto-payment trigger */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 14, marginTop: 6, marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>📡 Webhook Endpoint</div>
            <p style={{ fontSize: 10, color: '#6b7280', marginBottom: 8, lineHeight: 1.6 }}>
              Receives SignWell document events. Required for auto-triggering Stripe payment links when clients sign FlexIT agreements.
              Register the URL below in SignWell → Webhooks (or via API).
            </p>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
              Webhook URL <span style={{ fontWeight: 400, color: '#9ca3af' }}>(read-only, paste this into SignWell)</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                readOnly
                value={`${typeof window !== 'undefined' ? window.location.origin : 'https://lustrous-treacle-e0ca6a.netlify.app'}/.netlify/functions/signwellWebhook${webhookSecret ? `?secret=${webhookSecret}` : ''}`}
                style={{ width: '100%', padding: '7px 9px', paddingRight: 88, border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, outline: 'none', fontFamily: 'DM Mono, monospace', background: '#f8fafc', boxSizing: 'border-box' }}
              />
              <button
                onClick={() => {
                  const url = `${window.location.origin}/.netlify/functions/signwellWebhook${webhookSecret ? `?secret=${webhookSecret}` : ''}`;
                  navigator.clipboard?.writeText(url);
                }}
                style={{ position: 'absolute', right: 4, top: 4, padding: '5px 12px', background: '#0f1e3c', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                Copy
              </button>
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 10, marginBottom: 4 }}>
              Webhook Secret <span style={{ fontWeight: 400, color: '#9ca3af' }}>(authenticates incoming requests)</span>
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={webhookSecret}
                onChange={e => setWebhookSecret(e.target.value)}
                placeholder="Click Generate to create one"
                style={{ flex: 1, padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, outline: 'none', fontFamily: 'DM Mono, monospace' }}
              />
              <button onClick={generateSecret}
                style={{ padding: '7px 14px', background: 'white', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 11, fontWeight: 600, color: '#0f1e3c', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ⟳ Generate
              </button>
            </div>
            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 4 }}>
              SignWell does not publish HMAC signature support; we authenticate by including the secret as a query parameter on the webhook URL.
              Anyone with this URL can post events, so keep it private. Click Generate for a fresh 32-char hex secret.
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '7px 18px', background: '#166534', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save API Key'}
            </button>
            <button onClick={testConnection} disabled={testing || !key}
              style={{ padding: '7px 14px', background: 'white', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, color: '#374151', cursor: 'pointer', opacity: testing ? 0.6 : 1 }}>
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            {saved && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
            {testMsg && <span style={{ fontSize: 11, fontWeight: 600, color: testMsg.startsWith('✓') ? '#166534' : '#dc2626' }}>{testMsg}</span>}
          </div>
          <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 8 }}>
            The key is stored in Pricing Settings and used server-side only. You can also set SIGNWELL_API_KEY in Netlify environment variables (env var takes precedence).
          </div>
        </>
      )}
    </div>
  );
}


// ─── Voice Hardware Catalog ───────────────────────────────────────────────────
function VoiceHardwareAdmin() {
  const [devices,  setDevices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all'); // all | preferred | supported | legacy | inactive
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);

  const BLANK = { manufacturer:'', model:'', category:'preferred', compatibility:'compatible', auto_provision:true, firmware_notes:'', notes:'', active:true,
                  catalog_id:'', short_label:'', short_description:'', hardware_type:'phone',
                  monthly_lease:null, purchase_price:null, ports:null,
                  lease_eligible:false, purchase_eligible:false, sort_order:100 };

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('voice_hardware').select('*').order('manufacturer').order('model');
    setDevices(data || []);
    setLoading(false);
  }

  async function save() {
    // Validation: if marked as eligible (lease or purchase), catalog_id is required
    if ((editing.lease_eligible || editing.purchase_eligible) && !editing.catalog_id?.trim()) {
      window.alert('A Catalog ID is required for devices marked as Lease- or Purchase-Eligible. Devices appearing in Voice quote dropdowns need a stable Catalog ID so existing quotes can resolve their hardware reference.');
      return;
    }
    setSaving(true);
    // Normalize catalog_id to null when blank — the unique index ignores nulls
    // so multiple BYOH-only entries can coexist without catalog ids
    const payload = {
      ...editing,
      catalog_id: editing.catalog_id?.trim() ? editing.catalog_id.trim() : null,
      updated_at: new Date().toISOString(),
    };
    if (editing.id) {
      await supabase.from('voice_hardware').update(payload).eq('id', editing.id);
    } else {
      await supabase.from('voice_hardware').insert(payload);
    }
    await load();
    setEditing(null);
    setSaving(false);
  }

  async function toggleActive(device) {
    await supabase.from('voice_hardware').update({ active: !device.active, updated_at: new Date().toISOString() }).eq('id', device.id);
    setDevices(prev => prev.map(d => d.id === device.id ? { ...d, active: !d.active } : d));
  }

  async function deleteDevice(id) {
    if (!window.confirm('Delete this device permanently?')) return;
    await supabase.from('voice_hardware').delete().eq('id', id);
    setDevices(prev => prev.filter(d => d.id !== id));
  }

  const COMPAT_COLORS = { compatible:'#166534', limited:'#92400e', manual_only:'#9a3412', not_compatible:'#991b1b' };
  const COMPAT_LABELS = { compatible:'Preferred', limited:'Limited', manual_only:'Legacy', not_compatible:'Not Compatible' };

  const filtered = devices.filter(d => {
    const inCatalog = !!(d.lease_eligible || d.purchase_eligible);
    if (filter === 'inactive' && d.active) return false;
    if (filter === 'catalog' && !inCatalog) return false;
    if (filter !== 'all' && filter !== 'inactive' && filter !== 'catalog' && d.category !== filter) return false;
    if (filter !== 'inactive' && !d.active) return false;
    if (search) {
      const q = search.toLowerCase();
      return d.manufacturer.toLowerCase().includes(q) || d.model.toLowerCase().includes(q);
    }
    return true;
  });

  const counts = {
    all:       devices.filter(d=>d.active).length,
    catalog:   devices.filter(d=>d.active && (d.lease_eligible || d.purchase_eligible)).length,
    preferred: devices.filter(d=>d.active && d.category==='preferred').length,
    supported: devices.filter(d=>d.active && d.category==='supported').length,
    legacy:    devices.filter(d=>d.active && d.category==='legacy').length,
    inactive:  devices.filter(d=>!d.active).length,
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <h2 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>Voice Hardware Catalog</h2>
          <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>3CX-compatible phone and device compatibility database. Drives BYOH validation on Voice quotes <strong>and pricing dropdowns for the lease/purchase options</strong> (when lease- or purchase-eligible).</div>
        </div>
        <button onClick={() => setEditing({...BLANK})}
          style={{ padding:'7px 14px', background:'#7c3aed', color:'white', border:'none', borderRadius:5, fontSize:11, fontWeight:700, cursor:'pointer' }}>
          + Add Device
        </button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search manufacturer or model..."
          style={{ padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', width:220 }}/>
        {[['all','All Active'], ['catalog','💰 Sales Catalog'], ['preferred','Preferred'], ['supported','Supported'], ['legacy','Legacy'], ['inactive','Inactive']].map(([k,l]) => (
          <button key={k} onClick={()=>setFilter(k)}
            style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${filter===k?'#7c3aed':'#e5e7eb'}`, background:filter===k?'#f5f3ff':'white', color:filter===k?'#6d28d9':'#374151', fontSize:10, fontWeight:filter===k?700:400, cursor:'pointer' }}>
            {l} {counts[k] != null ? `(${counts[k]})` : ''}
          </button>
        ))}
      </div>

      {loading ? <div style={{ fontSize:11, color:'#9ca3af' }}>Loading...</div> : (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:6, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['Manufacturer','Model','Category','Compatibility','Auto-Prov','Pricing','Notes','Status',''].map(h => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', letterSpacing:'.05em', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr key={d.id} style={{ borderBottom:'1px solid #f1f5f9', background: i%2===0 ? 'white' : '#fafafa', opacity: d.active ? 1 : 0.5 }}>
                  <td style={{ padding:'6px 10px', fontWeight:600, color:'#0f1e3c' }}>{d.manufacturer}</td>
                  <td style={{ padding:'6px 10px' }}>{d.model}</td>
                  <td style={{ padding:'6px 10px', textTransform:'capitalize' }}>{d.category}</td>
                  <td style={{ padding:'6px 10px' }}>
                    <span style={{ fontSize:9, fontWeight:700, color: COMPAT_COLORS[d.compatibility]||'#374151' }}>
                      {COMPAT_LABELS[d.compatibility]||d.compatibility}
                    </span>
                  </td>
                  <td style={{ padding:'6px 10px', textAlign:'center' }}>{d.auto_provision ? '✓' : '—'}</td>
                  <td style={{ padding:'6px 10px', fontSize:10, whiteSpace:'nowrap' }}>
                    {(d.lease_eligible || d.purchase_eligible) ? (
                      <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
                        {d.lease_eligible && d.monthly_lease != null && (
                          <span style={{ fontFamily:'DM Mono, monospace', color:'#0f766e' }}>Lease ${Number(d.monthly_lease).toFixed(0)}/mo</span>
                        )}
                        {d.purchase_eligible && d.purchase_price != null && (
                          <span style={{ fontFamily:'DM Mono, monospace', color:'#1e40af' }}>Buy ${Number(d.purchase_price).toFixed(0)}</span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color:'#cbd5e1', fontSize:9 }}>BYOH only</span>
                    )}
                  </td>
                  <td style={{ padding:'6px 10px', color:'#6b7280', fontSize:10, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {d.firmware_notes && <span style={{ color:'#d97706', marginRight:4 }}>⚠ {d.firmware_notes}</span>}
                    {d.notes}
                  </td>
                  <td style={{ padding:'6px 10px' }}>
                    <button onClick={()=>toggleActive(d)}
                      style={{ fontSize:9, padding:'2px 7px', borderRadius:3, border:'1px solid #e5e7eb', background: d.active?'#f0fdf4':'#f9fafb', color:d.active?'#166534':'#6b7280', cursor:'pointer' }}>
                      {d.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ padding:'6px 10px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <button onClick={()=>setEditing({...d})} style={{ padding:'2px 7px', fontSize:9, background:'white', border:'1px solid #d1d5db', borderRadius:3, cursor:'pointer' }}>Edit</button>
                      <button onClick={()=>deleteDevice(d.id)} style={{ padding:'2px 7px', fontSize:9, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, cursor:'pointer', color:'#dc2626' }}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding:'20px', textAlign:'center', color:'#9ca3af', fontSize:11 }}>No devices match current filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
          <div style={{ background:'white', borderRadius:8, padding:24, width:640, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:'0 0 16px' }}>{editing.id ? 'Edit Device' : 'Add Device'}</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              {[['Manufacturer', 'manufacturer'], ['Model', 'model']].map(([lbl, key]) => (
                <div key={key}>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>{lbl} *</label>
                  <input value={editing[key]||''} onChange={e=>setEditing(p=>({...p,[key]:e.target.value}))}
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>
                </div>
              ))}
              <div>
                <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Category</label>
                <select value={editing.category} onChange={e=>setEditing(p=>({...p,category:e.target.value}))}
                  style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                  {['preferred','supported','doorphone','gateway','headset','legacy'].map(c=>(
                    <option key={c} value={c} style={{textTransform:'capitalize'}}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Compatibility</label>
                <select value={editing.compatibility} onChange={e=>setEditing(p=>({...p,compatibility:e.target.value}))}
                  style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                  <option value="compatible">Preferred — full support</option>
                  <option value="limited">Supported — limited / 3rd party</option>
                  <option value="manual_only">Legacy — manual config only</option>
                  <option value="not_compatible">Not Compatible</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Firmware / EOL Notes</label>
              <input value={editing.firmware_notes||''} onChange={e=>setEditing(p=>({...p,firmware_notes:e.target.value}))} placeholder="e.g. EOL — no new firmware, 3rd-party supported only..."
                style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Notes</label>
              <textarea value={editing.notes||''} onChange={e=>setEditing(p=>({...p,notes:e.target.value}))} rows={2} placeholder="Router phone, DECT, hotel series, etc."
                style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, resize:'vertical', outline:'none' }}/>
            </div>

            {/* ── Sales Catalog Pricing ────────────────────────────────────── */}
            <div style={{ background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:6, padding:'10px 12px', marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                <span style={{ fontSize:11, fontWeight:700, color:'#6d28d9' }}>💰 Sales Catalog</span>
                <span style={{ fontSize:9, color:'#9ca3af' }}>Mark as eligible to appear in Voice quote dropdowns</span>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>
                    Catalog ID <span style={{ color:'#9ca3af', fontWeight:400 }}>(stable id, e.g. T33G)</span>
                  </label>
                  <input value={editing.catalog_id||''} onChange={e=>setEditing(p=>({...p,catalog_id:e.target.value}))} placeholder="T33G / ht802 / etc"
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', fontFamily:'DM Mono, monospace' }}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Hardware Type</label>
                  <select value={editing.hardware_type||'phone'} onChange={e=>setEditing(p=>({...p,hardware_type:e.target.value}))}
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none' }}>
                    {['phone','dect','ata','headset','gateway','doorphone','other'].map(t=>(
                      <option key={t} value={t} style={{textTransform:'capitalize'}}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Short Label (dropdown)</label>
                  <input value={editing.short_label||''} onChange={e=>setEditing(p=>({...p,short_label:e.target.value}))} placeholder="Yealink T33G"
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Sort Order</label>
                  <input type="number" value={editing.sort_order??100} onChange={e=>setEditing(p=>({...p,sort_order:e.target.value===''?null:parseInt(e.target.value)}))} placeholder="100"
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', fontFamily:'DM Mono, monospace' }}/>
                </div>
              </div>

              <div style={{ marginBottom:10 }}>
                <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>Short Description (dropdown subtitle)</label>
                <input value={editing.short_description||''} onChange={e=>setEditing(p=>({...p,short_description:e.target.value}))} placeholder="Entry level color screen"
                  style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div>
                  <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', marginBottom:3 }}>
                    <input type="checkbox" checked={!!editing.lease_eligible} onChange={e=>setEditing(p=>({...p,lease_eligible:e.target.checked}))} style={{ accentColor:'#7c3aed' }}/>
                    <span style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', color:'#0f766e' }}>Lease Eligible</span>
                  </label>
                  <input type="number" min="0" step="0.01" disabled={!editing.lease_eligible}
                    value={editing.monthly_lease ?? ''} onChange={e=>setEditing(p=>({...p,monthly_lease:e.target.value===''?null:parseFloat(e.target.value)}))}
                    placeholder="Monthly $"
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', fontFamily:'DM Mono, monospace', background: editing.lease_eligible ? 'white' : '#f8fafc', opacity: editing.lease_eligible ? 1 : 0.6 }}/>
                </div>
                <div>
                  <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', marginBottom:3 }}>
                    <input type="checkbox" checked={!!editing.purchase_eligible} onChange={e=>setEditing(p=>({...p,purchase_eligible:e.target.checked}))} style={{ accentColor:'#7c3aed' }}/>
                    <span style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', color:'#1e40af' }}>Purchase Eligible</span>
                  </label>
                  <input type="number" min="0" step="0.01" disabled={!editing.purchase_eligible}
                    value={editing.purchase_price ?? ''} onChange={e=>setEditing(p=>({...p,purchase_price:e.target.value===''?null:parseFloat(e.target.value)}))}
                    placeholder="Purchase $"
                    style={{ width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', fontFamily:'DM Mono, monospace', background: editing.purchase_eligible ? 'white' : '#f8fafc', opacity: editing.purchase_eligible ? 1 : 0.6 }}/>
                </div>
              </div>

              {(editing.hardware_type === 'ata' || editing.hardware_type === 'gateway') && (
                <div>
                  <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3 }}>FXS Ports</label>
                  <input type="number" min="0" value={editing.ports??''} onChange={e=>setEditing(p=>({...p,ports:e.target.value===''?null:parseInt(e.target.value)}))}
                    style={{ width:120, padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', fontFamily:'DM Mono, monospace' }}/>
                </div>
              )}

              {(editing.lease_eligible || editing.purchase_eligible) && !editing.catalog_id && (
                <div style={{ fontSize:9, color:'#dc2626', fontWeight:600, marginTop:6 }}>
                  ⚠ A Catalog ID is required for devices in the sales catalog
                </div>
              )}
            </div>
            {/* ── End Sales Catalog Pricing ────────────────────────────────── */}

            <div style={{ display:'flex', gap:8, marginBottom:14 }}>
              <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
                <input type="checkbox" checked={editing.auto_provision} onChange={e=>setEditing(p=>({...p,auto_provision:e.target.checked}))} style={{ accentColor:'#7c3aed' }}/>
                <span style={{ fontSize:11 }}>Auto-provisioned by 3CX</span>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
                <input type="checkbox" checked={editing.active} onChange={e=>setEditing(p=>({...p,active:e.target.checked}))} style={{ accentColor:'#7c3aed' }}/>
                <span style={{ fontSize:11 }}>Active (visible to reps)</span>
              </label>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={()=>setEditing(null)} style={{ padding:'7px 16px', background:'white', border:'1px solid #d1d5db', borderRadius:5, fontSize:12, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving||!editing.manufacturer||!editing.model}
                style={{ padding:'7px 18px', background:'#7c3aed', color:'white', border:'none', borderRadius:5, fontSize:12, fontWeight:700, cursor:'pointer', opacity:saving?0.7:1 }}>
                {saving?'Saving...':'Save Device'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stripe Integration ──────────────────────────────────────────────────────
function StripeIntegration() {
  const [secretKey,  setSecretKey]  = useState('');
  const [pubKey,     setPubKey]     = useState('');
  const [whSecret,   setWhSecret]   = useState('');
  const [mode,       setMode]       = useState('test');
  const [stmtDesc,   setStmtDesc]   = useState('FERRUM IT PREPAY');
  const [autoPay,    setAutoPay]    = useState(true);
  const [saved,      setSaved]      = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [testing,    setTesting]    = useState(false);
  const [testMsg,    setTestMsg]    = useState('');
  const [loading,    setLoading]    = useState(true);
  const { profile } = useAuth();

  useEffect(() => {
    Promise.all([
      supabase.from('pricing_settings').select('value').eq('key', 'stripe_secret_key').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'stripe_publishable_key').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'stripe_webhook_secret').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'stripe_mode').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'stripe_statement_desc').single(),
      supabase.from('pricing_settings').select('value').eq('key', 'flexit_auto_payment_after_sign').single(),
    ]).then(([sk, pk, wh, md, sd, ap]) => {
      if (sk.data?.value) setSecretKey(sk.data.value);
      if (pk.data?.value) setPubKey(pk.data.value);
      if (wh.data?.value) setWhSecret(wh.data.value);
      if (md.data?.value) setMode(md.data.value);
      if (sd.data?.value) setStmtDesc(sd.data.value);
      if (ap.data?.value !== undefined) setAutoPay(ap.data.value !== 'false');
      setLoading(false);
    });
  }, []);

  // Auto-derive mode from key prefix when secret key changes
  useEffect(() => {
    if (!secretKey) return;
    if (secretKey.startsWith('sk_live_')) setMode('live');
    else if (secretKey.startsWith('sk_test_')) setMode('test');
  }, [secretKey]);

  async function save() {
    setSaving(true); setSaved(false); setTestMsg('');
    await Promise.all([
      supabase.from('pricing_settings').upsert({ key: 'stripe_secret_key', value: secretKey, description: 'Stripe secret API key (sk_test_… or sk_live_…). Used server-side only.' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'stripe_publishable_key', value: pubKey, description: 'Stripe publishable key (pk_test_… or pk_live_…). Safe to expose client-side.' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'stripe_webhook_secret', value: whSecret, description: 'Stripe webhook signing secret (whsec_…). Used to verify webhook authenticity.' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'stripe_mode', value: mode, description: 'Stripe mode: test or live.' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'stripe_statement_desc', value: stmtDesc, description: 'Statement descriptor on the cardholder bank statement (max 22 chars).' }, { onConflict: 'key' }),
      supabase.from('pricing_settings').upsert({ key: 'flexit_auto_payment_after_sign', value: autoPay ? 'true' : 'false', description: 'When true, signing a FlexIT quote automatically creates a Stripe payment link and emails it to the client.' }, { onConflict: 'key' }),
    ]);
    await logActivity({ action: 'UPDATE', entityType: 'setting', entityId: null, entityName: 'stripe_integration', changes: { mode, has_secret: !!secretKey, has_webhook: !!whSecret, autoPay } });
    setSaved(true); setSaving(false);
    setTimeout(() => setSaved(false), 2500);
  }

  async function testConnection() {
    if (!secretKey) { setTestMsg('✗ Enter a secret API key first'); return; }
    setTesting(true); setTestMsg('');
    try {
      const res = await fetch('/.netlify/functions/stripeProxy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'testConnection' }),
      });
      const data = await res.json();
      if (res.ok) {
        const liveLabel = data.livemode ? 'LIVE' : 'TEST';
        setTestMsg(`✓ Connected · Stripe is responding in ${liveLabel} mode`);
      } else {
        setTestMsg('✗ ' + (data.error || `Error ${res.status}`));
      }
    } catch (e) { setTestMsg('✗ ' + e.message); }
    setTesting(false);
  }

  const webhookUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://lustrous-treacle-e0ca6a.netlify.app'}/.netlify/functions/stripeWebhook`;
  const isConfigured = !!secretKey && !!whSecret;
  const modeColor = mode === 'live' ? '#dc2626' : '#7c3aed';
  const modeBg    = mode === 'live' ? '#fef2f2' : '#faf5ff';

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div style={{ width: 24, height: 24, background: '#635bff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>S</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c' }}>Stripe — Prepayment Collection</div>
        {isConfigured && (
          <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: modeColor, background: modeBg, padding: '2px 7px', borderRadius: 3, textTransform: 'uppercase' }}>
            {mode}
          </span>
        )}
      </div>
      <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 14, lineHeight: 1.6 }}>
        Collect Payment #1 (initial onboarding prepayment) by credit card via hosted Stripe Checkout.
        The 2% card surcharge from <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, background: '#f1f5f9', padding: '0 4px', borderRadius: 2 }}>payment_cc_surcharge</span> is added as a separate line item on every Checkout page.
        ACH/EFT remains free and external — clients pay via{' '}
        <a href="https://ferrumit.com/billing" target="_blank" rel="noopener noreferrer" style={{ color: '#635bff' }}>ferrumit.com/billing</a>.
        Get your API keys from{' '}
        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#635bff' }}>Stripe → Developers → API keys</a>.
      </p>

      {/* Webhook URL — copy/paste into Stripe dashboard */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '8px 12px', marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#a16207', marginBottom: 4 }}>📌 Register this webhook in Stripe</div>
        <p style={{ fontSize: 10, color: '#92400e', marginBottom: 6, lineHeight: 1.6 }}>
          Stripe → Developers → Webhooks → Add endpoint. Listen for events:{' '}
          <span style={{ fontFamily: 'DM Mono, monospace' }}>checkout.session.completed</span>,{' '}
          <span style={{ fontFamily: 'DM Mono, monospace' }}>checkout.session.expired</span>,{' '}
          <span style={{ fontFamily: 'DM Mono, monospace' }}>payment_intent.payment_failed</span>,{' '}
          <span style={{ fontFamily: 'DM Mono, monospace' }}>charge.refunded</span>.
        </p>
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, background: 'white', padding: '5px 8px', borderRadius: 3, border: '1px solid #fde68a', wordBreak: 'break-all' }}>
          {webhookUrl}
        </div>
      </div>

      {loading ? <div style={{ fontSize: 11, color: '#9ca3af' }}>Loading…</div> : (
        <>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Secret API Key
          </label>
          <input
            type="password"
            value={secretKey}
            onChange={e => setSecretKey(e.target.value)}
            placeholder="sk_test_… or sk_live_…"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 14 }}
          />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Publishable Key <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional — reserved for future Elements deploy)</span>
          </label>
          <input
            type="text"
            value={pubKey}
            onChange={e => setPubKey(e.target.value)}
            placeholder="pk_test_… or pk_live_…"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 14 }}
          />

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Webhook Signing Secret
          </label>
          <input
            type="password"
            value={whSecret}
            onChange={e => setWhSecret(e.target.value)}
            placeholder="whsec_…"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 4 }}
          />
          <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 14 }}>
            Found under your webhook endpoint in Stripe → "Signing secret" → reveal.
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Statement Descriptor
          </label>
          <input
            type="text"
            value={stmtDesc}
            onChange={e => setStmtDesc(e.target.value.toUpperCase().slice(0, 22))}
            maxLength={22}
            placeholder="FERRUM IT PREPAY"
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 4 }}
          />
          <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 14 }}>
            Appears on the customer's card statement (max 22 chars, uppercase).
          </div>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
            Mode
          </label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {['test', 'live'].map(m => (
              <button key={m} onClick={() => setMode(m)} type="button"
                style={{
                  flex: 1, padding: '8px 12px', borderRadius: 5,
                  border: `1px solid ${mode === m ? (m === 'live' ? '#dc2626' : '#7c3aed') : '#d1d5db'}`,
                  background: mode === m ? (m === 'live' ? '#fef2f2' : '#faf5ff') : 'white',
                  color:      mode === m ? (m === 'live' ? '#dc2626' : '#7c3aed') : '#6b7280',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.04em',
                }}>
                {mode === m ? '● ' : ''}{m}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 9, color: '#9ca3af', marginBottom: 14 }}>
            Auto-detected from key prefix. Test mode = no real charges. Live mode = real money.
          </div>

          {/* FlexIT auto-payment toggle */}
          <div style={{ background: autoPay ? '#f0fdf4' : '#fef2f2', border: `1px solid ${autoPay ? '#bbf7d0' : '#fecaca'}`, borderRadius: 5, padding: 10, marginBottom: 14 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={autoPay} onChange={e => setAutoPay(e.target.checked)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f1e3c' }}>
                  ⚡ Auto-trigger Stripe payment when FlexIT contract is signed
                </div>
                <div style={{ fontSize: 10, color: '#374151', lineHeight: 1.6, marginTop: 4 }}>
                  When enabled, the SignWell webhook automatically creates a Stripe Checkout link and emails it to the client the moment they sign the FlexIT agreement.
                  Service desk and finance don't need to touch Stripe — the entire flow runs end-to-end.
                  Disable for testing or if you want the rep to send the payment link manually.
                </div>
              </div>
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '7px 18px', background: '#635bff', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save Stripe Settings'}
            </button>
            <button onClick={testConnection} disabled={testing || !secretKey}
              style={{ padding: '7px 14px', background: 'white', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, color: '#374151', cursor: 'pointer', opacity: testing ? 0.6 : 1 }}>
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            {saved && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
            {testMsg && <span style={{ fontSize: 11, fontWeight: 600, color: testMsg.startsWith('✓') ? '#166534' : '#dc2626' }}>{testMsg}</span>}
          </div>

          <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 10, lineHeight: 1.6 }}>
            Keys are stored in Pricing Settings and used server-side only via the stripeProxy Netlify function.
            You can also set <span style={{ fontFamily: 'DM Mono, monospace' }}>STRIPE_SECRET_KEY</span> and{' '}
            <span style={{ fontFamily: 'DM Mono, monospace' }}>STRIPE_WEBHOOK_SECRET</span> in Netlify env vars (env takes precedence).
            The webhook function additionally requires <span style={{ fontFamily: 'DM Mono, monospace' }}>SUPABASE_SERVICE_ROLE_KEY</span> in Netlify env to update payment status.
          </div>
        </>
      )}
    </div>
  );
}

// ─── SPT Integration ─────────────────────────────────────────────────────────
function SPTIntegration() {
  const [key,     setKey]     = useState('');
  const [saved,   setSaved]   = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();

  useEffect(() => {
    supabase.from('pricing_settings').select('value').eq('key', 'spt_api_key').single()
      .then(({ data }) => { if (data?.value) setKey(data.value); setLoading(false); });
  }, []);

  async function save() {
    setSaving(true); setSaved(false);
    await supabase.from('pricing_settings').upsert({ key: 'spt_api_key', value: key, description: 'Smart Pricing Table API key' }, { onConflict: 'key' });
    await logActivity({ action: 'UPDATE', entityType: 'setting', entityId: null, entityName: 'spt_api_key', changes: { updated: true } });
    setSaved(true); setSaving(false);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1e3c', marginBottom: 4 }}>Smart Pricing Table</div>
      <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 14 }}>
        Connect to Smart Pricing Table to export rate sheets and proposals directly from Ferrum IQ.
        Get your API key from <a href="https://web.smartpricingtable.com/settings/profile" target="_blank" rel="noopener noreferrer" style={{ color: '#f97316' }}>SPT Profile Settings</a>.
      </p>
      {loading ? <div style={{ fontSize: 11, color: '#9ca3af' }}>Loading...</div> : (
        <>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>API Key</label>
          <input
            type="password"
            value={key}
            onChange={e => setKey(e.target.value)}
            placeholder="spt_live_..."
            style={{ width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 12, outline: 'none', fontFamily: 'DM Mono, monospace', marginBottom: 10 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={save} disabled={saving}
              style={{ padding: '7px 18px', background: '#f97316', color: 'white', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving...' : 'Save API Key'}
            </button>
            {saved && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>✓ Saved</span>}
          </div>
          <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 8 }}>
            The key is stored securely and only used server-side. You can also set SPT_API_KEY in your Netlify environment variables.
          </div>
        </>
      )}
    </div>
  );
}


// ─── Voice Fax Packages ──────────────────────────────────────────────────────
function VoiceFaxPackagesAdmin() {
  const [pkgs,    setPkgs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const BLANK = {
    package_key: '', label: '', sell_mrr: 0, cost_mrr: 0,
    included_users: 1, included_pages: null, included_dids: 1,
    overage_sell_per_page: null, overage_cost_per_page: 0,
    extra_user_sell: null, extra_user_cost: 0,
    extra_did_sell: null,  extra_did_cost: 0,
    description: '', sort_order: 999, active: true,
  };

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const { data } = await supabase.from('voice_fax_packages').select('*').order('sort_order').order('package_key');
    setPkgs(data || []);
    setLoading(false);
  }

  async function save() {
    setSaving(true);
    const payload = { ...editing, updated_at: new Date().toISOString() };
    // Coerce numeric fields
    ['sell_mrr','cost_mrr','overage_cost_per_page','extra_user_cost','extra_did_cost'].forEach(k => {
      payload[k] = parseFloat(payload[k] || 0);
    });
    ['overage_sell_per_page','extra_user_sell','extra_did_sell'].forEach(k => {
      payload[k] = payload[k] === '' || payload[k] == null ? null : parseFloat(payload[k]);
    });
    ['included_users','included_pages','included_dids','sort_order'].forEach(k => {
      payload[k] = payload[k] === '' || payload[k] == null ? null : parseInt(payload[k]);
    });
    if (editing.id) {
      await supabase.from('voice_fax_packages').update(payload).eq('id', editing.id);
    } else {
      delete payload.id;
      await supabase.from('voice_fax_packages').insert(payload);
    }
    await load();
    setEditing(null);
    setSaving(false);
  }

  async function toggleActive(pkg) {
    await supabase.from('voice_fax_packages').update({ active: !pkg.active, updated_at: new Date().toISOString() }).eq('id', pkg.id);
    setPkgs(prev => prev.map(p => p.id === pkg.id ? { ...p, active: !p.active } : p));
  }

  async function deletePkg(id) {
    if (!window.confirm('Delete this fax package permanently?')) return;
    await supabase.from('voice_fax_packages').delete().eq('id', id);
    setPkgs(prev => prev.filter(p => p.id !== id));
  }

  function gmFor(p) {
    const sell = parseFloat(p.sell_mrr || 0);
    const cost = parseFloat(p.cost_mrr || 0);
    if (sell <= 0) return null;
    return ((sell - cost) / sell) * 100;
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div>
          <h2 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:0 }}>Voice Fax Packages</h2>
          <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>
            mFax / Documo virtual fax pricing — sell + cost split for accurate gross margin on Voice and Bundle quotes.
            Replaces the hardcoded <code>FAX_PACKAGES</code> constant from <code>lib/voicePricing.js</code>.
          </div>
        </div>
        <button onClick={() => setEditing({...BLANK})}
          style={{ padding:'7px 14px', background:'#0891b2', color:'white', border:'none', borderRadius:5, fontSize:11, fontWeight:700, cursor:'pointer' }}>
          + Add Package
        </button>
      </div>

      {loading ? <div style={{ fontSize:11, color:'#9ca3af' }}>Loading...</div> : (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:6, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['Key','Label','Sell MRR','Cost MRR','GM %','Users','Pages','DIDs','Overage Sell/pg','Status',''].map(h => (
                  <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', letterSpacing:'.05em', borderBottom:'1px solid #e5e7eb' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pkgs.map((p, i) => {
                const gm = gmFor(p);
                const gmCol = gm == null ? '#6b7280' : gm >= 50 ? '#065f46' : gm >= 25 ? '#92400e' : '#991b1b';
                const noCost = !parseFloat(p.cost_mrr || 0);
                return (
                  <tr key={p.id} style={{ borderBottom:'1px solid #f1f5f9', background: i%2===0 ? 'white' : '#fafafa', opacity: p.active ? 1 : 0.5 }}>
                    <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', fontSize:10, color:'#374151' }}>{p.package_key}</td>
                    <td style={{ padding:'6px 10px', fontWeight:600, color:'#0f1e3c' }}>{p.label}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace' }}>${parseFloat(p.sell_mrr||0).toFixed(2)}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', color: noCost ? '#92400e' : '#374151' }}>
                      ${parseFloat(p.cost_mrr||0).toFixed(2)} {noCost && <span style={{ fontSize:8, marginLeft:3 }}>⚠ unset</span>}
                    </td>
                    <td style={{ padding:'6px 10px', fontWeight:700, color:gmCol, fontFamily:'DM Mono, monospace' }}>
                      {gm == null ? '—' : `${gm.toFixed(0)}%`}
                    </td>
                    <td style={{ padding:'6px 10px' }}>{p.included_users}</td>
                    <td style={{ padding:'6px 10px' }}>{p.included_pages ?? '—'}</td>
                    <td style={{ padding:'6px 10px' }}>{p.included_dids}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'DM Mono, monospace', fontSize:10 }}>
                      {p.overage_sell_per_page == null ? '—' : `$${parseFloat(p.overage_sell_per_page).toFixed(4)}`}
                    </td>
                    <td style={{ padding:'6px 10px' }}>
                      <button onClick={()=>toggleActive(p)}
                        style={{ fontSize:9, padding:'2px 7px', borderRadius:3, border:'1px solid #e5e7eb', background: p.active?'#f0fdf4':'#f9fafb', color:p.active?'#166534':'#6b7280', cursor:'pointer' }}>
                        {p.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ padding:'6px 10px' }}>
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={()=>setEditing({...p})} style={{ padding:'2px 7px', fontSize:9, background:'white', border:'1px solid #d1d5db', borderRadius:3, cursor:'pointer' }}>Edit</button>
                        <button onClick={()=>deletePkg(p.id)} style={{ padding:'2px 7px', fontSize:9, background:'#fef2f2', border:'1px solid #fecaca', borderRadius:3, cursor:'pointer', color:'#dc2626' }}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pkgs.length === 0 && (
                <tr><td colSpan={11} style={{ padding:'20px', textAlign:'center', color:'#9ca3af', fontSize:11 }}>No fax packages yet — run supabase_voice_fax_packages.sql to seed, or click + Add Package</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 }}>
          <div style={{ background:'white', borderRadius:8, padding:24, width:640, maxHeight:'90vh', overflow:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:'#0f1e3c', margin:'0 0 4px' }}>{editing.id ? 'Edit Fax Package' : 'Add Fax Package'}</h3>
            <div style={{ fontSize:10, color:'#6b7280', marginBottom:14 }}>Sell prices appear on quotes. Cost values flow into gross margin calculations.</div>

            {/* Identity */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:10, marginBottom:10 }}>
              <FaxFld label="Package Key *" hint="lowercase identifier, e.g. solo, team, business">
                <input value={editing.package_key||''} onChange={e=>setEditing(p=>({...p, package_key:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'_')}))} disabled={!!editing.id}
                  style={faxInputStyle(!!editing.id)}/>
              </FaxFld>
              <FaxFld label="Label *" hint="Customer-facing name shown on the quote">
                <input value={editing.label||''} onChange={e=>setEditing(p=>({...p, label:e.target.value}))} style={faxInputStyle()}/>
              </FaxFld>
            </div>

            {/* Pricing — sell + cost side by side */}
            <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, padding:10, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', marginBottom:8, letterSpacing:'.05em' }}>Pricing</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                <FaxFld label="Sell MRR ($)"><input type="number" step="0.01" min="0" value={editing.sell_mrr??0} onChange={e=>setEditing(p=>({...p, sell_mrr:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Cost MRR ($)"><input type="number" step="0.01" min="0" value={editing.cost_mrr??0} onChange={e=>setEditing(p=>({...p, cost_mrr:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Live GM %">
                  <div style={{ padding:'5px 8px', fontSize:11, fontWeight:700, fontFamily:'DM Mono, monospace',
                    color: parseFloat(editing.sell_mrr)>0 ? (((editing.sell_mrr-editing.cost_mrr)/editing.sell_mrr*100)>=50?'#065f46':((editing.sell_mrr-editing.cost_mrr)/editing.sell_mrr*100)>=25?'#92400e':'#991b1b') : '#9ca3af' }}>
                    {parseFloat(editing.sell_mrr)>0 ? `${((editing.sell_mrr-editing.cost_mrr)/editing.sell_mrr*100).toFixed(0)}%` : '—'}
                  </div>
                </FaxFld>
              </div>
            </div>

            {/* Included */}
            <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, padding:10, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', marginBottom:8, letterSpacing:'.05em' }}>Included in base price</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                <FaxFld label="Users"><input type="number" min="0" value={editing.included_users??1} onChange={e=>setEditing(p=>({...p, included_users:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Pages/mo (blank = unlimited / N-A)"><input type="number" min="0" value={editing.included_pages??''} onChange={e=>setEditing(p=>({...p, included_pages:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="DIDs"><input type="number" min="0" value={editing.included_dids??0} onChange={e=>setEditing(p=>({...p, included_dids:e.target.value}))} style={faxInputStyle()}/></FaxFld>
              </div>
            </div>

            {/* Overage */}
            <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, padding:10, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', marginBottom:8, letterSpacing:'.05em' }}>Per-page overage</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <FaxFld label="Overage Sell ($/page)"><input type="number" step="0.0001" min="0" value={editing.overage_sell_per_page??''} onChange={e=>setEditing(p=>({...p, overage_sell_per_page:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Overage Cost ($/page)"><input type="number" step="0.0001" min="0" value={editing.overage_cost_per_page??0} onChange={e=>setEditing(p=>({...p, overage_cost_per_page:e.target.value}))} style={faxInputStyle()}/></FaxFld>
              </div>
            </div>

            {/* Extras */}
            <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:6, padding:10, marginBottom:10 }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#6b7280', marginBottom:8, letterSpacing:'.05em' }}>Per-extra add-ons</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:8 }}>
                <FaxFld label="Extra User Sell ($/mo)"><input type="number" step="0.01" min="0" value={editing.extra_user_sell??''} onChange={e=>setEditing(p=>({...p, extra_user_sell:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Extra User Cost ($/mo)"><input type="number" step="0.01" min="0" value={editing.extra_user_cost??0} onChange={e=>setEditing(p=>({...p, extra_user_cost:e.target.value}))} style={faxInputStyle()}/></FaxFld>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <FaxFld label="Extra DID Sell ($/mo)"><input type="number" step="0.01" min="0" value={editing.extra_did_sell??''} onChange={e=>setEditing(p=>({...p, extra_did_sell:e.target.value}))} style={faxInputStyle()}/></FaxFld>
                <FaxFld label="Extra DID Cost ($/mo)"><input type="number" step="0.01" min="0" value={editing.extra_did_cost??0} onChange={e=>setEditing(p=>({...p, extra_did_cost:e.target.value}))} style={faxInputStyle()}/></FaxFld>
              </div>
            </div>

            {/* Meta */}
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10, marginBottom:10 }}>
              <FaxFld label="Description"><input value={editing.description||''} onChange={e=>setEditing(p=>({...p, description:e.target.value}))} style={faxInputStyle()}/></FaxFld>
              <FaxFld label="Sort Order"><input type="number" value={editing.sort_order??999} onChange={e=>setEditing(p=>({...p, sort_order:e.target.value}))} style={faxInputStyle()}/></FaxFld>
            </div>

            <label style={{ display:'flex', alignItems:'center', gap:6, marginBottom:14, fontSize:11, color:'#374151', cursor:'pointer' }}>
              <input type="checkbox" checked={!!editing.active} onChange={e=>setEditing(p=>({...p, active:e.target.checked}))}/>
              Active (show in quote pickers)
            </label>

            {/* Footer */}
            <div style={{ display:'flex', justifyContent:'flex-end', gap:8 }}>
              <button onClick={()=>setEditing(null)}
                style={{ padding:'7px 14px', background:'white', color:'#374151', border:'1px solid #d1d5db', borderRadius:5, fontSize:11, fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving || !editing.package_key || !editing.label}
                style={{ padding:'7px 14px', background:'#0891b2', color:'white', border:'none', borderRadius:5, fontSize:11, fontWeight:700, cursor: saving || !editing.package_key || !editing.label ? 'not-allowed' : 'pointer', opacity: saving || !editing.package_key || !editing.label ? 0.5 : 1 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact field wrapper for the fax package edit modal
function FaxFld({ label, hint, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:9, fontWeight:700, textTransform:'uppercase', color:'#374151', marginBottom:3, letterSpacing:'.03em' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize:8, color:'#9ca3af', marginTop:2 }}>{hint}</div>}
    </div>
  );
}
function faxInputStyle(disabled) {
  return { width:'100%', padding:'5px 8px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none', background: disabled ? '#f3f4f6' : 'white', color: disabled ? '#9ca3af' : '#0f1e3c' };
}
